import { DraftTemplateDefinition, PageDefinition, Scoring, TemplateDefinition, aiAllowanceCapability, limits, planCapabilityKeys } from "@hlt/contracts";
// src/access/rules.ts
const canCreateAudit = (role) => role === "administrator" || role === "consultant";
const canManageWorkspace = (role) => role === "administrator";
const canWrite = canCreateAudit;
/** Read access: membership plus ownership or workspace audience. Admin role alone never suffices. */
function canReadAudit(userId, role, acl) {
	if (!role) return false;
	return acl.ownerId === userId || acl.audience === "workspace";
}
/** Only the owner conducts an audit, and only while holding a writing role. */
function canEditAudit(userId, role, acl) {
	return canWrite(role) && acl.ownerId === userId;
}
const featureEffective = (f) => f.contractActive && f.granted && f.policyAllows && f.platformEnabled;
// src/ai/language.ts
/**
* Lightweight language identification for the five launch languages, used to reject model output that
* stayed in (or switched to) the wrong language. It scores frequent function words and language-specific
* letters; short texts are reported as unknown instead of guessed.
*/
const functionWords = {
	en: [
		"the",
		"and",
		"is",
		"are",
		"of",
		"to",
		"in",
		"not",
		"with",
		"for",
		"on",
		"at",
		"be",
		"this",
		"that",
		"it",
		"from",
		"by",
		"or",
		"was",
		"has",
		"have",
		"there",
		"were",
		"been",
		"their",
		"which",
		"its",
		"an",
		"all",
		"since",
		"without",
		"into",
		"must",
		"should",
		"no",
		"any",
		"every",
		"each",
		"they",
		"do"
	],
	de: [
		"der",
		"die",
		"das",
		"und",
		"ist",
		"sind",
		"nicht",
		"kein",
		"keine",
		"mit",
		"für",
		"auf",
		"im",
		"zu",
		"den",
		"dem",
		"des",
		"ein",
		"eine",
		"einer",
		"von",
		"bei",
		"wird",
		"werden",
		"sich",
		"auch",
		"am",
		"oder",
		"seit",
		"zum",
		"zur",
		"vor",
		"nach",
		"nur",
		"noch",
		"wurde",
		"alle",
		"jede",
		"jeder",
		"sowie",
		"ohne",
		"wenn"
	],
	es: [
		"el",
		"la",
		"los",
		"las",
		"y",
		"es",
		"son",
		"con",
		"para",
		"en",
		"del",
		"un",
		"una",
		"por",
		"que",
		"se",
		"está",
		"están",
		"al",
		"sin",
		"o",
		"lo",
		"su",
		"sus",
		"hay",
		"desde",
		"entre",
		"todos",
		"todas",
		"cada",
		"como",
		"más",
		"pero",
		"fue",
		"ha",
		"han"
	],
	fr: [
		"le",
		"la",
		"les",
		"et",
		"est",
		"sont",
		"ne",
		"pas",
		"avec",
		"pour",
		"dans",
		"du",
		"des",
		"un",
		"une",
		"par",
		"que",
		"sur",
		"au",
		"aux",
		"sans",
		"ou",
		"qui",
		"il",
		"elle",
		"ce",
		"cette",
		"été",
		"tous",
		"toutes",
		"chaque",
		"plus",
		"mais",
		"depuis",
		"entre",
		"leur",
		"leurs",
		"aucun",
		"aucune"
	],
	tr: [
		"ve",
		"bir",
		"bu",
		"ile",
		"için",
		"değil",
		"yok",
		"var",
		"da",
		"de",
		"çok",
		"olarak",
		"her",
		"ama",
		"gibi",
		"daha",
		"veya",
		"olan",
		"sonra",
		"tüm",
		"şu",
		"hiç",
		"kadar",
		"göre",
		"önce",
		"ise",
		"mi",
		"mı",
		"olmalı",
		"edilmiş",
		"edilir",
		"ediliyor",
		"bulunuyor",
		"olmayan"
	]
};
/** Letters that occur (almost) only in one of the five languages. */
const distinctiveLetters = {
	en: /(?!)/u,
	de: /[äß]/u,
	es: /[ñ¿¡]/u,
	fr: /[èêëàâîïôœùû]/u,
	tr: /[ğışİ]/u
};
const wordIndex = /* @__PURE__ */ new Map();
for (const [locale, words] of Object.entries(functionWords)) for (const word of words) wordIndex.set(word, [...wordIndex.get(word) ?? [], locale]);
const tokenize = (text) => text.toLowerCase().replace(/\u0307/gu, "").split(/[^\p{L}]+/u).filter(Boolean);
/** Most likely language of `text`, or null when it is too short or ambiguous to tell. */
function detectLanguage(text) {
	const lower = text.toLowerCase();
	const words = tokenize(text);
	const scores = {
		en: 0,
		de: 0,
		es: 0,
		fr: 0,
		tr: 0
	};
	for (const word of words) {
		const owners = wordIndex.get(word);
		if (!owners) continue;
		for (const owner of owners) scores[owner] += 1 / owners.length;
	}
	for (const [locale, pattern] of Object.entries(distinctiveLetters)) {
		const matches = lower.match(new RegExp(pattern.source, "gu"));
		if (matches) scores[locale] += Math.min(3, matches.length) * .75;
	}
	const ranked = Object.entries(scores).sort((a, b) => b[1] - a[1]);
	const [best, second] = ranked;
	const total = ranked.reduce((n, [, s]) => n + s, 0);
	if (!best || best[1] < 1.5 || words.length < 3) return {
		locale: null,
		confidence: 0,
		words: words.length
	};
	if (second && best[1] - second[1] < 1) return {
		locale: null,
		confidence: 0,
		words: words.length
	};
	return {
		locale: best[0],
		confidence: total > 0 ? best[1] / total : 0,
		words: words.length
	};
}
/** True when the text is long enough to judge and clearly written in `locale`. */
function clearlyInLanguage(text, locale) {
	const guess = detectLanguage(text);
	return guess.locale === locale && guess.confidence >= .6 && guess.words >= 4;
}
// src/ai/text-guards.ts
/**
* Guards for model-produced text: machine output may rephrase, but must not introduce markup or change
* numbers, placeholders or negations relative to its source.
*/
const markupPattern = /<\s*\/?\s*[a-z!?][^>]*>|javascript\s*:|data\s*:\s*text\/html/i;
const placeholderPattern = /\{\{\s*[\w.-]+\s*\}\}|\{[\w.-]+\}|%[sd]/g;
/** HTML-like tags or script URLs (executable content). */
const containsMarkup = (text) => markupPattern.test(text);
const sorted = (values) => [...values].sort();
const digitRuns = (text) => sorted(text.match(/\d+/g) ?? []);
const placeholdersOf = (text) => sorted((text.match(placeholderPattern) ?? []).map((p) => p.replace(/\s+/g, "")));
const sameMultiset = (a, b) => a.length === b.length && sorted(a).every((value, i) => value === sorted(b)[i]);
/** Negation words of the five launch languages (whole words, case-insensitive). */
const negations = /* @__PURE__ */ new Set([
	"no",
	"not",
	"none",
	"never",
	"nothing",
	"nobody",
	"without",
	"cannot",
	"nicht",
	"kein",
	"keine",
	"keinen",
	"keinem",
	"keiner",
	"keines",
	"nie",
	"niemals",
	"nichts",
	"ohne",
	"ni",
	"nunca",
	"ningún",
	"ninguna",
	"ninguno",
	"sin",
	"nada",
	"ne",
	"pas",
	"aucun",
	"aucune",
	"jamais",
	"sans",
	"rien",
	"değil",
	"yok",
	"hiç",
	"hayır"
]);
/** English contractions typed without an apostrophe ("isnt", "dont"). */
const bareContractions = /* @__PURE__ */ new Set([
	"isnt",
	"arent",
	"wasnt",
	"werent",
	"dont",
	"doesnt",
	"didnt",
	"cant",
	"wont",
	"hasnt",
	"havent",
	"shouldnt",
	"couldnt"
]);
/**
* True when `word` is a negation word of five or more letters with one letter missing ("nich", "nichs").
* Substitutions are not considered: they would turn ordinary words such as "Licht" into negations.
*/
function truncatedNegation(word) {
	if (word.length < 4) return false;
	for (const negation of negations) {
		if (negation.length < 5 || negation.length !== word.length + 1) continue;
		for (let i = 0; i < negation.length; i++) if (negation.slice(0, i) + negation.slice(i + 1) === word) return true;
	}
	return false;
}
/**
* Negation words of the five launch languages. With `typos`, misspelled negations in hand-typed text
* ("nich", "isnt") count too, so a corrected sentence can be compared with its careless original.
*/
function negationCount(text, { typos = false } = {}) {
	return text.toLowerCase().split(/[^\p{L}']+/u).filter((word) => negations.has(word) || word.endsWith("n't") || typos && (bareContractions.has(word) || truncatedNegation(word))).length;
}
/** True when `candidate` keeps every number, placeholder and negation of `source` and adds no markup. */
function preservesFacts(source, candidate) {
	if (containsMarkup(candidate) && !containsMarkup(source)) return false;
	if (!sameMultiset(digitRuns(source), digitRuns(candidate))) return false;
	if (!sameMultiset(placeholdersOf(source), placeholdersOf(candidate))) return false;
	return true;
}
// src/ai/translation-guards.ts
const identifierPattern = /\b(?=[A-Z0-9-]*\d)(?=[A-Z0-9-]*[A-Z])[A-Z0-9]+(?:-[A-Z0-9]+)*\b/g;
/** Codes such as FX-207, TW-12 or B2: capital letters combined with digits. */
const identifiersOf = (text) => [...new Set(text.match(identifierPattern) ?? [])].sort();
const contentWords = (text) => text.toLowerCase().split(/[^\p{L}]+/u).filter((w) => w.length >= 4);
/** Share of the output's longer words that also occur verbatim in the source (1 = nothing translated). */
function untranslatedRatio(source, output) {
	const sourceWords = new Set(contentWords(source));
	const words = contentWords(output);
	if (!words.length) return 0;
	return words.filter((w) => sourceWords.has(w)).length / words.length;
}
const escapeRegExp = (text) => text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const containsTerm = (text, term) => new RegExp(`(^|[^\\p{L}])${escapeRegExp(term.toLowerCase())}`, "u").test(text.toLowerCase());
const wordCount = (text) => text.split(/\s+/).filter((w) => /\p{L}/u.test(w)).length;
/** Every issue found in one translated field; an empty list means the field may be stored. */
function translationIssues(check) {
	const source = check.source.trim();
	const output = check.output.trim();
	const issues = [];
	if (!output) return source ? ["empty"] : [];
	if (output.length > (check.maxLength ?? 8e3)) issues.push("tooLong");
	if (containsMarkup(output) && !containsMarkup(source)) issues.push("markup");
	if (!sameMultiset(digitRuns(source), digitRuns(output))) issues.push("digits");
	if (!sameMultiset(placeholdersOf(source), placeholdersOf(output))) issues.push("placeholders");
	if (identifiersOf(source).some((id) => !output.includes(id))) issues.push("identifiers");
	if (check.keepTerms.some((term) => source.includes(term) && !output.includes(term))) issues.push("keepTerm");
	if (wordCount(source) <= 6 && check.glossary.some((entry) => containsTerm(source, entry.source) && !containsTerm(output, entry.target))) issues.push("glossary");
	if (check.sourceLocale !== check.targetLocale) {
		const letters = wordCount(source);
		const alreadyTarget = clearlyInLanguage(source, check.targetLocale);
		if (letters >= 2 && output === source && identifiersOf(source).join(" ") !== source && !alreadyTarget) issues.push("untranslated");
		else if (!alreadyTarget && clearlyInLanguage(output, check.sourceLocale)) issues.push("wrongLanguage");
		else if (!alreadyTarget && letters >= 8 && untranslatedRatio(source, output) >= .85) issues.push("untranslated");
		if (source.length >= 24) {
			const ratio = output.length / source.length;
			if (ratio < .45 || ratio > 2.6) issues.push("lengthRatio");
		}
	}
	return issues;
}
// src/ai/rewrite-guards.ts
/** Words that state an absence, the usual rewording of a negation ("no label" → "label missing"). */
const absenceWords = /* @__PURE__ */ new Set([
	"missing",
	"lacking",
	"absent",
	"fehlt",
	"fehlen",
	"fehlend",
	"fehlende",
	"fehlenden",
	"falta",
	"faltan",
	"manque",
	"manquent",
	"manquant",
	"manquante",
	"eksik"
]);
function absenceCount(text) {
	return text.toLowerCase().split(/[^\p{L}']+/u).filter((word) => absenceWords.has(word)).length;
}
/**
* Checks for a reworded remark or a corrected transcript: same language, every number, placeholder,
* identifier and negation kept (none dropped, none added), no markup, and no large expansion or loss of
* content. Misspelled negations in the original ("nich", "isnt") are counted as negations.
*/
function rewriteIssues(original, suggestion, locale, { minRatio = .45, maxRatio = 2.2 } = {}) {
	const before = original.trim();
	const after = suggestion.trim();
	if (!after) return before ? ["empty"] : [];
	const issues = [];
	if (containsMarkup(after) && !containsMarkup(before)) issues.push("markup");
	if (!sameMultiset(digitRuns(before), digitRuns(after))) issues.push("digits");
	if (!sameMultiset(placeholdersOf(before), placeholdersOf(after))) issues.push("placeholders");
	if (identifiersOf(before).some((id) => !after.includes(id))) issues.push("identifiers");
	if (negationCount(before, { typos: true }) + absenceCount(before) !== negationCount(after) + absenceCount(after)) issues.push("negations");
	if (before.length >= 20) {
		const ratio = after.length / before.length;
		if (ratio < minRatio || ratio > maxRatio) issues.push("lengthRatio");
	}
	const guess = detectLanguage(after);
	if (locale && guess.locale && guess.locale !== locale && guess.confidence >= .7 && guess.words >= 5) issues.push("wrongLanguage");
	return issues;
}
// src/page/color.ts
const hexPattern$1 = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i;
function channels(hex) {
	const m = hexPattern$1.exec(hex);
	if (!m) return null;
	return [
		parseInt(m[1] ?? "0", 16),
		parseInt(m[2] ?? "0", 16),
		parseInt(m[3] ?? "0", 16)
	];
}
/** WCAG relative luminance of an opaque #RRGGBB color. */
function luminance(hex) {
	const rgb = channels(hex);
	if (!rgb) return 0;
	const [r, g, b] = rgb.map((v) => {
		const c = v / 255;
		return c <= .04045 ? c / 12.92 : ((c + .055) / 1.055) ** 2.4;
	});
	return .2126 * r + .7152 * g + .0722 * b;
}
function contrastRatio(a, b) {
	const la = luminance(a);
	const lb = luminance(b);
	return (Math.max(la, lb) + .05) / (Math.min(la, lb) + .05);
}
/** Choose a readable foreground (dark ink or white) for a background. */
function readableOn(background, dark = "#101424", light = "#FFFFFF") {
	return contrastRatio(background, dark) >= contrastRatio(background, light) ? dark : light;
}
// src/billing/brand-accent.ts
/**
* Sidebar surfaces and text of the design tokens (`spec/ux/design-tokens.json`: sidebar, nav-active,
* foreground, muted-foreground, nav-indicator). White-label accents are checked against these and never replace status colors.
*/
const sidebarSurfaces = {
	light: {
		sidebar: "#FBF8F1",
		active: "#E2DCCC",
		foreground: "#10231A",
		muted: "#4E5A52",
		indicator: "#006B2E"
	},
	dark: {
		sidebar: "#0E0D0B",
		active: "#2C2B27",
		foreground: "#EFEDE5",
		muted: "#B6B4AD",
		indicator: "#3FC168"
	}
};
/** WCAG 2.2: 3:1 for the current-destination bar and icon (1.4.11), 4.5:1 for the brand name (1.4.3). */
const indicatorContrast = 3;
const textContrast = 4.5;
/** At most this far toward black or white; beyond it the organization's hue is gone and the default stays. */
const maxShift = .6;
const minimumDistinctness = 1.25;
/** How far the whole sidebar background moves toward the accent: a recognizable tint, never a colored panel. */
const surfaceTint = {
	light: .08,
	dark: .12
};
/** Active and hovered destinations sit further toward the accent than the tinted background. */
const activeTint = {
	light: .18,
	dark: .26
};
const hoverTint = {
	light: .1,
	dark: .15
};
const hexPattern = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i;
function rgb(hex) {
	const m = hexPattern.exec(hex);
	return m ? [
		parseInt(m[1] ?? "0", 16),
		parseInt(m[2] ?? "0", 16),
		parseInt(m[3] ?? "0", 16)
	] : null;
}
/** Linear blend of two opaque colors in sRGB (t = 0 → a, t = 1 → b). */
function mixColors(a, b, t) {
	const x = rgb(a);
	const y = rgb(b);
	if (!x || !y) return a.toUpperCase();
	return `#${x.map((v, i) => Math.round(v + ((y[i] ?? v) - v) * t).toString(16).padStart(2, "0")).join("")}`.toUpperCase();
}
/** Move a color toward black (light surfaces) or white (dark surfaces) until it reaches the contrast. */
function adjusted(color, background, min) {
	const target = luminance(background) < .18 ? "#FFFFFF" : "#000000";
	for (let step = 0; step <= 12; step++) {
		const candidate = mixColors(color, target, step / 12 * maxShift);
		if (contrastRatio(candidate, background) >= min) return candidate;
	}
	return null;
}
/**
* The organization's sidebar theme for one color scheme: a tinted sidebar background, the
* hover and active-destination surfaces, the navigation indicator (bar and icon of the current destination)
* and the brand-name text, all derived from the accent and checked against WCAG 2.2 (4.5:1 for text on every
* surface, 3:1 for the indicator). The tint is dropped when the sidebar text would no longer be readable on it;
* null keeps the HLT defaults when no variant close to the chosen hue is readable.
*/
function sidebarAccent(accent, theme) {
	if (!accent || !rgb(accent)) return null;
	const base = sidebarSurfaces[theme];
	if (contrastRatio(accent, base.sidebar) < minimumDistinctness) return null;
	const readable = (background) => contrastRatio(base.foreground, background) >= textContrast && contrastRatio(base.muted, background) >= textContrast;
	const tinted = mixColors(base.sidebar, accent, surfaceTint[theme]);
	const surface = readable(tinted) ? tinted : base.sidebar;
	const subtle = mixColors(surface, accent, activeTint[theme]);
	if (contrastRatio(base.foreground, subtle) < textContrast) return null;
	const hoverCandidate = mixColors(surface, accent, hoverTint[theme]);
	const hover = readable(hoverCandidate) ? hoverCandidate : surface;
	const indicatorOnSidebar = adjusted(accent, surface, indicatorContrast);
	const indicator = indicatorOnSidebar && adjusted(indicatorOnSidebar, subtle, indicatorContrast);
	const text = adjusted(accent, surface, textContrast);
	if (!indicator || !text) return null;
	return {
		indicator,
		text,
		subtle,
		surface,
		hover
	};
}
// src/billing/cost.ts
/** HLT invoices from Germany; domestic business customers pay German VAT on net prices. */
const sellerCountry = "DE";
const germanVatBasisPoints = 1900;
/** EU member states (ISO 3166-1 alpha-2) for the reverse-charge rule. */
const euCountries = /* @__PURE__ */ new Set([
	"AT",
	"BE",
	"BG",
	"CY",
	"CZ",
	"DE",
	"DK",
	"EE",
	"ES",
	"FI",
	"FR",
	"GR",
	"HR",
	"HU",
	"IE",
	"IT",
	"LT",
	"LU",
	"LV",
	"MT",
	"NL",
	"PL",
	"PT",
	"RO",
	"SE",
	"SI",
	"SK"
]);
/**
* Whether billing asks for a VAT ID: only business customers in another EU member state, for whom it decides
* reverse charge. German customers pay German VAT and customers outside the EU are not taxable in Germany.
*/
const asksForVatId = (country) => country !== "DE" && euCountries.has(country);
/**
* How VAT applies to an invoice (German/EU B2B, prices exclusive of VAT; to be confirmed by a tax advisor).
* Domestic customers pay 19 %; EU business customers with a VAT ID are invoiced under reverse charge; customers
* outside the EU are not taxable in Germany. Without billing details, or for an EU customer without a VAT ID,
* the treatment is unknown and estimates assume German VAT.
*/
function vatTreatment(billing) {
	if (!billing) return "unknown";
	if (billing.country === "DE") return "domestic";
	if (!euCountries.has(billing.country)) return "notTaxable";
	return billing.vatId ? "reverseCharge" : "unknown";
}
const vatBasisPointsFor = (treatment) => treatment === "domestic" || treatment === "unknown" ? germanVatBasisPoints : 0;
/** VAT on an integer cent amount, rounded half away from zero (one line per rate, like the invoice). */
function vatOf(net, basisPoints) {
	const raw = Math.abs(net) * basisPoints / 1e4;
	return Math.sign(net) * Math.round(raw);
}
/** Monthly: price per member × agreed members; add-ons: one recurring line each. */
function recurringCost(terms, billing) {
	const memberNet = terms.memberUnitAmount * terms.agreedMembers;
	const addonsNet = terms.capabilities.reduce((sum, c) => sum + c.unitAmount, 0);
	const net = memberNet + addonsNet;
	const treatment = vatTreatment(billing);
	const vatRateBasisPoints = vatBasisPointsFor(treatment);
	const tax = vatOf(net, vatRateBasisPoints);
	return {
		interval: "month",
		memberNet,
		addonsNet,
		net,
		tax,
		gross: net + tax,
		vatRateBasisPoints,
		vatTreatment: treatment
	};
}
/** One-time amount (the allowance reset) with VAT for the customer's treatment. */
function oneTimeAmount(net, billing) {
	const tax = vatOf(net, vatBasisPointsFor(vatTreatment(billing)));
	return {
		net,
		tax,
		gross: net + tax
	};
}
// src/time/zone.ts
/** Calendar parts of an instant in an IANA timezone. */
function zonedParts(instant, timeZone) {
	const fmt = new Intl.DateTimeFormat("en-US", {
		timeZone,
		year: "numeric",
		month: "2-digit",
		day: "2-digit",
		hour: "2-digit",
		minute: "2-digit",
		hourCycle: "h23",
		weekday: "short"
	});
	const parts = Object.fromEntries(fmt.formatToParts(instant).map((p) => [p.type, p.value]));
	return {
		year: Number(parts.year),
		month: Number(parts.month),
		day: Number(parts.day),
		hour: Number(parts.hour),
		minute: Number(parts.minute),
		weekday: [
			"Sun",
			"Mon",
			"Tue",
			"Wed",
			"Thu",
			"Fri",
			"Sat"
		].indexOf(parts.weekday ?? "Sun")
	};
}
/** Convert a wall-clock local date/time in a zone to the UTC instant (DST-aware, iterative). */
function zonedTimeToUtc(local, timeZone) {
	let guess = Date.UTC(local.year, local.month - 1, local.day, local.hour, local.minute);
	for (let i = 0; i < 3; i++) {
		const p = zonedParts(new Date(guess), timeZone);
		const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute);
		const target = Date.UTC(local.year, local.month - 1, local.day, local.hour, local.minute);
		guess += target - asUtc;
	}
	return new Date(guess);
}
/** Bucket key (day/week/month) for an instant in the workspace timezone. Weeks start Monday. */
function bucketKey(instant, timeZone, bucket) {
	const p = zonedParts(instant, timeZone);
	const pad = (n) => String(n).padStart(2, "0");
	if (bucket === "month") return `${p.year}-${pad(p.month)}`;
	if (bucket === "day") return `${p.year}-${pad(p.month)}-${pad(p.day)}`;
	const d = new Date(Date.UTC(p.year, p.month - 1, p.day));
	const offset = (p.weekday + 6) % 7;
	d.setUTCDate(d.getUTCDate() - offset);
	return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}
function isValidTimeZone(tz) {
	try {
		new Intl.DateTimeFormat("en-US", { timeZone: tz });
		return true;
	} catch {
		return false;
	}
}
// src/billing/period.ts
/** Billing dates are computed in Europe/Berlin, like other HLT dates. */
const billingTimeZone = "Europe/Berlin";
/**
* The same local time `months` calendar months later, clamped to the month's last day (31 Jan → 28/29 Feb).
* The free first month ends one calendar month after the agreement starts.
*/
function addCalendarMonths(instant, months, timeZone = billingTimeZone) {
	const p = zonedParts(instant, timeZone);
	const first = new Date(Date.UTC(p.year, p.month - 1 + months, 1));
	const lastDay = new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth() + 1, 0)).getUTCDate();
	const local = zonedTimeToUtc({
		year: first.getUTCFullYear(),
		month: first.getUTCMonth() + 1,
		day: Math.min(p.day, lastDay),
		hour: p.hour,
		minute: p.minute
	}, timeZone);
	return new Date(local.getTime() + instant.getUTCSeconds() * 1e3 + instant.getUTCMilliseconds());
}
/** The billing period containing `at` for a subscription anchored at `anchor` (monthly or yearly). */
function periodContaining(anchor, interval, at, timeZone = billingTimeZone) {
	const step = interval === "year" ? 12 : 1;
	let start = anchor;
	let end = addCalendarMonths(anchor, step, timeZone);
	for (let i = 0; i < 1200 && end.getTime() <= at.getTime(); i++) {
		start = end;
		end = addCalendarMonths(anchor, step * (i + 2), timeZone);
	}
	return {
		start,
		end
	};
}
// src/billing/terms.ts
const bytesPerGb = 1024 ** 3;
/** AI allowance keys stored in a plan. */
const planAllowanceKeys = Object.keys(aiAllowanceCapability);
const capabilityOrder = (key) => planCapabilityKeys.indexOf(key);
/**
* Canonical terms: one entry per capability (the last one wins), in catalog order, and no allowance for an AI
* feature the terms do not include, so an excluded feature is never counted, shown or offered.
*/
function normalizeTerms(terms) {
	const capabilities = [...new Map(terms.capabilities.map((c) => [c.key, c])).values()].sort((a, b) => capabilityOrder(a.key) - capabilityOrder(b.key));
	const included = new Set(capabilities.map((c) => c.key));
	const allowances = Object.fromEntries(planAllowanceKeys.map((key) => [key, included.has(aiAllowanceCapability[key]) ? terms.allowances[key] : 0]));
	return {
		...terms,
		capabilities,
		allowances
	};
}
const priceOf = (capabilities, key) => capabilities.find((c) => c.key === key)?.unitAmount;
/** Compare two agreements field by field, so a mixed change (more members, one add-on less) is visible. */
function changeDirection(current, next) {
	let increases = false;
	let decreases = false;
	const compare = (a, b) => {
		if (b > a) increases = true;
		if (b < a) decreases = true;
	};
	compare(current.memberUnitAmount, next.memberUnitAmount);
	compare(current.agreedMembers, next.agreedMembers);
	compare(current.storageGb, next.storageGb);
	compare(current.imageLimit, next.imageLimit);
	for (const key of planAllowanceKeys) compare(current.allowances[key], next.allowances[key]);
	for (const key of planCapabilityKeys) {
		const before = priceOf(current.capabilities, key);
		const after = priceOf(next.capabilities, key);
		if (before === void 0 && after !== void 0) increases = true;
		else if (before !== void 0 && after === void 0) decreases = true;
		else if (before !== void 0 && after !== void 0) compare(before, after);
	}
	return {
		increases,
		decreases
	};
}
/**
* When a change takes effect, chosen automatically: increases now with proration,
* decreases at the next billing period (no credit). A mixed change starts now; the summary shows the decrease.
*/
function defaultTiming(current, next) {
	const { increases, decreases } = changeDirection(current, next);
	return decreases && !increases ? "nextPeriod" : "now";
}
/**
* Prorated net amount for a change of the recurring net at `at` within the period (Stripe prorates to the
* second: Δ × remaining ÷ period length). Negative values are credits applied to a later invoice.
*/
function prorationNet(deltaNet, periodStart, periodEnd, at) {
	const length = periodEnd.getTime() - periodStart.getTime();
	if (length <= 0 || deltaNet === 0) return 0;
	const remaining = Math.min(Math.max(periodEnd.getTime() - at.getTime(), 0), length);
	return Math.round(deltaNet * remaining / length);
}
// src/csv/format.ts
const csvHeader = [
	"categoryKey",
	"categoryTitle",
	"categoryWeight",
	"questionKey",
	"title",
	"description",
	"required",
	"allowNotApplicable",
	"weight",
	"scoring",
	"categoryKind",
	"showWhen",
	"prefill"
];
/** Columns added later; files without them stay valid and read as a scored category without conditions. */
const optionalCsvColumns = [
	"categoryKind",
	"showWhen",
	"prefill"
];
const quote = (s) => /["\n\r,;]/.test(s) ? `"${s.replace(/"/g, "\"\"")}"` : s;
/** Escape a value for tabular data export and defend against spreadsheet formula injection. */
function csvCell(value) {
	let s = value === null || value === void 0 ? "" : String(value);
	if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
	return quote(s);
}
/** Serialize a definition into the documented CSV exchange format. */
function definitionToCsv(def, delimiter = ",") {
	const lines = [csvHeader.join(delimiter)];
	for (const c of def.categories) for (const q of c.questions) {
		const cells = [
			c.key,
			c.title,
			c.weight,
			q.key,
			q.title,
			q.description,
			q.required,
			q.allowNotApplicable,
			q.weight
		];
		const extra = [
			c.kind ?? "scored",
			(q.showWhen ?? []).join(" "),
			q.prefill ?? ""
		];
		lines.push([
			...cells.map((v) => quote(String(v))),
			quote(JSON.stringify(q.scoring)),
			...extra.map(quote)
		].join(delimiter));
	}
	return lines.join("\r\n");
}
// src/csv/parse.ts
/** RFC 4180 parser with a selectable delimiter: quoted multiline fields, escaped quotes, BOM, CRLF/LF. */
function parseCsv(text, delimiter) {
	const input = text.startsWith("﻿") ? text.slice(1) : text;
	const rows = [];
	let row = [];
	let field = "";
	let quoted = false;
	const endField = () => {
		row.push(field);
		field = "";
	};
	const endRow = () => {
		endField();
		rows.push(row);
		row = [];
	};
	for (let i = 0; i < input.length; i++) {
		const ch = input[i];
		if (quoted) {
			if (ch !== "\"") field += ch;
			else if (input[i + 1] === "\"") {
				field += "\"";
				i++;
			} else quoted = false;
			continue;
		}
		if (ch === "\"" && field === "") quoted = true;
		else if (ch === delimiter) endField();
		else if (ch === "\r" && input[i + 1] === "\n") {
			endRow();
			i++;
		} else if (ch === "\n" || ch === "\r") endRow();
		else field += ch;
	}
	if (quoted) return {
		rows,
		error: "Unterminated quoted field"
	};
	if (field !== "" || row.length) endRow();
	return {
		rows,
		error: null
	};
}
const isBlankRow = (row) => row.every((c) => c.trim() === "");
// src/validation.ts
/** Map Zod issues to API field errors with dotted paths. */
function issuesToFieldErrors(issues) {
	return issues.map((i) => ({
		path: i.path.map(String).join(".") || "(root)",
		message: i.message
	}));
}
// src/template/answer-types.ts
/**
* The three ways a question is answered: "Yes / No", a count of defects ("None / 1 / 2 / 3 / 4 / >4") or a
* maturity level from 0 to 4 for how far a standard is in place. "Unknown" is always offered beside them; it
* is the not-applicable answer and is left out of the score. Authors never see points: the scoring below is
* fixed and internal. Maturity levels carry a description per level, which authors may reword.
*/
const answerTypes = [
	"yesNo",
	"defects",
	"maturity"
];
/** Internal display scale of every new template; it is never shown or chosen. */
const fixedDisplayMaximum = 10;
/** Option labels in each content language; numbers read the same everywhere. */
const answerLabels = {
	en: {
		yes: "Yes",
		no: "No",
		none: "None",
		maturity: [
			"Not in place",
			"Exists but is not followed",
			"Followed in parts of the area",
			"Followed throughout, with minor gaps",
			"Followed throughout and kept up to date"
		]
	},
	de: {
		yes: "Ja",
		no: "Nein",
		none: "Keine",
		maturity: [
			"Nicht vorhanden",
			"Vorhanden, wird aber nicht befolgt",
			"In Teilen des Bereichs befolgt",
			"Überall befolgt, mit kleinen Lücken",
			"Überall befolgt und aktuell gehalten"
		]
	},
	es: {
		yes: "Sí",
		no: "No",
		none: "Ninguno",
		maturity: [
			"No existe",
			"Existe, pero no se sigue",
			"Se sigue en partes del área",
			"Se sigue en toda el área, con pequeñas carencias",
			"Se sigue en toda el área y se mantiene actualizado"
		]
	},
	fr: {
		yes: "Oui",
		no: "Non",
		none: "Aucun",
		maturity: [
			"Inexistant",
			"Existe, mais n'est pas appliqué",
			"Appliqué dans une partie de la zone",
			"Appliqué partout, avec de petits écarts",
			"Appliqué partout et tenu à jour"
		]
	},
	tr: {
		yes: "Evet",
		no: "Hayır",
		none: "Yok",
		maturity: [
			"Mevcut değil",
			"Mevcut ama uygulanmıyor",
			"Alanın bazı bölümlerinde uygulanıyor",
			"Her yerde uygulanıyor, küçük eksikler var",
			"Her yerde uygulanıyor ve güncel tutuluyor"
		]
	}
};
const yesNoKeys = ["yes", "no"];
/** The starter's defect keys (kept, so existing answers and comparisons stay valid). */
const defectKeys = [
	"defects-0",
	"defects-1",
	"defects-2",
	"defects-3",
	"defects-4",
	"defects-5-plus"
];
const defectLabels = [
	"",
	"1",
	"2",
	"3",
	"4",
	">4"
];
const maturityKeys = [
	"maturity-0",
	"maturity-1",
	"maturity-2",
	"maturity-3",
	"maturity-4"
];
/** The fixed, internal scoring of an answer type, labeled in the content language. */
function answerScoring(type, locale) {
	const labels = answerLabels[locale];
	if (type === "yesNo") return {
		kind: "choice",
		options: [{
			key: "yes",
			label: labels.yes,
			value: 10,
			quality: 100,
			critical: false
		}, {
			key: "no",
			label: labels.no,
			value: 0,
			quality: 0,
			critical: false
		}]
	};
	if (type === "maturity") return {
		kind: "choice",
		options: maturityKeys.map((key, level) => ({
			key,
			label: labels.maturity[level] ?? String(level),
			value: 10 * level / 4,
			quality: 25 * level,
			critical: false
		}))
	};
	return {
		kind: "choice",
		options: defectKeys.map((key, n) => ({
			key,
			label: n === 0 ? labels.none : defectLabels[n] ?? String(n),
			value: 10 - 2 * n,
			quality: 100 - 20 * n,
			critical: false
		}))
	};
}
const sameKeys = (scoring, keys) => scoring.kind === "choice" && scoring.options.length === keys.length && scoring.options.every((o, i) => o.key === keys[i]);
/** The answer type of a stored rule, or null for an older custom rule (kept until the author changes it). */
function answerTypeOf(scoring) {
	if (!scoring) return null;
	if (sameKeys(scoring, yesNoKeys)) return "yesNo";
	if (sameKeys(scoring, defectKeys)) return "defects";
	if (sameKeys(scoring, maturityKeys)) return "maturity";
	return null;
}
/**
* The nearest answer type for any rule (used for imported and generated content): two options are a yes/no
* check, a recognized maturity scale stays one, anything else counts defects.
*/
function closestAnswerType(scoring) {
	if (!scoring) return "yesNo";
	const known = answerTypeOf(scoring);
	if (known) return known;
	return scoring.kind === "choice" && scoring.options.length === 2 ? "yesNo" : "defects";
}
/** Keep the level descriptions an author wrote for a maturity question; other types have fixed labels. */
function keptScoring(type, current, locale) {
	const fixed = answerScoring(type, locale);
	if (type !== "maturity" || fixed.kind !== "choice" || !current || answerTypeOf(current) !== "maturity") return fixed;
	const labels = current.kind === "choice" ? current.options.map((o) => o.label) : [];
	return {
		...fixed,
		options: fixed.options.map((o, i) => ({
			...o,
			label: labels[i]?.trim() || o.label
		}))
	};
}
/**
* A question as authors create it: Unknown always allowed, equal weight, the given answer type. Its area
* condition and prefill stay as they were.
*/
function standardQuestion(question, type, locale) {
	return {
		key: question.key,
		title: question.title,
		description: question.description,
		required: true,
		allowNotApplicable: true,
		weight: 1,
		scoring: keptScoring(type, question.scoring, locale),
		...question.showWhen?.length ? { showWhen: question.showWhen } : {},
		...question.prefill ? { prefill: question.prefill } : {}
	};
}
/** The authoring defaults applied to a draft question on every save: Unknown is always offered. */
function withAuthoringDefaults(question) {
	return question.allowNotApplicable && question.required ? question : {
		...question,
		allowNotApplicable: true,
		required: true
	};
}
/**
* Imported or generated content brought to the answer types with the fixed internal scoring: equal
* weights, no critical options, Unknown always allowed and the internal 0–10 scale. A safety category stays
* one; its failed answers count as safety findings by category, not by option flags.
*/
function normalizeToAnswerTypes(definition, locale) {
	const category = (c) => ({
		...c,
		weight: 1,
		questions: c.questions.map((q) => standardQuestion(q, closestAnswerType(q.scoring), locale))
	});
	return {
		...definition,
		displayMaximum: 10,
		categories: definition.categories.map(category)
	};
}
/** Draft variant of {@link normalizeToAnswerTypes}; questions without a rule become yes/no checks. */
function normalizeDraftToAnswerTypes(definition, locale) {
	return {
		...definition,
		displayMaximum: 10,
		categories: definition.categories.map((c) => ({
			...c,
			weight: 1,
			questions: c.questions.map((q) => standardQuestion(q, closestAnswerType(q.scoring), locale))
		}))
	};
}
// src/scoring/decimal.ts
/** Decimal-safe helpers: values are compared as scaled integers, never by binary float equality. */
function decimalPlaces(n) {
	if (!Number.isFinite(n)) return 0;
	const s = n.toString();
	const exponent = s.split("e-")[1];
	if (exponent !== void 0) return Number(exponent);
	const i = s.indexOf(".");
	return i === -1 ? 0 : s.length - i - 1;
}
function isOnStep(value, minimum, step) {
	const scale = 10 ** Math.max(decimalPlaces(value), decimalPlaces(minimum), decimalPlaces(step));
	const s = Math.round(step * scale);
	if (s <= 0) return false;
	return (Math.round(value * scale) - Math.round(minimum * scale)) % s === 0;
}
/** Round half-up to `digits` decimals, compensating representation error such as 8.55 → 8.549999. */
function roundHalfUp(value, digits = 1) {
	const scale = 10 ** digits;
	return Math.floor(value * scale + .5 + 1e-9) / scale;
}
// src/template/rules.ts
const keyPattern = /^[a-z0-9][a-z0-9._-]{0,79}$/i;
const keyMessage = "Use letters, numbers, dot, dash or underscore";
function validateChoice(scoring, path) {
	const errors = [];
	const keys = /* @__PURE__ */ new Set();
	scoring.options.forEach((option, i) => {
		if (keys.has(option.key)) errors.push({
			path: `${path}.options.${i}.key`,
			message: "Option keys must be unique"
		});
		keys.add(option.key);
		if (!keyPattern.test(option.key)) errors.push({
			path: `${path}.options.${i}.key`,
			message: keyMessage
		});
		if (!option.label.trim()) errors.push({
			path: `${path}.options.${i}.label`,
			message: "Label is required"
		});
	});
	return errors;
}
function validateBands(scoring, path) {
	const errors = [];
	const { minimum, maximum, step } = scoring;
	if (!(maximum > minimum)) errors.push({
		path: `${path}.maximum`,
		message: "Maximum must be greater than minimum"
	});
	if (!isOnStep(maximum, minimum, step)) errors.push({
		path: `${path}.step`,
		message: "Maximum must be reachable from minimum in whole steps"
	});
	const sorted = scoring.bands.map((band, index) => ({
		band,
		index
	})).sort((a, b) => a.band.lower - b.band.lower);
	let cursor = minimum;
	sorted.forEach(({ band, index }, position) => {
		const p = `${path}.bands.${index}`;
		if (!(band.upper > band.lower)) errors.push({
			path: p,
			message: "Band upper bound must exceed its lower bound"
		});
		if (band.lower < cursor) errors.push({
			path: p,
			message: "Bands overlap"
		});
		if (band.lower > cursor) errors.push({
			path: p,
			message: "Bands leave a gap; intervals must be exhaustive"
		});
		if (band.lower < minimum || band.upper > maximum) errors.push({
			path: p,
			message: "Band lies outside the input range"
		});
		if (!band.label.trim()) errors.push({
			path: `${p}.label`,
			message: "Label is required"
		});
		if (position < sorted.length - 1 && band.includeUpper) errors.push({
			path: `${p}.includeUpper`,
			message: "Only the final band may include its upper bound"
		});
		cursor = band.upper;
	});
	if (cursor !== maximum) errors.push({
		path: `${path}.bands`,
		message: "Bands must cover the range up to the maximum"
	});
	return errors;
}
/** Cross-field validation of a scoring rule (JSON Schema cannot express coverage or overlap). */
function validateScoring(scoring, path) {
	return scoring.kind === "choice" ? validateChoice(scoring, path) : validateBands(scoring, path);
}
// src/template/publish.ts
const maxQuestions = 200;
function crossFieldErrors(def) {
	const errors = [];
	const categoryKeys = /* @__PURE__ */ new Set();
	const questionKeys = /* @__PURE__ */ new Set();
	let questions = 0;
	def.categories.forEach((category, ci) => {
		if (categoryKeys.has(category.key)) errors.push({
			path: `categories.${ci}.key`,
			message: "Category keys must be unique"
		});
		categoryKeys.add(category.key);
		if (!keyPattern.test(category.key)) errors.push({
			path: `categories.${ci}.key`,
			message: keyMessage
		});
		if (!category.title.trim()) errors.push({
			path: `categories.${ci}.title`,
			message: "Title is required"
		});
		category.questions.forEach((question, qi) => {
			questions += 1;
			const p = `categories.${ci}.questions.${qi}`;
			if (questionKeys.has(question.key)) errors.push({
				path: `${p}.key`,
				message: "Question keys must be unique across the template"
			});
			questionKeys.add(question.key);
			if (!keyPattern.test(question.key)) errors.push({
				path: `${p}.key`,
				message: keyMessage
			});
			if (!question.title.trim()) errors.push({
				path: `${p}.title`,
				message: "Title is required"
			});
			errors.push(...validateScoring(question.scoring, `${p}.scoring`));
			if (question.prefill && answerTypeOf(question.scoring) !== "defects") errors.push({
				path: `${p}.prefill`,
				message: "Only a number-of-defects question can be filled in"
			});
		});
	});
	if (questions > maxQuestions) errors.push({
		path: "categories",
		message: `At most ${maxQuestions} questions`
	});
	return errors;
}
/** Publish-time validation: the strict immutable definition plus cross-field rules. */
function validateForPublish(input) {
	const parsed = TemplateDefinition.safeParse(input);
	if (!parsed.success) return {
		definition: null,
		errors: issuesToFieldErrors(parsed.error.issues)
	};
	const errors = crossFieldErrors(parsed.data);
	return {
		definition: errors.length ? null : parsed.data,
		errors
	};
}
/** Draft saves accept incomplete content but still reject invalid structure. */
function validateDraft(input) {
	const parsed = DraftTemplateDefinition.safeParse(input);
	if (!parsed.success) return {
		draft: null,
		errors: issuesToFieldErrors(parsed.error.issues)
	};
	if (parsed.data.categories.reduce((n, c) => n + c.questions.length, 0) > maxQuestions) return {
		draft: null,
		errors: [{
			path: "categories",
			message: `At most ${maxQuestions} questions`
		}]
	};
	return {
		draft: parsed.data,
		errors: []
	};
}
/** A blank draft; its content language is recorded by the server from the author (no template language). */
function emptyDraft(title = "") {
	return {
		title,
		description: "",
		aggregation: "weightedCategoryMean",
		displayMaximum: 10,
		categories: []
	};
}
// src/template/area-facts.ts
const areaFacts = [
	"hazardousSubstances",
	"ppeRequired",
	"deskWork"
];
/** Narrows a stored value to a known fact; unknown values from storage are ignored. */
const isAreaFact = (value) => areaFacts.includes(value);
/** True for categories reported as a safety status instead of being averaged into the score. */
const isSafetyCategory = (category) => category.kind === "safety";
/**
* The questions an audit of an area contains: a question with an area condition is kept only when the area has
* every fact it names, and a category left without questions is dropped. Hidden questions are not "Unknown":
* they are not part of the audit and do not reduce its coverage. The definition is returned unchanged when
* nothing would remain, so an audit always has something to answer.
*/
function definitionForArea(definition, facts) {
	const has = new Set(facts);
	const categories = definition.categories.map((category) => ({
		...category,
		questions: category.questions.filter((q) => (q.showWhen ?? []).every((fact) => has.has(fact)))
	})).filter((category) => category.questions.length > 0);
	return categories.length > 0 ? {
		...definition,
		categories
	} : definition;
}
/** Facts in the fixed order, without duplicates, so stored values compare equal. */
function normalizeAreaFacts(facts) {
	const given = new Set(facts ?? []);
	return areaFacts.filter((fact) => given.has(fact));
}
// src/csv/import.ts
const maxRows = 200;
const parseBool = (v) => v === "true" ? true : v === "false" ? false : null;
const parseWeight = (v) => {
	if (!/^\d+(\.\d+)?$/.test(v.trim())) return null;
	const n = Number(v.trim());
	return n >= .01 && n <= 100 ? n : null;
};
function headerErrors(header, row) {
	const errors = [];
	for (const h of csvHeader) if (!header.includes(h) && !optionalCsvColumns.includes(h)) errors.push({
		row,
		column: h,
		message: "Missing header"
	});
	for (const h of header) if (!csvHeader.includes(h)) errors.push({
		row,
		column: h || "(blank)",
		message: "Unknown header"
	});
	return errors;
}
function parseScoring(raw, row, errors) {
	let json;
	try {
		json = JSON.parse(raw);
	} catch {
		errors.push({
			row,
			column: "scoring",
			message: "Invalid JSON"
		});
		return null;
	}
	const result = Scoring.safeParse(json);
	if (result.success) return result.data;
	errors.push({
		row,
		column: "scoring",
		message: `Unsupported scoring rule: ${result.error.issues[0]?.message ?? "invalid"}`
	});
	return null;
}
function parseKind(raw, row, errors) {
	const value = raw.trim() || "scored";
	if (value === "scored" || value === "safety") return value;
	errors.push({
		row,
		column: "categoryKind",
		message: "Use scored or safety"
	});
	return "scored";
}
function parseShowWhen(raw, row, errors) {
	const facts = [];
	for (const word of raw.split(/\s+/).filter(Boolean)) {
		const fact = areaFacts.find((f) => f === word);
		if (fact) facts.push(fact);
		else errors.push({
			row,
			column: "showWhen",
			message: `Use area facts: ${areaFacts.join(", ")}`
		});
	}
	return facts;
}
function parsePrefill(raw, row, errors) {
	const value = raw.trim();
	if (!value) return null;
	if (value === "previousOpenActions") return value;
	errors.push({
		row,
		column: "prefill",
		message: "Use previousOpenActions or leave empty"
	});
	return null;
}
var DefinitionBuilder = class {
	categories = [];
	meta = /* @__PURE__ */ new Map();
	questionKeys = /* @__PURE__ */ new Set();
	addRow(get, row, errors) {
		const categoryKey = get("categoryKey").trim();
		const categoryTitle = get("categoryTitle").trim();
		const categoryWeight = parseWeight(get("categoryWeight"));
		const questionKey = get("questionKey").trim();
		const title = get("title").trim();
		const required = parseBool(get("required").trim());
		const allowNotApplicable = parseBool(get("allowNotApplicable").trim());
		const weight = parseWeight(get("weight"));
		const before = errors.length;
		if (!categoryKey) errors.push({
			row,
			column: "categoryKey",
			message: "Required"
		});
		if (!categoryTitle) errors.push({
			row,
			column: "categoryTitle",
			message: "Required"
		});
		if (categoryWeight === null) errors.push({
			row,
			column: "categoryWeight",
			message: "Use a decimal from 0.01 to 100 with a dot"
		});
		if (!questionKey) errors.push({
			row,
			column: "questionKey",
			message: "Required"
		});
		else if (this.questionKeys.has(questionKey)) errors.push({
			row,
			column: "questionKey",
			message: "Duplicate question key"
		});
		this.questionKeys.add(questionKey);
		if (!title) errors.push({
			row,
			column: "title",
			message: "Required"
		});
		if (required === null) errors.push({
			row,
			column: "required",
			message: "Use true or false"
		});
		if (allowNotApplicable === null) errors.push({
			row,
			column: "allowNotApplicable",
			message: "Use true or false"
		});
		if (weight === null) errors.push({
			row,
			column: "weight",
			message: "Use a decimal from 0.01 to 100 with a dot"
		});
		const scoring = parseScoring(get("scoring"), row, errors);
		const kind = parseKind(get("categoryKind"), row, errors);
		const showWhen = parseShowWhen(get("showWhen"), row, errors);
		const prefill = parsePrefill(get("prefill"), row, errors);
		if (categoryKey && categoryWeight !== null) this.categoryFor(categoryKey, categoryTitle, categoryWeight, kind, row, errors);
		const category = this.meta.get(categoryKey);
		if (errors.length > before || !category || !scoring || required === null || allowNotApplicable === null || weight === null) return;
		category.questions.push({
			key: questionKey,
			title,
			description: get("description"),
			required,
			allowNotApplicable,
			weight,
			scoring,
			...showWhen.length ? { showWhen } : {},
			...prefill ? { prefill } : {}
		});
	}
	categoryFor(key, title, weight, kind, row, errors) {
		const existing = this.meta.get(key);
		if (!existing) {
			const category = {
				key,
				title,
				weight,
				questions: [],
				...kind === "safety" ? { kind } : {}
			};
			this.meta.set(key, category);
			this.categories.push(category);
		} else if (existing.title !== title || existing.weight !== weight || (existing.kind ?? "scored") !== kind) errors.push({
			row,
			column: "categoryTitle",
			message: "Category title/weight/kind disagree with an earlier row"
		});
	}
};
/**
* Whether a CSV file is meant to be in the HLT exchange layout: at least half of its column names are present.
* Such files are read locally with row-specific problems; other tables are left to the AI document import.
*/
function isHltCsvLayout(csv, delimiter) {
	const parsed = parseCsv(csv, delimiter);
	if (parsed.error) return false;
	const header = parsed.rows.find((r) => !isBlankRow(r))?.map((h) => h.trim()) ?? [];
	return csvHeader.filter((column) => header.includes(column)).length * 2 >= csvHeader.length;
}
/**
* Parse the documented CSV exchange format into a publishable draft. Formulas are never evaluated.
* The draft has no language fields: the server records the content language when it is saved.
*/
function importTemplateCsv(input) {
	const parsed = parseCsv(input.csv, input.delimiter);
	if (parsed.error) return {
		definition: null,
		errors: [{
			row: 0,
			column: "file",
			message: parsed.error
		}]
	};
	const headerIndex = parsed.rows.findIndex((r) => !isBlankRow(r));
	const header = parsed.rows[headerIndex]?.map((h) => h.trim());
	if (!header) return {
		definition: null,
		errors: [{
			row: 0,
			column: "file",
			message: "The file is empty"
		}]
	};
	const errors = headerErrors(header, headerIndex + 1);
	if (errors.length) return {
		definition: null,
		errors
	};
	const builder = new DefinitionBuilder();
	let count = 0;
	parsed.rows.slice(headerIndex + 1).forEach((cells, offset) => {
		const row = headerIndex + 2 + offset;
		if (isBlankRow(cells)) return;
		count += 1;
		if (count > maxRows) {
			if (count === 201) errors.push({
				row,
				column: "row",
				message: `At most ${maxRows} questions`
			});
			return;
		}
		if (cells.length !== header.length) {
			errors.push({
				row,
				column: "row",
				message: `Expected ${header.length} columns, found ${cells.length}`
			});
			return;
		}
		builder.addRow((c) => header.includes(c) ? cells[header.indexOf(c)] ?? "" : "", row, errors);
	});
	if (count === 0) errors.push({
		row: headerIndex + 1,
		column: "file",
		message: "No question rows"
	});
	if (errors.length) return {
		definition: null,
		errors: errors.slice(0, 500)
	};
	const result = validateForPublish({
		title: input.title,
		description: input.description,
		sourceLocale: "en",
		aggregation: "weightedCategoryMean",
		displayMaximum: input.displayMaximum,
		categories: builder.categories
	});
	if (!result.definition) return {
		definition: null,
		errors: result.errors.map((e) => ({
			row: 0,
			column: e.path.slice(0, 80),
			message: e.message
		}))
	};
	const { sourceLocale: _sourceLocale, fieldLocales: _fieldLocales, ...draft } = result.definition;
	return {
		definition: draft,
		errors: []
	};
}
// src/finding/rules.ts
/**
* Linking requires identical effective audiences so a link never broadens a private origin. Workspace
* audiences are equal; private audiences need the same owner.
*/
function sameEffectiveAudience(a, b) {
	if (a.audience !== b.audience) return false;
	return a.audience === "workspace" || a.ownerId === b.ownerId;
}
/** An automatically created item may be consolidated only while nobody has touched it since creation. */
function isUntouchedAutomaticItem(item) {
	return item.replacedBy === null && item.revision === 1 && item.state === "open" && item.collaboratorCount === 0 && item.eventKinds.every((kind) => kind === "created");
}
/** Collaborators are a set of other people; the owner is never also a collaborator. */
function normalizeCollaborators(ownerId, collaboratorIds) {
	return [...new Set(collaboratorIds)].filter((id) => id !== ownerId).sort();
}
// src/hash/sha256.ts
/** Synchronous SHA-256 over UTF-8 text with hex output, so domain code stays runtime-neutral. */
const K = new Uint32Array([
	1116352408,
	1899447441,
	3049323471,
	3921009573,
	961987163,
	1508970993,
	2453635748,
	2870763221,
	3624381080,
	310598401,
	607225278,
	1426881987,
	1925078388,
	2162078206,
	2614888103,
	3248222580,
	3835390401,
	4022224774,
	264347078,
	604807628,
	770255983,
	1249150122,
	1555081692,
	1996064986,
	2554220882,
	2821834349,
	2952996808,
	3210313671,
	3336571891,
	3584528711,
	113926993,
	338241895,
	666307205,
	773529912,
	1294757372,
	1396182291,
	1695183700,
	1986661051,
	2177026350,
	2456956037,
	2730485921,
	2820302411,
	3259730800,
	3345764771,
	3516065817,
	3600352804,
	4094571909,
	275423344,
	430227734,
	506948616,
	659060556,
	883997877,
	958139571,
	1322822218,
	1537002063,
	1747873779,
	1955562222,
	2024104815,
	2227730452,
	2361852424,
	2428436474,
	2756734187,
	3204031479,
	3329325298
]);
const rotr = (x, n) => x >>> n | x << 32 - n;
function pad$1(bytes) {
	const padded = new Uint8Array(bytes.length + 9 + 63 >> 6 << 6);
	padded.set(bytes);
	padded[bytes.length] = 128;
	const view = new DataView(padded.buffer);
	const bitLen = bytes.length * 8;
	view.setUint32(padded.length - 4, bitLen >>> 0);
	view.setUint32(padded.length - 8, Math.floor(bitLen / 2 ** 32));
	return padded;
}
function sha256Hex(input) {
	const padded = pad$1(new TextEncoder().encode(input));
	const view = new DataView(padded.buffer);
	const h = [
		1779033703,
		3144134277,
		1013904242,
		2773480762,
		1359893119,
		2600822924,
		528734635,
		1541459225
	];
	const w = Array.from({ length: 64 }, () => 0);
	for (let off = 0; off < padded.length; off += 64) {
		for (let i = 0; i < 16; i++) w[i] = view.getUint32(off + i * 4);
		for (let i = 16; i < 64; i++) {
			const a = w[i - 15] ?? 0;
			const b = w[i - 2] ?? 0;
			const s0 = rotr(a, 7) ^ rotr(a, 18) ^ a >>> 3;
			const s1 = rotr(b, 17) ^ rotr(b, 19) ^ b >>> 10;
			w[i] = (w[i - 16] ?? 0) + s0 + (w[i - 7] ?? 0) + s1 >>> 0;
		}
		let [a, b, c, d, e, f, g, hh] = h;
		for (let i = 0; i < 64; i++) {
			const s1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25);
			const ch = e & f ^ ~e & g;
			const t1 = hh + s1 + ch + (K[i] ?? 0) + (w[i] ?? 0) >>> 0;
			const t2 = (rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22)) + (a & b ^ a & c ^ b & c) >>> 0;
			hh = g;
			g = f;
			f = e;
			e = d + t1 >>> 0;
			d = c;
			c = b;
			b = a;
			a = t1 + t2 >>> 0;
		}
		const next = [
			a,
			b,
			c,
			d,
			e,
			f,
			g,
			hh
		];
		for (let i = 0; i < 8; i++) h[i] = (h[i] ?? 0) + (next[i] ?? 0) >>> 0;
	}
	return h.map((x) => x.toString(16).padStart(8, "0")).join("");
}
// src/hash/canonical-json.ts
/** Deterministic JSON with sorted object keys; undefined properties are omitted. */
function canonicalJson(value) {
	if (value === null || typeof value !== "object") return JSON.stringify(value) ?? "null";
	if (Array.isArray(value)) return `[${value.map((v) => canonicalJson(v)).join(",")}]`;
	return `{${Object.entries(value).filter(([, v]) => v !== void 0).sort(([a], [b]) => a < b ? -1 : a > b ? 1 : 0).map(([k, v]) => `${JSON.stringify(k)}:${canonicalJson(v)}`).join(",")}}`;
}
const sameJson = (a, b) => canonicalJson(a) === canonicalJson(b);
// src/media/rules.ts
const mediaOwnerTypes = [
	"remark",
	"finding",
	"branding",
	"audio",
	"amendment",
	"analyticsPage"
];
/** Stored image formats. Browsers convert other pictures (WebP, HEIC) before upload. */
const imageMimes = ["image/jpeg", "image/png"];
const audioMimes = [
	"audio/webm",
	"audio/mp4",
	"audio/ogg",
	"audio/wav"
];
const preview = {
	longEdge: limits.thumbnailLongEdge,
	maxBytes: limits.thumbnailMaxBytes
};
/** Owner types whose images are audit evidence (comments and findings). */
const evidenceOwnerTypes = [
	"remark",
	"amendment",
	"finding"
];
const isImage = (mime) => imageMimes.includes(mime);
const isAudio = (mime) => audioMimes.includes(mime);
/**
* Byte limit and processing kind for an owner type and declared MIME, or null when that MIME is not
* accepted for the owner (audio only for voice input, rasters only for evidence and assets).
*/
function uploadRuleFor(ownerType, mime) {
	if (ownerType === "audio") return isAudio(mime) ? {
		kind: "audio",
		maxBytes: limits.audioUploadBytes,
		longEdge: 0,
		thumbnail: null
	} : null;
	if (!isImage(mime)) return null;
	if (ownerType === "branding") return {
		kind: "image",
		maxBytes: limits.logoUploadBytes,
		longEdge: limits.logoLongEdge,
		thumbnail: {
			longEdge: Math.round(limits.logoLongEdge / 2),
			maxBytes: limits.thumbnailMaxBytes
		}
	};
	if (evidenceOwnerTypes.includes(ownerType)) return {
		kind: "image",
		maxBytes: limits.evidenceMaxBytes,
		longEdge: limits.evidenceLongEdge,
		thumbnail: preview
	};
	return {
		kind: "image",
		maxBytes: limits.imageUploadBytes,
		longEdge: limits.evidenceLongEdge,
		thumbnail: preview
	};
}
/** Owner types whose items count against the per-remark image limit. */
const remarkOwnerTypes = ["remark", "amendment"];
/** Stored states include the internal `receiving` step, which callers still see as a reservation. */
function publicMediaState(stored) {
	switch (stored) {
		case "uploaded":
		case "processing":
		case "ready":
		case "failed":
		case "deleted": return stored;
		default: return "reserved";
	}
}
const startsWith = (bytes, signature, offset = 0) => signature.every((value, index) => bytes[offset + index] === value);
/** Raster format from magic bytes, so a declared MIME can be checked before costly decoding. */
function sniffImageMime(bytes) {
	if (startsWith(bytes, [
		255,
		216,
		255
	])) return "image/jpeg";
	if (startsWith(bytes, [
		137,
		80,
		78,
		71,
		13,
		10,
		26,
		10
	])) return "image/png";
	return null;
}
/** Private object keys; derived from IDs only, never from client input. */
const mediaObjectKeys = {
	quarantine: (organizationId, mediaId) => `quarantine/${organizationId}/${mediaId}`,
	evidence: (organizationId, mediaId) => `media/${organizationId}/${mediaId}/evidence.jpg`,
	thumbnail: (organizationId, mediaId) => `media/${organizationId}/${mediaId}/thumb.jpg`,
	audio: (organizationId, mediaId) => `audio/${organizationId}/${mediaId}`
};
// src/page/catalog.ts
const dataWidgetTypes = [
	"scoreSummary",
	"categoryComparison",
	"scoreTrend",
	"categoryBars",
	"remarks",
	"gallery",
	"auditList",
	"keyFigure",
	"auditCount",
	"findingsFlow",
	"areaRanking",
	"areaCategoryMatrix",
	"comparisonTable",
	"changedAnswers",
	"topChanges",
	"answerDistribution",
	"deficiencyPareto",
	"repeatDeficiencies",
	"findingsBreakdown",
	"areaStatus"
];
const contentWidgetTypes = ["text", "image"];
const isContentWidget = (type) => contentWidgetTypes.includes(type);
/** Widgets that read two or three explicitly selected audits (`filters.auditRevisionIds`). */
const comparisonWidgetTypes = [
	"categoryComparison",
	"comparisonTable",
	"changedAnswers",
	"topChanges"
];
const allowedVariants = {
	scoreSummary: ["metric"],
	categoryComparison: [
		"groupedBars",
		"radar",
		"table"
	],
	scoreTrend: [
		"line",
		"area",
		"table"
	],
	categoryBars: ["bars", "table"],
	remarks: ["table"],
	auditList: ["table"],
	gallery: ["grid"],
	text: ["text"],
	image: ["image"],
	keyFigure: ["metric"],
	auditCount: ["columns", "table"],
	findingsFlow: ["columns", "table"],
	areaRanking: ["bars", "table"],
	areaCategoryMatrix: ["table"],
	comparisonTable: ["table"],
	changedAnswers: ["table"],
	topChanges: ["table"],
	answerDistribution: ["stacked", "table"],
	deficiencyPareto: ["bars", "table"],
	repeatDeficiencies: ["bars", "table"],
	findingsBreakdown: ["stacked", "table"],
	areaStatus: ["table"]
};
const chartOptionKeys = [
	"variant",
	"showLegend",
	"legendPosition",
	"showValues",
	"showGrid",
	"showAxisLabels",
	"axisLabelAngle",
	"decimals",
	"series",
	"categoryKeys"
];
const countChartKeys = [
	"variant",
	"showLegend",
	"legendPosition",
	"showValues",
	"showGrid",
	"showAxisLabels",
	"axisLabelAngle",
	"series"
];
/** Option keys each widget type accepts; anything else is a field-specific validation error. */
const allowedOptionKeys = {
	scoreSummary: [
		"variant",
		"decimals",
		"sampleCountPosition"
	],
	categoryComparison: chartOptionKeys,
	scoreTrend: chartOptionKeys,
	categoryBars: [...chartOptionKeys, "orientation"],
	remarks: [
		"variant",
		"columns",
		"sortBy",
		"sortDirection",
		"density",
		"wrapText",
		"showThumbnails"
	],
	auditList: [
		"variant",
		"columns",
		"sortBy",
		"sortDirection",
		"density",
		"wrapText"
	],
	gallery: [
		"variant",
		"galleryColumns",
		"aspectRatio",
		"imageFit",
		"showCaptions",
		"maxImages"
	],
	text: ["variant", "textStyle"],
	image: [
		"variant",
		"aspectRatio",
		"imageFit"
	],
	keyFigure: [
		"variant",
		"metric",
		"comparePrevious",
		"olderThanDays",
		"decimals"
	],
	auditCount: [...countChartKeys, "stackBy"],
	findingsFlow: countChartKeys,
	areaRanking: [
		"variant",
		"orientation",
		"sortDirection",
		"decimals",
		"showValues",
		"showGrid",
		"showAxisLabels",
		"series"
	],
	areaCategoryMatrix: [
		"variant",
		"decimals",
		"categoryKeys"
	],
	comparisonTable: [
		"variant",
		"decimals",
		"expandQuestions",
		"categoryKeys"
	],
	changedAnswers: [
		"variant",
		"changeFilter",
		"showThumbnails"
	],
	topChanges: [
		"variant",
		"topCount",
		"decimals"
	],
	answerDistribution: [
		...countChartKeys,
		"groupBy",
		"showPercent"
	],
	deficiencyPareto: [
		"variant",
		"topCount",
		"showValues",
		"showGrid",
		"showAxisLabels",
		"axisLabelAngle"
	],
	repeatDeficiencies: [
		"variant",
		"topCount",
		"showValues",
		"showGrid",
		"showAxisLabels",
		"axisLabelAngle"
	],
	findingsBreakdown: [
		...countChartKeys,
		"groupBy",
		"stackBy"
	],
	areaStatus: ["variant", "decimals"]
};
/** Values an option may take for one type when the shared enum is wider than that type supports. */
const allowedOptionValues = {
	answerDistribution: { groupBy: ["category", "area"] },
	findingsBreakdown: { groupBy: ["age", "area"] }
};
const defaultColumns = {
	remarks: [
		"criterion",
		"remark",
		"state",
		"owner",
		"area",
		"effectiveAt",
		"photos"
	],
	auditList: [
		"area",
		"effectiveAt",
		"template",
		"owner",
		"score",
		"state"
	]
};
/** Content-specific minimum grid size (columns × 32 px rows). */
const minSize = {
	scoreSummary: {
		w: 2,
		h: 3
	},
	categoryComparison: {
		w: 4,
		h: 8
	},
	scoreTrend: {
		w: 4,
		h: 6
	},
	categoryBars: {
		w: 4,
		h: 6
	},
	remarks: {
		w: 6,
		h: 6
	},
	auditList: {
		w: 6,
		h: 6
	},
	gallery: {
		w: 4,
		h: 6
	},
	text: {
		w: 2,
		h: 2
	},
	image: {
		w: 2,
		h: 3
	},
	keyFigure: {
		w: 2,
		h: 3
	},
	auditCount: {
		w: 4,
		h: 6
	},
	findingsFlow: {
		w: 6,
		h: 6
	},
	areaRanking: {
		w: 4,
		h: 6
	},
	areaCategoryMatrix: {
		w: 8,
		h: 6
	},
	comparisonTable: {
		w: 8,
		h: 8
	},
	changedAnswers: {
		w: 6,
		h: 6
	},
	topChanges: {
		w: 3,
		h: 6
	},
	answerDistribution: {
		w: 4,
		h: 6
	},
	deficiencyPareto: {
		w: 6,
		h: 7
	},
	repeatDeficiencies: {
		w: 6,
		h: 7
	},
	findingsBreakdown: {
		w: 4,
		h: 6
	},
	areaStatus: {
		w: 6,
		h: 6
	}
};
/** Size of a newly added widget before any automatic arrangement. */
const defaultSize = {
	scoreSummary: {
		w: 4,
		h: 4
	},
	categoryComparison: {
		w: 6,
		h: 9
	},
	scoreTrend: {
		w: 12,
		h: 8
	},
	categoryBars: {
		w: 6,
		h: 8
	},
	remarks: {
		w: 12,
		h: 9
	},
	auditList: {
		w: 12,
		h: 9
	},
	gallery: {
		w: 12,
		h: 8
	},
	text: {
		w: 6,
		h: 3
	},
	image: {
		w: 6,
		h: 6
	},
	keyFigure: {
		w: 3,
		h: 4
	},
	auditCount: {
		w: 6,
		h: 7
	},
	findingsFlow: {
		w: 12,
		h: 7
	},
	areaRanking: {
		w: 6,
		h: 8
	},
	areaCategoryMatrix: {
		w: 12,
		h: 9
	},
	comparisonTable: {
		w: 12,
		h: 12
	},
	changedAnswers: {
		w: 12,
		h: 10
	},
	topChanges: {
		w: 4,
		h: 8
	},
	answerDistribution: {
		w: 6,
		h: 7
	},
	deficiencyPareto: {
		w: 12,
		h: 9
	},
	repeatDeficiencies: {
		w: 12,
		h: 9
	},
	findingsBreakdown: {
		w: 6,
		h: 7
	},
	areaStatus: {
		w: 12,
		h: 9
	}
};
// src/page/layout.ts
/** Screen reading order: row, then column. */
function readingOrder(cells) {
	return [...cells].sort((a, b) => a.y - b.y || a.x - b.x).map((c) => c.widgetId);
}
function cellsOverlap(a, b) {
	return a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;
}
/**
* First position (top to bottom, left to right) where a `w × h` widget fits without overlapping;
* below everything when the grid has no such gap.
*/
function firstFreeSlot(cells, w, h, columns) {
	const width = Math.min(w, columns);
	const bottom = cells.reduce((max, c) => Math.max(max, c.y + c.h), 0);
	for (let y = 0; y < bottom; y++) for (let x = 0; x + width <= columns; x++) {
		const probe = {
			widgetId: "",
			x,
			y,
			w: width,
			h
		};
		if (!cells.some((c) => cellsOverlap(c, probe))) return {
			x,
			y
		};
	}
	return {
		x: 0,
		y: bottom
	};
}
/** Maximal horizontal strips such that no cell crosses a strip boundary (export pagination, reflow). */
function cutBands(cells) {
	const sorted = [...cells].sort((a, b) => a.y - b.y || a.x - b.x);
	const bands = [];
	let current = [];
	let top = 0;
	let bottom = 0;
	for (const cell of sorted) {
		if (current.length && cell.y >= bottom) {
			bands.push({
				cells: current,
				top,
				bottom
			});
			current = [];
		}
		if (!current.length) top = cell.y;
		current.push(cell);
		bottom = Math.max(bottom, cell.y + cell.h);
	}
	if (current.length) bands.push({
		cells: current,
		top,
		bottom
	});
	return bands;
}
/**
* Reflow a 12-column layout to fewer columns band by band, keeping the reading order and heights:
* widths scale proportionally but never below each widget's minimum; a band that cannot fit side by
* side is stacked.
*/
function reflowColumns(cells, columns, minWidth) {
	const out = [];
	let y = 0;
	for (const band of cutBands(cells)) {
		const row = [...band.cells].sort((a, b) => a.x - b.x);
		const mins = row.map((c) => Math.min(minWidth(c.widgetId), columns));
		const widths = row.map((c, i) => row.length === 1 ? columns : Math.max(mins[i] ?? 1, Math.round(c.w * columns / 12)));
		let total = widths.reduce((a, b) => a + b, 0);
		while (total > columns) {
			let pick = -1;
			widths.forEach((w, i) => {
				if (w > (mins[i] ?? 1) && (pick < 0 || w >= (widths[pick] ?? 0))) pick = i;
			});
			if (pick < 0) break;
			widths[pick] = (widths[pick] ?? 1) - 1;
			total -= 1;
		}
		if (total > columns) {
			for (const c of row) {
				out.push({
					...c,
					x: 0,
					y,
					w: columns
				});
				y += c.h;
			}
			continue;
		}
		let x = 0;
		const height = band.bottom - band.top;
		row.forEach((c, i) => {
			out.push({
				widgetId: c.widgetId,
				x,
				y: y + (c.y - band.top),
				w: widths[i] ?? columns,
				h: c.h
			});
			x += widths[i] ?? columns;
		});
		y += height;
	}
	return out;
}
// src/page/roles.ts
const roleOfType = {
	keyFigure: "kpi",
	scoreSummary: "kpi",
	categoryComparison: "chart",
	categoryBars: "chart",
	auditCount: "chart",
	areaRanking: "chart",
	answerDistribution: "chart",
	findingsBreakdown: "chart",
	topChanges: "compact",
	scoreTrend: "wideChart",
	findingsFlow: "wideChart",
	deficiencyPareto: "wideChart",
	repeatDeficiencies: "wideChart",
	areaCategoryMatrix: "wideChart",
	remarks: "table",
	auditList: "table",
	comparisonTable: "table",
	changedAnswers: "table",
	areaStatus: "table",
	gallery: "media",
	image: "media",
	text: "text"
};
const roleOf = (widget) => roleOfType[widget.type];
/** Sizing hints for category charts and lists; seven categories is the documented worked example. */
const defaultLayoutHints = {
	categories: 7,
	areas: 10,
	rows: 8
};
const ranges = {
	kpi: {
		min: {
			w: 2,
			h: 3
		},
		target: [3, 2],
		max: {
			w: 12,
			h: 4
		},
		grow: 1
	},
	chart: {
		min: {
			w: 4,
			h: 6
		},
		target: [6, 4],
		max: {
			w: 12,
			h: 12
		},
		grow: 2
	},
	compact: {
		min: {
			w: 3,
			h: 6
		},
		target: [4, 4],
		max: {
			w: 6,
			h: 12
		},
		grow: 1
	},
	wideChart: {
		min: {
			w: 6,
			h: 6
		},
		target: [12, 8],
		max: {
			w: 12,
			h: 14
		},
		grow: 3
	},
	table: {
		min: {
			w: 6,
			h: 6
		},
		target: [12, 8],
		max: {
			w: 12,
			h: 20
		},
		grow: 1
	},
	media: {
		min: {
			w: 4,
			h: 6
		},
		target: [12, 8],
		max: {
			w: 12,
			h: 16
		},
		grow: 1
	},
	text: {
		min: {
			w: 2,
			h: 2
		},
		target: [6, 4],
		max: {
			w: 12,
			h: 8
		},
		grow: 1
	}
};
const clamp$1 = (value, low, high) => Math.min(Math.max(value, low), high);
const bars = (count) => 3 + Math.ceil(count / 2);
const tableRows = (rows) => 3 + Math.ceil(.75 * rows);
function categoriesOf(widget, hints) {
	return (widget.options.categoryKeys?.length ?? 0) || hints.categories;
}
function areasOf(widget, hints) {
	const configured = widget.filters.areaIds?.length ?? 0;
	return Math.min(configured || hints.areas, 20);
}
/** Plain text of a text widget, or an empty string. */
const textOf = (widget) => widget.content?.kind === "text" ? widget.content.text : "";
/** A heading by option, or legacy text without the option that is one line of at most 80 characters. */
function isHeading(widget) {
	if (widget.type !== "text") return false;
	if (widget.options.textStyle) return widget.options.textStyle === "heading";
	const text = textOf(widget).trim();
	return !text.includes("\n") && text.length <= 80;
}
const referenceWidth = 1216;
const gutter = 16;
const charWidth = 8.8;
const lineHeight = 24;
const noteChrome = 56;
const rowPitch = 48;
/** Rows a note needs at `w` of `columns` columns: wrapped lines plus title and padding. */
function noteHeight(text, w, columns) {
	const widthPx = w * ((referenceWidth - (columns - 1) * gutter) / columns) + (w - 1) * gutter;
	const lines = Math.max(1, text.split("\n").reduce((sum, paragraph) => sum + Math.ceil(paragraph.length * charWidth / widthPx), 0));
	return clamp$1(Math.ceil((lines * lineHeight + noteChrome) / rowPitch), 2, 8);
}
function targetHeight(widget, role, w, columns, hints) {
	switch (widget.type) {
		case "categoryBars": return bars(categoriesOf(widget, hints));
		case "areaRanking": return bars(areasOf(widget, hints));
		case "answerDistribution": return widget.options.groupBy === "area" ? bars(areasOf(widget, hints)) : bars(categoriesOf(widget, hints));
		case "findingsBreakdown": return widget.options.groupBy === "area" ? bars(areasOf(widget, hints)) : 7;
		case "areaCategoryMatrix": return tableRows(areasOf(widget, hints));
		case "areaStatus": return tableRows(areasOf(widget, hints));
		case "remarks":
		case "auditList": return tableRows(hints.rows);
		case "comparisonTable": return 12;
		case "changedAnswers": return 10;
		case "categoryComparison":
		case "deficiencyPareto":
		case "repeatDeficiencies": return 9;
		case "auditCount":
		case "findingsFlow": return 7;
		case "image": return 6;
		case "text": return isHeading(widget) ? 2 : noteHeight(textOf(widget), w, columns);
		default: return role === "kpi" ? 4 : 8;
	}
}
/** Readable size range of one widget on a grid with `columns` columns (12 desktop, 8 tablet). */
function roleSpec(widget, columns, hints) {
	const role = roleOf(widget);
	const range = ranges[role];
	const typeMin = minSize[widget.type];
	const heading = isHeading(widget);
	const fullWidth = columns === 12 ? 0 : 1;
	let targetW = range.target[fullWidth] ?? columns;
	if (widget.type === "image") targetW = columns / 2;
	if (heading) targetW = columns;
	const maxW = Math.min(widget.type === "categoryComparison" && widget.options.variant === "radar" ? 8 : range.max.w, columns);
	const roleMin = widget.type === "image" ? typeMin : range.min;
	const minW = Math.min(Math.max(roleMin.w, typeMin.w), columns);
	const minH = Math.max(roleMin.h, typeMin.h);
	const w = clamp$1(targetW, minW, maxW);
	const maxH = Math.max(range.max.h, minH);
	return {
		role,
		minW,
		minH,
		targetW: w,
		targetH: clamp$1(targetHeight(widget, role, w, columns, hints), minH, maxH),
		maxW,
		maxH,
		grow: range.grow,
		heading
	};
}
/** Phone heights by role; lists and text size to their content on phones, this is their minimum. */
function phoneHeight(spec, hints) {
	switch (spec.role) {
		case "kpi":
		case "text": return 160;
		case "table": return Math.min(640, 120 + 40 * hints.rows);
		default: return 320;
	}
}
// src/page/arrange.ts
/** Freeform pages use a grid four times finer than Grid pages (48 columns, 8 px rows, 4 px gaps). */
const freeformScale = 4;
const freeformColumns = 48;
const sum = (values) => values.reduce((total, v) => total + v, 0);
/** Start at the clamped targets; shrink the item with most slack, then grow the one with lowest w/grow. */
function distribute(row, columns) {
	const slots = row.map((it) => ({
		spec: it.spec,
		w: clampedTarget(it, columns)
	}));
	const total = () => sum(slots.map((s) => s.w));
	while (total() > columns) {
		let pick;
		for (const slot of slots) if (slot.w > slot.spec.minW && (!pick || slot.w - slot.spec.minW >= pick.w - pick.spec.minW)) pick = slot;
		if (!pick) break;
		pick.w -= 1;
	}
	while (total() < columns) {
		let pick;
		for (const slot of slots) {
			if (slot.w >= Math.min(slot.spec.maxW, columns) || slot.spec.grow <= 0) continue;
			if (!pick || slot.w / slot.spec.grow < pick.w / pick.spec.grow) pick = slot;
		}
		if (!pick) break;
		pick.w += 1;
	}
	return slots.map((s) => s.w);
}
/** `n` items in `ceil(n / perRow)` rows whose sizes differ by at most one, larger rows first. */
function balancedRows(items, perRow) {
	if (!items.length) return [];
	const count = Math.ceil(items.length / Math.max(1, perRow));
	const base = Math.floor(items.length / count);
	const extra = items.length % count;
	const rows = [];
	let index = 0;
	for (let r = 0; r < count; r++) {
		const size = base + (r < extra ? 1 : 0);
		rows.push(items.slice(index, index + size));
		index += size;
	}
	return rows;
}
const clampedTarget = (it, columns) => Math.min(Math.max(it.spec.targetW, it.spec.minW), it.spec.maxW, columns);
/** Next-fit by target width: a new row starts when the next item's target width no longer fits. */
function nextFitRows(items, columns) {
	const rows = [];
	let row = [];
	let used = 0;
	for (const it of items) {
		const w = clampedTarget(it, columns);
		if (row.length && used + w > columns) {
			rows.push(row);
			row = [];
			used = 0;
		}
		row.push(it);
		used += w;
	}
	if (row.length) rows.push(row);
	return rows;
}
/** Fill a chart row's leftover width with a note that directly precedes or follows a row member. */
function withAdjacentNote(row, notes, used, columns) {
	const leftover = columns - sum(row.map((it) => clampedTarget(it, columns)));
	if (leftover < 1) return row;
	const ranks = new Set(row.flatMap((it) => [it.rank - 1, it.rank + 1]));
	const note = notes.find((n) => !used.has(n) && ranks.has(n.rank) && n.spec.minW <= leftover);
	if (!note) return row;
	used.add(note);
	return [...row, {
		...note,
		filler: true
	}];
}
function rowHeight(row) {
	const solid = row.filter((it) => it.spec.role !== "text");
	const source = solid.length ? solid : row;
	return Math.max(...source.map((it) => it.spec.targetH));
}
function arrangeOnce(page, columns, hints, order) {
	const rankOf = new Map(order.map((id, i) => [id, i]));
	const items = page.widgets.map((widget, index) => ({
		widget,
		spec: roleSpec(widget, columns, hints),
		rank: rankOf.get(widget.id) ?? order.length + index
	})).sort((a, b) => a.rank - b.rank || a.widget.id.localeCompare(b.widget.id)).map((it, rank) => ({
		...it,
		rank
	}));
	const cells = [];
	let y = 0;
	const emit = (row, widths) => {
		const h = rowHeight(row);
		let x = 0;
		row.forEach((it, k) => {
			const w = widths[k] ?? it.spec.minW;
			cells.push({
				widgetId: it.widget.id,
				x,
				y,
				w,
				h: it.filler ? h : Math.min(h, it.spec.maxH)
			});
			x += w;
		});
		y += h;
	};
	let section = {
		heading: null,
		items: []
	};
	const sections = [section];
	for (const it of items) if (it.spec.heading) {
		section = {
			heading: it,
			items: []
		};
		sections.push(section);
	} else section.items.push(it);
	for (const { heading, items: members } of sections) {
		if (heading) emit([heading], [columns]);
		const of = (...roles) => members.filter((it) => roles.includes(it.spec.role));
		const kpi = of("kpi");
		const wide = of("wideChart");
		const chart = of("chart", "compact");
		const notes = of("text");
		if (!wide.length && chart.length % 2 === 1) {
			const lead = chart.find((it) => it.spec.maxW >= columns);
			if (lead) {
				chart.splice(chart.indexOf(lead), 1);
				wide.unshift(lead);
			}
		}
		const perRow = Math.max(1, Math.floor(columns / (kpi[0]?.spec.targetW ?? 3)));
		for (const row of balancedRows(kpi, perRow)) emit(row, distribute(row, columns));
		for (const it of wide) emit([it], distribute([it], columns));
		const used = /* @__PURE__ */ new Set();
		for (const row of nextFitRows(chart, columns)) {
			const filled = withAdjacentNote(row, notes, used, columns);
			emit(filled, distribute(filled, columns));
		}
		for (const it of [...of("table"), ...of("media")]) emit([it], distribute([it], columns));
		for (const row of nextFitRows(notes.filter((n) => !used.has(n)), columns)) emit(row, distribute(row, columns));
	}
	return cells;
}
/**
* Role-aware automatic arrangement: a stratified row packer that places key figures, primary charts,
* secondary charts in pairs, tables and media, then notes. Widths and heights stay inside each role's
* readable range, so holes stay visible instead of being filled with stretched widgets. The result does
* not depend on query results: `order` is the current reading order (unplaced widgets follow by definition order).
*/
function arrangeGrid(page, columns, hints, order) {
	let current = order;
	let cells = arrangeOnce(page, columns, hints, current);
	for (let pass = 0; pass <= page.widgets.length; pass++) {
		const next = readingOrder(cells);
		if (next.join() === current.join()) break;
		current = next;
		const again = arrangeOnce(page, columns, hints, current);
		if (JSON.stringify(again) === JSON.stringify(cells)) break;
		cells = again;
	}
	return cells;
}
/** Freeform reading order: tops less than one coarse row apart count as one row, ordered by x. */
function freeformReadingOrder(cells) {
	const sorted = [...cells].sort((a, b) => a.y - b.y || a.x - b.x);
	const rows = [];
	for (const cell of sorted) {
		const row = rows.at(-1);
		if (row && cell.y - row.top < 4) row.cells.push(cell);
		else rows.push({
			top: cell.y,
			cells: [cell]
		});
	}
	return rows.flatMap((row) => row.cells.sort((a, b) => a.x - b.x).map((c) => c.widgetId));
}
/** Current reading order of the page's main layout (Freeform cells for Freeform pages). */
function pageReadingOrder(page) {
	return page.layout === "freeform" && page.freeform?.length ? freeformReadingOrder(page.freeform) : readingOrder(page.desktop);
}
const toFreeform = (cells) => cells.map((c) => ({
	widgetId: c.widgetId,
	x: c.x * 4,
	y: c.y * 4,
	w: c.w * 4,
	h: c.h * 4
}));
/**
* Arrange a whole page: desktop (12 columns) and tablet (8 columns) cells, Freeform cells on the fine
* grid for Freeform pages (the Grid result scaled by four, so positions stay on the coarse guides),
* phone and print order equal to the new reading order, and phone heights by role.
*/
function arrangePage(page, hints = defaultLayoutHints) {
	const order = pageReadingOrder(page);
	const desktop = arrangeGrid(page, 12, hints, order);
	const tablet = arrangeGrid(page, 8, hints, order);
	const reading = readingOrder(desktop);
	const widgets = page.widgets.map((widget) => ({
		...widget,
		presentation: {
			...widget.presentation,
			mobileHeight: phoneHeight(roleSpec(widget, 12, hints), hints)
		}
	}));
	return {
		...page,
		widgets,
		desktop,
		tablet,
		mobileOrder: reading,
		printOrder: reading,
		...page.layout === "freeform" ? { freeform: toFreeform(desktop) } : {}
	};
}
const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
/**
* Freeform cells on the 12-column grid: positions and sizes rounded to the coarse guides, and a cell
* that would overlap an earlier one (in reading order) moved down until it fits.
*/
function coarseCells(cells) {
	const placed = [];
	for (const cell of [...cells].sort((a, b) => a.y - b.y || a.x - b.x)) {
		const w = clamp(Math.round(cell.w / 4), 1, 12);
		const h = clamp(Math.round(cell.h / 4), 1, 30);
		const x = clamp(Math.round(cell.x / 4), 0, 12 - w);
		let y = Math.round(cell.y / 4);
		while (placed.some((other) => cellsOverlap(other, {
			widgetId: "",
			x,
			y,
			w,
			h
		}))) y += 1;
		placed.push({
			widgetId: cell.widgetId,
			x,
			y,
			w,
			h
		});
	}
	return placed;
}
/**
* Derive the other layouts from the one people edit; there are no per-device layouts. Grid pages keep their
* desktop cells, Freeform pages derive them from the fine cells; tablets reflow those to eight columns and
* phones and exports follow the reading order, with any unplaced widget at the end.
*/
function syncLayouts(page) {
	const ids = new Set(page.widgets.map((w) => w.id));
	const freeform = page.layout === "freeform" ? (page.freeform ?? []).filter((c) => ids.has(c.widgetId)) : null;
	const desktop = freeform ? coarseCells(freeform) : page.desktop.filter((c) => ids.has(c.widgetId));
	const typeOf = new Map(page.widgets.map((w) => [w.id, w.type]));
	const tablet = reflowColumns(desktop, 8, (id) => minSize[typeOf.get(id) ?? "text"].w);
	const order = freeform ? freeformReadingOrder(freeform) : readingOrder(desktop);
	const all = [...order, ...page.widgets.map((w) => w.id).filter((id) => !order.includes(id))];
	return {
		...page,
		desktop,
		tablet,
		mobileOrder: all,
		printOrder: all,
		...freeform ? { freeform } : {}
	};
}
const suggestedColumns = {
	quarter: 3,
	third: 4,
	half: 6,
	twoThirds: 8,
	full: 12
};
/**
* Append widgets below everything already on the page, in the given order and close to the asked widths:
* rows fill left to right, a row's leftover columns go to the widgets that may grow and every widget of a row
* gets the row's height. Existing cells never move. Tablet cells and the reading orders are derived after.
*/
function appendRows(page, items, hints = defaultLayoutHints) {
	const freeform = page.layout === "freeform";
	let y = (freeform ? coarseCells(page.freeform ?? []) : page.desktop).reduce((bottom, c) => Math.max(bottom, c.y + c.h), 0);
	const cells = [];
	let row = [];
	const flush = () => {
		if (!row.length) return;
		let leftover = 12 - sum(row.map((r) => r.w));
		while (leftover > 0) {
			const open = row.filter((r) => r.spec.grow > 0 && r.w < r.spec.maxW);
			if (!open.length) break;
			for (const r of open) {
				if (leftover <= 0) break;
				r.w += 1;
				leftover -= 1;
			}
		}
		const h = Math.max(...row.map((r) => r.spec.targetH));
		let x = 0;
		for (const r of row) {
			cells.push({
				widgetId: r.widget.id,
				x,
				y,
				w: r.w,
				h: Math.min(Math.max(h, r.spec.minH), 30)
			});
			x += r.w;
		}
		y += h;
		row = [];
	};
	for (const item of items) {
		const spec = roleSpec(item.widget, 12, hints);
		const w = spec.heading ? 12 : clamp(item.w ?? spec.targetW, spec.minW, spec.maxW);
		if (spec.heading || sum(row.map((r) => r.w)) + w > 12) flush();
		row.push({
			widget: item.widget,
			spec,
			w
		});
		if (spec.heading) flush();
	}
	flush();
	const widgets = items.map(({ widget }) => ({
		...widget,
		presentation: {
			...widget.presentation,
			mobileHeight: phoneHeight(roleSpec(widget, 12, hints), hints)
		}
	}));
	return syncLayouts({
		...page,
		widgets: [...page.widgets, ...widgets],
		desktop: [...page.desktop, ...cells],
		...freeform ? { freeform: [...page.freeform ?? [], ...toFreeform(cells)] } : {}
	});
}
// src/page/defaults.ts
/** Resolver defaults frozen into published versions; missing widget values inherit the page. */
const resolverDefaults = {
	font: "plexSans",
	titleFont: "plexSans",
	fontSize: 16,
	titleSize: 24,
	valueSize: 32,
	padding: 16,
	borderWidth: 1,
	radius: 4,
	shadow: "none",
	textAlign: "left",
	backgroundFit: "cover",
	overlayOpacity: 0
};
/** Count charts (audits, findings, answers) share the chart switches but have no score styling. */
const countDefaults = {
	showLegend: true,
	legendPosition: "bottom",
	showValues: false,
	showGrid: true,
	showAxisLabels: true,
	axisLabelAngle: 0,
	series: []
};
const chartDefaults = {
	showLegend: true,
	legendPosition: "bottom",
	showValues: false,
	showGrid: true,
	showAxisLabels: true,
	axisLabelAngle: 0,
	decimals: 1,
	series: [],
	categoryKeys: []
};
function defaultOptions(type) {
	switch (type) {
		case "scoreSummary": return {
			variant: "metric",
			decimals: 1,
			sampleCountPosition: "below"
		};
		case "categoryComparison": return {
			...chartDefaults,
			variant: "groupedBars"
		};
		case "scoreTrend": return {
			...chartDefaults,
			variant: "line"
		};
		case "categoryBars": return {
			...chartDefaults,
			variant: "bars",
			orientation: "horizontal"
		};
		case "remarks": return {
			variant: "table",
			columns: [...defaultColumns.remarks],
			sortBy: "effectiveAt",
			sortDirection: "desc",
			density: "normal",
			wrapText: true,
			showThumbnails: true
		};
		case "auditList": return {
			variant: "table",
			columns: [...defaultColumns.auditList],
			sortBy: "effectiveAt",
			sortDirection: "desc",
			density: "normal",
			wrapText: true
		};
		case "gallery": return {
			variant: "grid",
			galleryColumns: 3,
			aspectRatio: "landscape",
			imageFit: "contain",
			showCaptions: true,
			maxImages: 12
		};
		case "text": return { variant: "text" };
		case "image": return {
			variant: "image",
			aspectRatio: "landscape",
			imageFit: "contain"
		};
		case "keyFigure": return {
			variant: "metric",
			metric: "averageScore",
			comparePrevious: true,
			olderThanDays: 30,
			decimals: 1
		};
		case "auditCount": return {
			...countDefaults,
			variant: "columns",
			stackBy: "none"
		};
		case "findingsFlow": return {
			...countDefaults,
			variant: "columns"
		};
		case "areaRanking": return {
			variant: "bars",
			sortDirection: "asc",
			decimals: 1,
			showValues: true,
			showGrid: true,
			showAxisLabels: true,
			series: []
		};
		case "areaCategoryMatrix": return {
			variant: "table",
			decimals: 1,
			categoryKeys: []
		};
		case "comparisonTable": return {
			variant: "table",
			decimals: 1,
			expandQuestions: true,
			categoryKeys: []
		};
		case "changedAnswers": return {
			variant: "table",
			changeFilter: "both",
			showThumbnails: true
		};
		case "topChanges": return {
			variant: "table",
			topCount: 3,
			decimals: 1
		};
		case "answerDistribution": return {
			...countDefaults,
			variant: "stacked",
			groupBy: "category",
			showPercent: true
		};
		case "deficiencyPareto":
		case "repeatDeficiencies": return {
			variant: "bars",
			topCount: 10,
			showValues: true,
			showGrid: true,
			showAxisLabels: true,
			axisLabelAngle: 0
		};
		case "findingsBreakdown": return {
			...countDefaults,
			variant: "stacked",
			groupBy: "age",
			stackBy: "none"
		};
		case "areaStatus": return {
			variant: "table",
			decimals: 1
		};
	}
}
const fullWidthTypes = [
	"remarks",
	"auditList",
	"gallery",
	"text"
];
function createWidget(type, title, id = globalThis.crypto.randomUUID()) {
	return {
		id,
		type,
		title,
		filters: {},
		aggregation: type === "categoryComparison" ? "latest" : "mean",
		bucket: "month",
		printSize: fullWidthTypes.includes(type) ? "full" : "half",
		presentation: {
			style: {},
			showTitle: true,
			subtitle: "",
			showSubtitle: false,
			mobileHeight: 320,
			printIncluded: true,
			printBreakBefore: false
		},
		options: defaultOptions(type),
		content: isContentWidget(type) && type === "text" ? {
			kind: "text",
			text: "",
			sourceLocale: "en"
		} : null
	};
}
/** A page with the given widgets, arranged by role (Grid by default; Freeform adds the fine-grid cells). */
function basePage(name, widgets, sourceLocale = "en", layout = "grid") {
	return arrangePage({
		name,
		sourceLocale,
		layout,
		filters: {},
		appearance: {
			style: {},
			contentWidth: null,
			gutter: 16,
			rowHeight: 32,
			showTitle: true,
			subtitle: "",
			titleAlign: "left"
		},
		print: {
			paper: "A4",
			orientation: "landscape",
			marginMm: 12,
			includeBackground: false,
			showCompanyLogo: true
		},
		widgets,
		desktop: [],
		tablet: [],
		mobileOrder: [],
		printOrder: [],
		...layout === "freeform" ? { freeform: [] } : {}
	}, defaultLayoutHints);
}
// src/page/materialize.ts
/**
* Freeze renderer defaults into a definition at publication: type-specific widget options and
* page-level resolver style values. Explicit settings always win, so later default changes never
* alter a published version. Colors stay unset and keep following the light/dark theme tokens.
*/
function materializePage(definition) {
	const page = structuredClone(definition);
	page.appearance.style = {
		...resolverDefaults,
		...page.appearance.style
	};
	page.widgets = page.widgets.map((widget) => ({
		...widget,
		options: {
			...defaultOptions(widget.type),
			...stripUndefined(widget.options)
		}
	}));
	return page;
}
function stripUndefined(value) {
	return Object.fromEntries(Object.entries(value).filter(([, v]) => v !== void 0));
}
// src/page/period.ts
const periods = [
	"lastMonth",
	"last3Months",
	"last12Months",
	"allTime"
];
const monthsOf = {
	lastMonth: 1,
	last3Months: 3,
	last12Months: 12
};
/**
* A relative period ending now: from the start of the same calendar day `n` months ago in the
* workspace timezone (clamped to the month's last day) to the query time.
*/
function periodRange(period, now, timeZone) {
	const today = zonedParts(now, timeZone);
	const months = today.year * 12 + (today.month - 1) - monthsOf[period];
	const year = Math.floor(months / 12);
	const month = months % 12 + 1;
	const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
	return {
		from: zonedTimeToUtc({
			year,
			month,
			day: Math.min(today.day, lastDay),
			hour: 0,
			minute: 0
		}, timeZone),
		to: now
	};
}
/** The equally long range directly before `range` (for "compared with the previous period"). */
function previousRange(range) {
	const length = range.to.getTime() - range.from.getTime();
	return {
		from: new Date(range.from.getTime() - length),
		to: /* @__PURE__ */ new Date(range.from.getTime() - 1)
	};
}
/**
* Replace a relative period with absolute bounds at query time. Explicit `from`/`to` (a viewer's own
* date selection) win over the period; the period key itself is dropped. `allTime` means no bounds.
*/
function resolvePeriod(filters, now, timeZone) {
	const { period, ...rest } = filters;
	if (!period || period === "allTime" || rest.from || rest.to) return rest;
	const range = periodRange(period, now, timeZone);
	return {
		...rest,
		from: range.from.toISOString(),
		to: range.to.toISOString()
	};
}
// src/page/references.ts
/** Media IDs referenced by a page definition (page/widget backgrounds and image widgets). */
function pageMediaIds(def) {
	const ids = /* @__PURE__ */ new Set();
	if (def.appearance.style.backgroundMediaId) ids.add(def.appearance.style.backgroundMediaId);
	for (const widget of def.widgets) {
		if (widget.presentation.style.backgroundMediaId) ids.add(widget.presentation.style.backgroundMediaId);
		if (widget.content?.kind === "image") ids.add(widget.content.mediaId);
	}
	return [...ids];
}
// src/page/widget-catalog.ts
/** The overview's order: data forms from the simplest, then the content blocks. */
const chartForms = [
	"number",
	"columns",
	"bars",
	"line",
	"area",
	"stacked",
	"grouped",
	"radar",
	"heatmap",
	"pareto",
	"table",
	"photos",
	"heading",
	"text",
	"image"
];
const contentForms = [
	"heading",
	"text",
	"image"
];
const table = {
	form: "table",
	options: { variant: "table" }
};
const number = (metric, extra = {}) => ({
	type: "keyFigure",
	forms: [{ form: "number" }],
	options: {
		metric,
		...extra
	}
});
const widgetCatalog = [
	{
		id: "averageScore",
		...number("averageScore")
	},
	{
		id: "auditsCompleted",
		...number("auditsCompleted", { decimals: 0 })
	},
	{
		id: "openFindings",
		...number("openFindings", {
			comparePrevious: false,
			decimals: 0
		})
	},
	{
		id: "openOlderThan",
		...number("openOlderThan", {
			comparePrevious: false,
			olderThanDays: 30,
			decimals: 0
		})
	},
	{
		id: "findingsOpened",
		...number("findingsOpened", { decimals: 0 })
	},
	{
		id: "findingsFixed",
		...number("findingsFixed", { decimals: 0 })
	},
	{
		id: "weakestCategory",
		...number("weakestCategory", { comparePrevious: false })
	},
	{
		id: "scoreTrend",
		type: "scoreTrend",
		forms: [
			{
				form: "line",
				options: { variant: "line" }
			},
			{
				form: "area",
				options: { variant: "area" }
			},
			table
		]
	},
	{
		id: "auditsPerPeriod",
		type: "auditCount",
		forms: [
			{
				form: "columns",
				options: {
					variant: "columns",
					stackBy: "none"
				}
			},
			{
				form: "stacked",
				options: {
					variant: "columns",
					stackBy: "area"
				}
			},
			table
		]
	},
	{
		id: "findingsFlow",
		type: "findingsFlow",
		forms: [{
			form: "columns",
			options: { variant: "columns" }
		}, table]
	},
	{
		id: "categoryScores",
		type: "categoryBars",
		forms: [
			{
				form: "bars",
				options: {
					variant: "bars",
					orientation: "horizontal"
				}
			},
			{
				form: "columns",
				options: {
					variant: "bars",
					orientation: "vertical"
				}
			},
			table
		]
	},
	{
		id: "areaRanking",
		type: "areaRanking",
		forms: [
			{
				form: "bars",
				options: {
					variant: "bars",
					orientation: "horizontal"
				}
			},
			{
				form: "columns",
				options: {
					variant: "bars",
					orientation: "vertical"
				}
			},
			table
		]
	},
	{
		id: "areaCategoryMatrix",
		type: "areaCategoryMatrix",
		forms: [{ form: "heatmap" }]
	},
	{
		id: "auditComparison",
		type: "categoryComparison",
		forms: [
			{
				form: "grouped",
				options: { variant: "groupedBars" }
			},
			{
				form: "radar",
				options: { variant: "radar" }
			},
			table
		],
		needsAudits: true
	},
	{
		id: "comparisonTable",
		type: "comparisonTable",
		forms: [table],
		needsAudits: true
	},
	{
		id: "changedAnswers",
		type: "changedAnswers",
		forms: [table],
		needsAudits: true
	},
	{
		id: "biggestChanges",
		type: "topChanges",
		forms: [table],
		needsAudits: true
	},
	{
		id: "answerDistribution",
		type: "answerDistribution",
		forms: [{
			form: "stacked",
			options: { variant: "stacked" }
		}, table]
	},
	{
		id: "deficiencyPareto",
		type: "deficiencyPareto",
		forms: [{
			form: "pareto",
			options: { variant: "bars" }
		}, table]
	},
	{
		id: "repeatDeficiencies",
		type: "repeatDeficiencies",
		forms: [{
			form: "bars",
			options: { variant: "bars" }
		}, table]
	},
	{
		id: "findingsByAge",
		type: "findingsBreakdown",
		forms: [{
			form: "stacked",
			options: { variant: "stacked" }
		}, table],
		options: { groupBy: "age" }
	},
	{
		id: "findingsByArea",
		type: "findingsBreakdown",
		forms: [{
			form: "stacked",
			options: { variant: "stacked" }
		}, table],
		options: { groupBy: "area" }
	},
	{
		id: "latestAudits",
		type: "auditList",
		forms: [table]
	},
	{
		id: "findingsList",
		type: "remarks",
		forms: [table],
		filters: { findingState: "open" }
	},
	{
		id: "areaStatus",
		type: "areaStatus",
		forms: [table]
	},
	{
		id: "photoGallery",
		type: "gallery",
		forms: [{ form: "photos" }]
	},
	{
		id: "heading",
		type: "text",
		forms: [{ form: "heading" }],
		options: { textStyle: "heading" }
	},
	{
		id: "note",
		type: "text",
		forms: [{ form: "text" }],
		options: { textStyle: "note" }
	},
	{
		id: "image",
		type: "image",
		forms: [{ form: "image" }]
	}
];
const catalogEntry = (id) => widgetCatalog.find((e) => e.id === id);
/** Data entries (everything but the content blocks), in catalog order. */
const dataEntries = widgetCatalog.filter((e) => e.type !== "text" && e.type !== "image");
/** The entry a new widget of each chart form starts with; its settings then offer the others. */
const defaultEntryOfForm = {
	number: "averageScore",
	columns: "auditsPerPeriod",
	bars: "categoryScores",
	line: "scoreTrend",
	area: "scoreTrend",
	stacked: "answerDistribution",
	grouped: "auditComparison",
	radar: "auditComparison",
	heatmap: "areaCategoryMatrix",
	pareto: "deficiencyPareto",
	table: "latestAudits",
	photos: "photoGallery",
	heading: "heading",
	text: "note",
	image: "image"
};
/** Entries a chart form can draw (for "Show" in the settings, the current form's entries come first). */
const entriesOfForm = (form) => widgetCatalog.filter((e) => e.forms.some((f) => f.form === form));
/** A new widget for a catalog entry: the type defaults plus the entry's preset options and filters. */
function catalogWidget(entry, title, sourceLocale, id = globalThis.crypto.randomUUID(), form = entry.forms[0]?.form ?? "table") {
	const widget = createWidget(entry.type, title, id);
	const drawn = entry.forms.find((f) => f.form === form) ?? entry.forms[0];
	widget.options = {
		...widget.options,
		...entry.options,
		...drawn?.options
	};
	widget.filters = { ...entry.filters };
	if (widget.content?.kind === "text") widget.content = {
		...widget.content,
		sourceLocale
	};
	return widget;
}
/** The catalog entry a widget was most likely made from (older pages may predate the catalog). */
function entryOfWidget(widget) {
	const candidates = widgetCatalog.filter((e) => e.type === widget.type);
	const matches = (entry) => Object.entries(entry.options ?? {}).every(([key, value]) => {
		if (key === "decimals" || key === "comparePrevious" || key === "olderThanDays") return true;
		return widget.options[key] === value;
	});
	return candidates.find(matches) ?? candidates[0];
}
const sameOptions = (options, widget) => Object.entries(options).every(([key, value]) => {
	const current = widget.options[key];
	if (current === void 0) return value === "none" || value === "horizontal";
	return current === value;
});
/** The chart form a widget is drawn in. */
function formOfWidget(widget) {
	const entry = entryOfWidget(widget);
	return (entry?.forms.find((f) => sameOptions(f.options ?? {}, widget)) ?? entry?.forms[0])?.form ?? "table";
}
/** The same data drawn in another form the entry offers; other settings stay. */
function withForm(widget, form) {
	const drawn = entryOfWidget(widget)?.forms.find((f) => f.form === form);
	if (!drawn) return widget;
	return {
		...widget,
		options: {
			...widget.options,
			...drawn.options
		}
	};
}
/**
* Show another entry in the same widget: identity, title, period and areas stay; the form stays when the
* new entry offers it. A comparison keeps its chosen audits; other entries never carry them.
*/
function withEntry(widget, entry, title, sourceLocale) {
	const form = formOfWidget(widget);
	const keepForm = entry.forms.some((f) => f.form === form);
	const next = catalogWidget(entry, title, sourceLocale, widget.id, keepForm ? form : void 0);
	const { period, areaIds, templateVersionId, auditRevisionIds } = widget.filters;
	return {
		...next,
		presentation: widget.presentation,
		filters: {
			...next.filters,
			...period ? { period } : {},
			...areaIds?.length ? { areaIds } : {},
			...templateVersionId ? { templateVersionId } : {},
			...entry.needsAudits && auditRevisionIds ? { auditRevisionIds } : {}
		}
	};
}
/** The chart forms a data widget can be switched to in its settings (content blocks keep their own). */
const dataForms = chartForms.filter((form) => !contentForms.includes(form));
/** What a widget drawn in `form` can show: the "Shows" choices once the chart type is chosen. */
const showsInForm = (form) => dataEntries.filter((entry) => entry.forms.some((f) => f.form === form));
/**
* Draw a widget as another chart type: the same data when its entry offers that form, otherwise the form's
* default data (`titleOf` names the new entry; callers keep a customized title). Period and area stay.
*/
function withChartForm(widget, form, titleOf, sourceLocale) {
	if (entryOfWidget(widget)?.forms.some((f) => f.form === form)) return withForm(widget, form);
	const next = catalogEntry(defaultEntryOfForm[form]);
	if (!next) return widget;
	return withForm(withEntry(widget, next, titleOf(next), sourceLocale), form);
}
// src/page/reports.ts
const reportKeys = [
	"overview",
	"auditComparison",
	"areas",
	"findingsFollowUp"
];
const isReportKey = (value) => reportKeys.includes(value);
const reportIndex = {
	overview: 1,
	auditComparison: 2,
	areas: 3,
	findingsFollowUp: 4
};
/** Stable widget IDs, so URLs, cursors and query caches survive releases. */
const widgetId = (key, slot) => `00000000-0000-4000-8000-${(reportIndex[key] * 256 + slot).toString(16).padStart(12, "0")}`;
const kpis = (y, entries) => entries.map((entry, i) => ({
	entry,
	x: i * 3,
	y,
	w: 3,
	h: 4
}));
const layouts = {
	overview: {
		period: "last12Months",
		slots: [
			...kpis(0, [
				"averageScore",
				"openFindings",
				"openOlderThan",
				"auditsCompleted"
			]),
			{
				entry: "areaStatus",
				x: 0,
				y: 4,
				w: 12,
				h: 9
			},
			{
				entry: "categoryScores",
				x: 0,
				y: 13,
				w: 6,
				h: 9
			},
			{
				entry: "repeatDeficiencies",
				x: 6,
				y: 13,
				w: 6,
				h: 9
			},
			{
				entry: "findingsList",
				x: 0,
				y: 22,
				w: 12,
				h: 9
			},
			{
				entry: "photoGallery",
				x: 0,
				y: 31,
				w: 12,
				h: 8
			}
		]
	},
	auditComparison: { slots: [
		{
			entry: "auditComparison",
			x: 0,
			y: 0,
			w: 8,
			h: 9
		},
		{
			entry: "biggestChanges",
			x: 8,
			y: 0,
			w: 4,
			h: 9
		},
		{
			entry: "comparisonTable",
			x: 0,
			y: 9,
			w: 12,
			h: 12
		},
		{
			entry: "changedAnswers",
			x: 0,
			y: 21,
			w: 12,
			h: 10
		}
	] },
	areas: {
		period: "last12Months",
		slots: [
			{
				entry: "scoreTrend",
				x: 0,
				y: 0,
				w: 12,
				h: 8
			},
			{
				entry: "areaCategoryMatrix",
				x: 0,
				y: 8,
				w: 12,
				h: 9
			},
			{
				entry: "areaRanking",
				x: 0,
				y: 17,
				w: 6,
				h: 8
			},
			{
				entry: "auditsPerPeriod",
				form: "stacked",
				x: 6,
				y: 17,
				w: 6,
				h: 8
			}
		]
	},
	findingsFollowUp: {
		period: "last12Months",
		slots: [
			...kpis(0, [
				"openFindings",
				"openOlderThan",
				"findingsOpened",
				"findingsFixed"
			]),
			{
				entry: "findingsList",
				x: 0,
				y: 4,
				w: 12,
				h: 9
			},
			{
				entry: "findingsByAge",
				x: 0,
				y: 13,
				w: 6,
				h: 8
			},
			{
				entry: "findingsByArea",
				x: 6,
				y: 13,
				w: 6,
				h: 8
			},
			{
				entry: "deficiencyPareto",
				x: 0,
				y: 21,
				w: 6,
				h: 9
			},
			{
				entry: "repeatDeficiencies",
				x: 6,
				y: 21,
				w: 6,
				h: 9
			},
			{
				entry: "findingsFlow",
				x: 0,
				y: 30,
				w: 12,
				h: 7
			},
			{
				entry: "photoGallery",
				x: 0,
				y: 37,
				w: 12,
				h: 8
			}
		]
	}
};
/** The report page for `key` with titles from `t`; layout is fixed for desktop and reflowed for tablets. */
function reportPage(key, t = (k) => k, sourceLocale = "en") {
	const layout = layouts[key];
	const widgets = [];
	const desktop = [];
	layout.slots.forEach((slot, index) => {
		const entry = catalogEntry(slot.entry);
		if (!entry) throw new Error(`Unknown catalog entry ${slot.entry}`);
		const widget = catalogWidget(entry, t(`catalog.entries.${entry.id}.name`), sourceLocale, widgetId(key, index + 1), slot.form);
		widgets.push(widget);
		desktop.push({
			widgetId: widget.id,
			x: slot.x,
			y: slot.y,
			w: slot.w,
			h: slot.h
		});
	});
	const page = basePage(t(`reports.${key}.title`), widgets, sourceLocale);
	const typeOf = new Map(widgets.map((w) => [w.id, w.type]));
	const order = readingOrder(desktop);
	return {
		...page,
		filters: layout.period ? { period: layout.period } : {},
		appearance: {
			...page.appearance,
			subtitle: t(`reports.${key}.lead`)
		},
		desktop,
		tablet: reflowColumns(desktop, 8, (id) => minSize[typeOf.get(id) ?? "text"].w),
		mobileOrder: order,
		printOrder: order
	};
}
/** Widget of a report by ID (the server's only way to resolve report widgets). */
function reportWidget(key, id) {
	const page = reportPage(key);
	const widget = page.widgets.find((w) => w.id === id);
	return widget ? {
		page,
		widget
	} : null;
}
// src/page/suggest.ts
const dashboardGoals = [
	"overall_level",
	"compare_areas",
	"trend",
	"findings_follow_up",
	"recurring_problems",
	"audit_changes"
];
const dashboardAudiences = [
	"team",
	"management",
	"just_me"
];
/** At most this many goals can be chosen; a dashboard that answers everything answers nothing. */
const maxDashboardGoals = 2;
/** Catalog entries offered for each goal, most important first. */
const goalEntries = {
	overall_level: [
		"areaStatus",
		"averageScore",
		"categoryScores",
		"weakestCategory"
	],
	compare_areas: [
		"areaCategoryMatrix",
		"areaRanking",
		"areaStatus"
	],
	trend: [
		"scoreTrend",
		"auditsPerPeriod",
		"averageScore"
	],
	findings_follow_up: [
		"findingsList",
		"openOlderThan",
		"findingsByAge",
		"openFindings",
		"findingsByArea"
	],
	recurring_problems: [
		"repeatDeficiencies",
		"deficiencyPareto",
		"answerDistribution"
	],
	audit_changes: [
		"areaStatus",
		"repeatDeficiencies",
		"latestAudits"
	]
};
const audienceLimit = {
	team: 5,
	management: 7,
	just_me: 8
};
/** Team boards show findings and photos first and few numbers; management reviews lead with key figures. */
const teamFirst = [
	"areaStatus",
	"findingsList",
	"repeatDeficiencies",
	"photoGallery",
	"findingsByArea"
];
/** The default audience follows the page's visibility: private pages are for the author. */
const defaultAudience = (audience) => audience === "private" ? "just_me" : "team";
/** Ordered, de-duplicated catalog entry IDs for the answers, within the audience's widget budget. */
function suggestedEntries(answers) {
	const goals = answers.goals.slice(0, 2);
	const chosen = goals.length ? goals : ["overall_level"];
	const ids = [];
	const lists = chosen.map((g) => goalEntries[g]);
	const longest = Math.max(...lists.map((l) => l.length));
	for (let i = 0; i < longest; i++) for (const list of lists) {
		const id = list[i];
		if (id && !ids.includes(id)) ids.push(id);
	}
	let ordered = ids;
	if (answers.audience === "team") {
		if (chosen.includes("findings_follow_up") && !ordered.includes("photoGallery")) ordered = [...ordered, "photoGallery"];
		const numbers = ordered.filter((id) => catalogEntry(id)?.type === "keyFigure");
		ordered = ordered.filter((id) => !numbers.slice(1).includes(id));
		ordered = [...teamFirst.filter((id) => ordered.includes(id)), ...ordered.filter((id) => !teamFirst.includes(id))];
	} else {
		const isKpi = (id) => catalogEntry(id)?.type === "keyFigure";
		ordered = [...ordered.filter(isKpi), ...ordered.filter((id) => !isKpi(id))];
	}
	return ordered.slice(0, audienceLimit[answers.audience]);
}
/** Widgets for the suggested entries with localized titles; the caller arranges them automatically. */
function suggestDashboardWidgets(answers, title, sourceLocale) {
	return suggestedEntries(answers).flatMap((id) => {
		const entry = catalogEntry(id);
		return entry ? [catalogWidget(entry, title(id), sourceLocale)] : [];
	});
}
// src/page/validate.ts
const unsafeMarkup = /<\s*\/?\s*(script|style|iframe|object|embed|link|meta)|javascript:|on\w+\s*=|expression\s*\(|url\s*\(/i;
function widgetErrors(widget, index) {
	const errors = [];
	const p = `widgets.${index}`;
	const content = widget.content;
	if (isContentWidget(widget.type) && content?.kind !== widget.type) errors.push({
		path: `${p}.content`,
		message: `A ${widget.type} widget requires matching content`
	});
	if (!isContentWidget(widget.type) && content !== null) errors.push({
		path: `${p}.content`,
		message: "Data widgets have no content"
	});
	for (const key of Object.keys(widget.options)) if (widget.options[key] !== void 0 && !allowedOptionKeys[widget.type].includes(key)) errors.push({
		path: `${p}.options.${key}`,
		message: `Not supported by ${widget.type}`
	});
	for (const [key, values] of Object.entries(allowedOptionValues[widget.type] ?? {})) {
		const value = widget.options[key];
		if (typeof value === "string" && !values.includes(value)) errors.push({
			path: `${p}.options.${key}`,
			message: `${value} is not available for ${widget.type}`
		});
	}
	const variant = widget.options.variant;
	if (variant && !allowedVariants[widget.type].includes(variant)) errors.push({
		path: `${p}.options.variant`,
		message: `Variant ${variant} is not available for ${widget.type}`
	});
	const texts = [widget.title, widget.presentation.subtitle];
	if (content?.kind === "text") texts.push(content.text);
	if (content?.kind === "image") texts.push(content.alt, content.caption);
	if (texts.some((t) => unsafeMarkup.test(t))) errors.push({
		path: p,
		message: "Markup, scripts and URLs are not allowed"
	});
	return errors;
}
function gridErrors(page, cells, columns, name) {
	const errors = [];
	const ids = new Set(page.widgets.map((w) => w.id));
	const placed = /* @__PURE__ */ new Set();
	cells.forEach((cell, i) => {
		const p = `${name}.${i}`;
		if (!ids.has(cell.widgetId)) errors.push({
			path: `${p}.widgetId`,
			message: "Unknown widget ID"
		});
		if (placed.has(cell.widgetId)) errors.push({
			path: `${p}.widgetId`,
			message: "Widget placed twice"
		});
		placed.add(cell.widgetId);
		if (cell.x + cell.w > columns) errors.push({
			path: p,
			message: `Widget extends beyond ${columns} columns`
		});
		cells.slice(0, i).forEach((other, j) => {
			if (cellsOverlap(other, cell)) errors.push({
				path: p,
				message: `Overlaps widget at position ${j}`
			});
		});
	});
	return errors;
}
function orderErrors(page, order, name) {
	const errors = [];
	const ids = new Set(page.widgets.map((w) => w.id));
	if (new Set(order).size !== order.length) errors.push({
		path: name,
		message: "Each widget may appear only once"
	});
	if (order.some((id) => !ids.has(id))) errors.push({
		path: name,
		message: "Unknown widget ID"
	});
	return errors;
}
/**
* Validate a page definition on every save: unsafe values and invalid structure (unknown or duplicate
* widgets, overlaps, cells beyond the grid). There is no stricter rule for finishing an edit: the editor derives the other layouts itself, and the renderers place any widget a layout misses.
*/
function validatePage(input) {
	const parsed = PageDefinition.safeParse(input);
	if (!parsed.success) return {
		definition: null,
		errors: issuesToFieldErrors(parsed.error.issues)
	};
	const page = parsed.data;
	const errors = [];
	const seen = /* @__PURE__ */ new Set();
	page.widgets.forEach((widget, i) => {
		if (seen.has(widget.id)) errors.push({
			path: `widgets.${i}.id`,
			message: "Duplicate widget ID"
		});
		seen.add(widget.id);
		errors.push(...widgetErrors(widget, i));
	});
	if (unsafeMarkup.test(page.name) || unsafeMarkup.test(page.appearance.subtitle)) errors.push({
		path: "name",
		message: "Markup, scripts and URLs are not allowed"
	});
	errors.push(...gridErrors(page, page.desktop, 12, "desktop"), ...gridErrors(page, page.tablet, 8, "tablet"));
	if (page.layout === "freeform") errors.push(...gridErrors(page, page.freeform ?? [], 48, "freeform"));
	else if (page.freeform?.length) errors.push({
		path: "freeform",
		message: "Only Freeform pages have free positions"
	});
	errors.push(...orderErrors(page, page.mobileOrder, "mobileOrder"));
	errors.push(...orderErrors(page, page.printOrder, "printOrder"));
	return {
		definition: errors.length ? null : page,
		errors
	};
}
// src/scoring/evaluate.ts
const unanswered = {
	quality: null,
	critical: false,
	answered: false,
	notApplicable: false
};
/**
* The note on a gray "Unknown" (not applicable) answer is optional; a note that is given is at most 500 characters.
*/
function isValidNaReason(reason) {
	if (reason === null || reason === void 0) return true;
	return reason.trim().length <= 500;
}
/** Inclusive lower / exclusive upper; the final band (or one flagged includeUpper) includes the maximum. */
function findBand(bands, value, maximum) {
	return bands.find((b) => value >= b.lower && (value < b.upper || (b.includeUpper || b.upper === maximum) && value === b.upper));
}
/** Resolve the quality (0–100) and critical flag of one answer against its snapshotted rule. */
function evaluateQuestion(question, answer) {
	if (!answer) return unanswered;
	if (answer.notApplicable) {
		const valid = question.allowNotApplicable && isValidNaReason(answer.notApplicableReason);
		return {
			quality: null,
			critical: false,
			answered: valid,
			notApplicable: valid
		};
	}
	if (answer.value === null) return unanswered;
	const scoring = question.scoring;
	if (scoring.kind === "choice") {
		const option = scoring.options.find((o) => o.key === answer.value);
		return option ? {
			quality: option.quality,
			critical: option.critical,
			answered: true,
			notApplicable: false
		} : unanswered;
	}
	if (typeof answer.value !== "number") return unanswered;
	const band = findBand(scoring.bands, answer.value, scoring.maximum);
	return band ? {
		quality: band.quality,
		critical: band.critical,
		answered: true,
		notApplicable: false
	} : unanswered;
}
/** True when a submitted answer should create an Open finding automatically. */
function isNegative(question, answer) {
	const e = evaluateQuestion(question, answer);
	return !e.notApplicable && e.quality !== null && e.quality < 100;
}
// src/scoring/format.ts
/** Convert a normalized 0–100 value to the display scale, rounded half-up to one decimal. */
function toDisplay(value, displayMaximum) {
	if (value === null) return null;
	return roundHalfUp(value * displayMaximum / 100, 1);
}
/** Format with an explicit scale, e.g. "8.6/10". Null renders the supplied "not scored" text. */
function formatScore(value, displayMaximum, notScored = "Not scored", locale = "en") {
	const display = toDisplay(value, displayMaximum);
	if (display === null) return notScored;
	return `${new Intl.NumberFormat(locale, {
		minimumFractionDigits: 1,
		maximumFractionDigits: 1
	}).format(display)}/${displayMaximum}`;
}
/** A difference on the display scale is expressed in points, never as a percentage. */
function formatDelta(delta, displayMaximum, locale = "en") {
	if (delta === null) return null;
	const points = roundHalfUp(Math.abs(delta) * displayMaximum / 100, 1) * Math.sign(delta);
	return new Intl.NumberFormat(locale, {
		minimumFractionDigits: 1,
		maximumFractionDigits: 1,
		signDisplay: "exceptZero"
	}).format(points);
}
// src/scoring/score.ts
function scoreCategory(category, answers) {
	let numerator = 0;
	let denominator = 0;
	let totalQuestionWeight = 0;
	let requiredAnswered = 0;
	let requiredTotal = 0;
	let criticalCount = 0;
	const safety = isSafetyCategory(category);
	for (const question of category.questions) {
		totalQuestionWeight += question.weight;
		const evaluation = evaluateQuestion(question, answers[question.key]);
		requiredTotal += 1;
		if (evaluation.answered) requiredAnswered += 1;
		if (evaluation.critical || safety && evaluation.quality !== null && evaluation.quality < 100) criticalCount += 1;
		if (evaluation.quality !== null) {
			numerator += question.weight * evaluation.quality;
			denominator += question.weight;
		}
	}
	return {
		criticalCount,
		scored: !safety,
		score: {
			key: category.key,
			value: denominator > 0 ? numerator / denominator : null,
			categoryWeight: category.weight,
			answeredWeight: denominator,
			totalQuestionWeight,
			requiredAnswered,
			requiredTotal
		}
	};
}
/**
* Weighted category mean: category scores are weighted means of answered applicable question
* qualities; the overall score is the weighted mean of non-null scored category scores. Safety categories
* keep their own category score but stay out of the overall score and its scored coverage (they still count
* towards completion); their failed answers are counted as critical findings instead. Intermediates are never
* rounded; missing answers are excluded, never zero.
*/
function computeScore(definition, answers, provisional = true) {
	const results = definition.categories.map((c) => scoreCategory(c, answers));
	let numerator = 0;
	let denominator = 0;
	let applicableWeight = 0;
	let totalWeight = 0;
	for (const { score, scored } of results) {
		if (!scored) continue;
		if (score.value !== null) {
			numerator += score.categoryWeight * score.value;
			denominator += score.categoryWeight;
		}
		if (score.totalQuestionWeight > 0) applicableWeight += score.categoryWeight * score.answeredWeight / score.totalQuestionWeight;
		totalWeight += score.categoryWeight;
	}
	return {
		value: denominator > 0 ? numerator / denominator : null,
		provisional,
		requiredAnswered: results.reduce((n, r) => n + r.score.requiredAnswered, 0),
		requiredTotal: results.reduce((n, r) => n + r.score.requiredTotal, 0),
		applicableWeight,
		totalWeight,
		critical: results.some((r) => r.criticalCount > 0),
		criticalCount: results.reduce((n, r) => n + r.criticalCount, 0),
		categories: results.map((r) => r.score)
	};
}
// src/template/answer.ts
function validateNotApplicable(question, answer) {
	if (!question.allowNotApplicable) return [{
		path: "notApplicable",
		message: "This question does not allow Unknown"
	}];
	if (!isValidNaReason(answer.notApplicableReason)) return [{
		path: "notApplicableReason",
		message: "Keep the note to 500 characters"
	}];
	if (answer.value !== null) return [{
		path: "value",
		message: "An Unknown answer has no value"
	}];
	return [];
}
/** Server-side answer validation against the audit's snapshotted rule. */
function validateAnswer(question, answer) {
	if (answer.notApplicable) return validateNotApplicable(question, answer);
	if (answer.notApplicableReason) return [{
		path: "notApplicableReason",
		message: "Only Unknown answers carry a note"
	}];
	if (answer.value === null) return [];
	const scoring = question.scoring;
	if (scoring.kind === "choice") return typeof answer.value === "string" && scoring.options.some((o) => o.key === answer.value) ? [] : [{
		path: "value",
		message: "Choose one of the defined options"
	}];
	const value = answer.value;
	if (typeof value !== "number" || !Number.isFinite(value)) return [{
		path: "value",
		message: "Enter a number"
	}];
	if (value < scoring.minimum || value > scoring.maximum) return [{
		path: "value",
		message: `Enter a value from ${scoring.minimum} to ${scoring.maximum}`
	}];
	if (!isOnStep(value, scoring.minimum, scoring.step)) return [{
		path: "value",
		message: `Use steps of ${scoring.step}`
	}];
	if (!findBand(scoring.bands, value, scoring.maximum)) return [{
		path: "value",
		message: "Value is not covered by a scoring band"
	}];
	return [];
}
function questionIndex(def) {
	const map = /* @__PURE__ */ new Map();
	let position = 0;
	for (const category of def.categories) for (const question of category.questions) map.set(question.key, {
		question,
		categoryKey: category.key,
		categoryTitle: category.title,
		position: position++
	});
	return map;
}
// ../../spec/product/default-audit-template.json
var definition = {
	"title": "5S Audit",
	"description": "Universal 5S audit for every kind of area: Sort, Set in order, Shine, Standardize and Sustain, with safety checks reported separately.",
	"sourceLocale": "en",
	"aggregation": "weightedCategoryMean",
	"displayMaximum": 10,
	"categories": [
		{
			"key": "safety",
			"title": "Safety checks",
			"description": "Not part of the score. Every finding here is reported as a safety finding beside the score.",
			"weight": 1,
			"questions": [
				{
					"key": "safety-emergency-equipment",
					"title": "Emergency equipment that is blocked, incomplete or not signposted",
					"description": "Count each fire extinguisher, first-aid kit, eyewash station, defibrillator or emergency stop that is blocked, incomplete or has no sign.",
					"required": true,
					"allowNotApplicable": true,
					"weight": 1,
					"scoring": {
						"kind": "choice",
						"options": [
							{
								"key": "defects-0",
								"label": "None",
								"value": 10,
								"quality": 100,
								"critical": false
							},
							{
								"key": "defects-1",
								"label": "1",
								"value": 8,
								"quality": 80,
								"critical": false
							},
							{
								"key": "defects-2",
								"label": "2",
								"value": 6,
								"quality": 60,
								"critical": false
							},
							{
								"key": "defects-3",
								"label": "3",
								"value": 4,
								"quality": 40,
								"critical": false
							},
							{
								"key": "defects-4",
								"label": "4",
								"value": 2,
								"quality": 20,
								"critical": false
							},
							{
								"key": "defects-5-plus",
								"label": ">4",
								"value": 0,
								"quality": 0,
								"critical": false
							}
						]
					}
				},
				{
					"key": "safety-escape-routes",
					"title": "Obstructions on escape routes, emergency exits and fire doors",
					"description": "Count each object that narrows or blocks an escape route, an emergency exit, a fire door or the space in front of an electrical panel.",
					"required": true,
					"allowNotApplicable": true,
					"weight": 1,
					"scoring": {
						"kind": "choice",
						"options": [
							{
								"key": "defects-0",
								"label": "None",
								"value": 10,
								"quality": 100,
								"critical": false
							},
							{
								"key": "defects-1",
								"label": "1",
								"value": 8,
								"quality": 80,
								"critical": false
							},
							{
								"key": "defects-2",
								"label": "2",
								"value": 6,
								"quality": 60,
								"critical": false
							},
							{
								"key": "defects-3",
								"label": "3",
								"value": 4,
								"quality": 40,
								"critical": false
							},
							{
								"key": "defects-4",
								"label": "4",
								"value": 2,
								"quality": 20,
								"critical": false
							},
							{
								"key": "defects-5-plus",
								"label": ">4",
								"value": 0,
								"quality": 0,
								"critical": false
							}
						]
					}
				},
				{
					"key": "safety-trip-hazards",
					"title": "Trip and slip hazards on floors and walkways",
					"description": "Count loose cables or hoses, objects on the floor, spills and leaks where people walk.",
					"required": true,
					"allowNotApplicable": true,
					"weight": 1,
					"scoring": {
						"kind": "choice",
						"options": [
							{
								"key": "defects-0",
								"label": "None",
								"value": 10,
								"quality": 100,
								"critical": false
							},
							{
								"key": "defects-1",
								"label": "1",
								"value": 8,
								"quality": 80,
								"critical": false
							},
							{
								"key": "defects-2",
								"label": "2",
								"value": 6,
								"quality": 60,
								"critical": false
							},
							{
								"key": "defects-3",
								"label": "3",
								"value": 4,
								"quality": 40,
								"critical": false
							},
							{
								"key": "defects-4",
								"label": "4",
								"value": 2,
								"quality": 20,
								"critical": false
							},
							{
								"key": "defects-5-plus",
								"label": ">4",
								"value": 0,
								"quality": 0,
								"critical": false
							}
						]
					}
				},
				{
					"key": "safety-hazardous-substances",
					"title": "Hazardous substances that are unlabeled or outside their designated storage",
					"description": "Count each container of chemicals, fuels, gases or cleaning agents without a correct label or not stored where it belongs.",
					"required": true,
					"allowNotApplicable": true,
					"weight": 1,
					"scoring": {
						"kind": "choice",
						"options": [
							{
								"key": "defects-0",
								"label": "None",
								"value": 10,
								"quality": 100,
								"critical": false
							},
							{
								"key": "defects-1",
								"label": "1",
								"value": 8,
								"quality": 80,
								"critical": false
							},
							{
								"key": "defects-2",
								"label": "2",
								"value": 6,
								"quality": 60,
								"critical": false
							},
							{
								"key": "defects-3",
								"label": "3",
								"value": 4,
								"quality": 40,
								"critical": false
							},
							{
								"key": "defects-4",
								"label": "4",
								"value": 2,
								"quality": 20,
								"critical": false
							},
							{
								"key": "defects-5-plus",
								"label": ">4",
								"value": 0,
								"quality": 0,
								"critical": false
							}
						]
					},
					"showWhen": ["hazardousSubstances"]
				},
				{
					"key": "safety-ppe",
					"title": "People without the protective equipment required in the area",
					"description": "Count each person seen without the required protective equipment, such as safety shoes, glasses, gloves or hearing protection.",
					"required": true,
					"allowNotApplicable": true,
					"weight": 1,
					"scoring": {
						"kind": "choice",
						"options": [
							{
								"key": "defects-0",
								"label": "None",
								"value": 10,
								"quality": 100,
								"critical": false
							},
							{
								"key": "defects-1",
								"label": "1",
								"value": 8,
								"quality": 80,
								"critical": false
							},
							{
								"key": "defects-2",
								"label": "2",
								"value": 6,
								"quality": 60,
								"critical": false
							},
							{
								"key": "defects-3",
								"label": "3",
								"value": 4,
								"quality": 40,
								"critical": false
							},
							{
								"key": "defects-4",
								"label": "4",
								"value": 2,
								"quality": 20,
								"critical": false
							},
							{
								"key": "defects-5-plus",
								"label": ">4",
								"value": 0,
								"quality": 0,
								"critical": false
							}
						]
					},
					"showWhen": ["ppeRequired"]
				}
			],
			"kind": "safety"
		},
		{
			"key": "sort",
			"title": "Sort (Seiri)",
			"description": "Remove what is not needed.",
			"weight": 1,
			"questions": [
				{
					"key": "sort-unneeded-items",
					"title": "Items that are not needed for the work in this area",
					"description": "Count equipment, materials, furniture, documents or personal items that are not used in the normal work cycle and are not a required reserve.",
					"required": true,
					"allowNotApplicable": true,
					"weight": 1,
					"scoring": {
						"kind": "choice",
						"options": [
							{
								"key": "defects-0",
								"label": "None",
								"value": 10,
								"quality": 100,
								"critical": false
							},
							{
								"key": "defects-1",
								"label": "1",
								"value": 8,
								"quality": 80,
								"critical": false
							},
							{
								"key": "defects-2",
								"label": "2",
								"value": 6,
								"quality": 60,
								"critical": false
							},
							{
								"key": "defects-3",
								"label": "3",
								"value": 4,
								"quality": 40,
								"critical": false
							},
							{
								"key": "defects-4",
								"label": "4",
								"value": 2,
								"quality": 20,
								"critical": false
							},
							{
								"key": "defects-5-plus",
								"label": ">4",
								"value": 0,
								"quality": 0,
								"critical": false
							}
						]
					}
				},
				{
					"key": "sort-defective-items",
					"title": "Defective, expired or overdue items that are neither removed nor tagged",
					"description": "Count tools, instruments, supplies or stock that are broken, past their expiry date or overdue for calibration or inspection and can still be used because they carry no blocking tag.",
					"required": true,
					"allowNotApplicable": true,
					"weight": 1,
					"scoring": {
						"kind": "choice",
						"options": [
							{
								"key": "defects-0",
								"label": "None",
								"value": 10,
								"quality": 100,
								"critical": false
							},
							{
								"key": "defects-1",
								"label": "1",
								"value": 8,
								"quality": 80,
								"critical": false
							},
							{
								"key": "defects-2",
								"label": "2",
								"value": 6,
								"quality": 60,
								"critical": false
							},
							{
								"key": "defects-3",
								"label": "3",
								"value": 4,
								"quality": 40,
								"critical": false
							},
							{
								"key": "defects-4",
								"label": "4",
								"value": 2,
								"quality": 20,
								"critical": false
							},
							{
								"key": "defects-5-plus",
								"label": ">4",
								"value": 0,
								"quality": 0,
								"critical": false
							}
						]
					}
				},
				{
					"key": "sort-overstock",
					"title": "Storage places holding more than their agreed maximum",
					"description": "Count shelves, bins, racks or cupboards filled above their maximum mark or, where no maximum is set, visibly overfilled.",
					"required": true,
					"allowNotApplicable": true,
					"weight": 1,
					"scoring": {
						"kind": "choice",
						"options": [
							{
								"key": "defects-0",
								"label": "None",
								"value": 10,
								"quality": 100,
								"critical": false
							},
							{
								"key": "defects-1",
								"label": "1",
								"value": 8,
								"quality": 80,
								"critical": false
							},
							{
								"key": "defects-2",
								"label": "2",
								"value": 6,
								"quality": 60,
								"critical": false
							},
							{
								"key": "defects-3",
								"label": "3",
								"value": 4,
								"quality": 40,
								"critical": false
							},
							{
								"key": "defects-4",
								"label": "4",
								"value": 2,
								"quality": 20,
								"critical": false
							},
							{
								"key": "defects-5-plus",
								"label": ">4",
								"value": 0,
								"quality": 0,
								"critical": false
							}
						]
					}
				},
				{
					"key": "sort-outdated-information",
					"title": "Outdated notices and documents on display or at workplaces",
					"description": "Count notices, signs, lists, instructions or papers that are no longer valid.",
					"required": true,
					"allowNotApplicable": true,
					"weight": 1,
					"scoring": {
						"kind": "choice",
						"options": [
							{
								"key": "defects-0",
								"label": "None",
								"value": 10,
								"quality": 100,
								"critical": false
							},
							{
								"key": "defects-1",
								"label": "1",
								"value": 8,
								"quality": 80,
								"critical": false
							},
							{
								"key": "defects-2",
								"label": "2",
								"value": 6,
								"quality": 60,
								"critical": false
							},
							{
								"key": "defects-3",
								"label": "3",
								"value": 4,
								"quality": 40,
								"critical": false
							},
							{
								"key": "defects-4",
								"label": "4",
								"value": 2,
								"quality": 20,
								"critical": false
							},
							{
								"key": "defects-5-plus",
								"label": ">4",
								"value": 0,
								"quality": 0,
								"critical": false
							}
						]
					}
				},
				{
					"key": "sort-red-tag",
					"title": "Items of unclear need are red-tagged and decided within a set time",
					"description": "Ask where items of unclear need go, and look at the red-tag list or holding area.",
					"required": true,
					"allowNotApplicable": true,
					"weight": 1,
					"scoring": {
						"kind": "choice",
						"options": [
							{
								"key": "maturity-0",
								"label": "No red-tag practice",
								"value": 0,
								"quality": 0,
								"critical": false
							},
							{
								"key": "maturity-1",
								"label": "Tags are used, but tagged items stay undecided",
								"value": 2.5,
								"quality": 25,
								"critical": false
							},
							{
								"key": "maturity-2",
								"label": "Used in parts of the area",
								"value": 5,
								"quality": 50,
								"critical": false
							},
							{
								"key": "maturity-3",
								"label": "Used throughout, and decisions are mostly on time",
								"value": 7.5,
								"quality": 75,
								"critical": false
							},
							{
								"key": "maturity-4",
								"label": "Used throughout, and the red-tag list was reviewed in the current audit cycle",
								"value": 10,
								"quality": 100,
								"critical": false
							}
						]
					}
				},
				{
					"key": "sort-digital-files",
					"title": "Shared folders that hold outdated, duplicate or personal files",
					"description": "Count each such folder in the team's shared drive; check it together with the team.",
					"required": true,
					"allowNotApplicable": true,
					"weight": 1,
					"scoring": {
						"kind": "choice",
						"options": [
							{
								"key": "defects-0",
								"label": "None",
								"value": 10,
								"quality": 100,
								"critical": false
							},
							{
								"key": "defects-1",
								"label": "1",
								"value": 8,
								"quality": 80,
								"critical": false
							},
							{
								"key": "defects-2",
								"label": "2",
								"value": 6,
								"quality": 60,
								"critical": false
							},
							{
								"key": "defects-3",
								"label": "3",
								"value": 4,
								"quality": 40,
								"critical": false
							},
							{
								"key": "defects-4",
								"label": "4",
								"value": 2,
								"quality": 20,
								"critical": false
							},
							{
								"key": "defects-5-plus",
								"label": ">4",
								"value": 0,
								"quality": 0,
								"critical": false
							}
						]
					},
					"showWhen": ["deskWork"]
				}
			]
		},
		{
			"key": "set-in-order",
			"title": "Set in order (Seiton)",
			"description": "A place for everything, and everything in its place.",
			"weight": 1,
			"questions": [
				{
					"key": "set-in-order-home",
					"title": "Items or storage places without an identified home",
					"description": "Count items or storage places with no label, outline, shadow-board position, address or marked zone, or whose marking can no longer be read.",
					"required": true,
					"allowNotApplicable": true,
					"weight": 1,
					"scoring": {
						"kind": "choice",
						"options": [
							{
								"key": "defects-0",
								"label": "None",
								"value": 10,
								"quality": 100,
								"critical": false
							},
							{
								"key": "defects-1",
								"label": "1",
								"value": 8,
								"quality": 80,
								"critical": false
							},
							{
								"key": "defects-2",
								"label": "2",
								"value": 6,
								"quality": 60,
								"critical": false
							},
							{
								"key": "defects-3",
								"label": "3",
								"value": 4,
								"quality": 40,
								"critical": false
							},
							{
								"key": "defects-4",
								"label": "4",
								"value": 2,
								"quality": 20,
								"critical": false
							},
							{
								"key": "defects-5-plus",
								"label": ">4",
								"value": 0,
								"quality": 0,
								"critical": false
							}
						]
					}
				},
				{
					"key": "set-in-order-out-of-place",
					"title": "Items that are neither in their home nor in use",
					"description": "Count items left elsewhere, including items kept in improvised places such as cabinet tops, windowsills or under benches.",
					"required": true,
					"allowNotApplicable": true,
					"weight": 1,
					"scoring": {
						"kind": "choice",
						"options": [
							{
								"key": "defects-0",
								"label": "None",
								"value": 10,
								"quality": 100,
								"critical": false
							},
							{
								"key": "defects-1",
								"label": "1",
								"value": 8,
								"quality": 80,
								"critical": false
							},
							{
								"key": "defects-2",
								"label": "2",
								"value": 6,
								"quality": 60,
								"critical": false
							},
							{
								"key": "defects-3",
								"label": "3",
								"value": 4,
								"quality": 40,
								"critical": false
							},
							{
								"key": "defects-4",
								"label": "4",
								"value": 2,
								"quality": 20,
								"critical": false
							},
							{
								"key": "defects-5-plus",
								"label": ">4",
								"value": 0,
								"quality": 0,
								"critical": false
							}
						]
					}
				},
				{
					"key": "set-in-order-zones",
					"title": "Items standing outside their marked zone",
					"description": "Count items, equipment, carts or vehicles outside the marked zone for storage, parking or work.",
					"required": true,
					"allowNotApplicable": true,
					"weight": 1,
					"scoring": {
						"kind": "choice",
						"options": [
							{
								"key": "defects-0",
								"label": "None",
								"value": 10,
								"quality": 100,
								"critical": false
							},
							{
								"key": "defects-1",
								"label": "1",
								"value": 8,
								"quality": 80,
								"critical": false
							},
							{
								"key": "defects-2",
								"label": "2",
								"value": 6,
								"quality": 60,
								"critical": false
							},
							{
								"key": "defects-3",
								"label": "3",
								"value": 4,
								"quality": 40,
								"critical": false
							},
							{
								"key": "defects-4",
								"label": "4",
								"value": 2,
								"quality": 20,
								"critical": false
							},
							{
								"key": "defects-5-plus",
								"label": ">4",
								"value": 0,
								"quality": 0,
								"critical": false
							}
						]
					}
				},
				{
					"key": "set-in-order-point-of-use",
					"title": "Frequently used items that are not kept at the point of use",
					"description": "Count items that people need every day but must fetch from elsewhere. Ask the team if unsure.",
					"required": true,
					"allowNotApplicable": true,
					"weight": 1,
					"scoring": {
						"kind": "choice",
						"options": [
							{
								"key": "defects-0",
								"label": "None",
								"value": 10,
								"quality": 100,
								"critical": false
							},
							{
								"key": "defects-1",
								"label": "1",
								"value": 8,
								"quality": 80,
								"critical": false
							},
							{
								"key": "defects-2",
								"label": "2",
								"value": 6,
								"quality": 60,
								"critical": false
							},
							{
								"key": "defects-3",
								"label": "3",
								"value": 4,
								"quality": 40,
								"critical": false
							},
							{
								"key": "defects-4",
								"label": "4",
								"value": 2,
								"quality": 20,
								"critical": false
							},
							{
								"key": "defects-5-plus",
								"label": ">4",
								"value": 0,
								"quality": 0,
								"critical": false
							}
						]
					}
				},
				{
					"key": "set-in-order-digital-files",
					"title": "Shared folders that break the agreed structure or naming rules",
					"description": "Count each such folder in the team's shared drive; check it together with the team.",
					"required": true,
					"allowNotApplicable": true,
					"weight": 1,
					"scoring": {
						"kind": "choice",
						"options": [
							{
								"key": "defects-0",
								"label": "None",
								"value": 10,
								"quality": 100,
								"critical": false
							},
							{
								"key": "defects-1",
								"label": "1",
								"value": 8,
								"quality": 80,
								"critical": false
							},
							{
								"key": "defects-2",
								"label": "2",
								"value": 6,
								"quality": 60,
								"critical": false
							},
							{
								"key": "defects-3",
								"label": "3",
								"value": 4,
								"quality": 40,
								"critical": false
							},
							{
								"key": "defects-4",
								"label": "4",
								"value": 2,
								"quality": 20,
								"critical": false
							},
							{
								"key": "defects-5-plus",
								"label": ">4",
								"value": 0,
								"quality": 0,
								"critical": false
							}
						]
					},
					"showWhen": ["deskWork"]
				}
			]
		},
		{
			"key": "shine",
			"title": "Shine (Seiso)",
			"description": "Clean, and inspect while cleaning.",
			"weight": 1,
			"questions": [
				{
					"key": "shine-surfaces",
					"title": "Places where floors, walls, ceilings or lights are dirty",
					"description": "Count places with dirt, dust, residue or waste on floors, walls, ceilings, windows or light fittings.",
					"required": true,
					"allowNotApplicable": true,
					"weight": 1,
					"scoring": {
						"kind": "choice",
						"options": [
							{
								"key": "defects-0",
								"label": "None",
								"value": 10,
								"quality": 100,
								"critical": false
							},
							{
								"key": "defects-1",
								"label": "1",
								"value": 8,
								"quality": 80,
								"critical": false
							},
							{
								"key": "defects-2",
								"label": "2",
								"value": 6,
								"quality": 60,
								"critical": false
							},
							{
								"key": "defects-3",
								"label": "3",
								"value": 4,
								"quality": 40,
								"critical": false
							},
							{
								"key": "defects-4",
								"label": "4",
								"value": 2,
								"quality": 20,
								"critical": false
							},
							{
								"key": "defects-5-plus",
								"label": ">4",
								"value": 0,
								"quality": 0,
								"critical": false
							}
						]
					}
				},
				{
					"key": "shine-equipment",
					"title": "Equipment, work surfaces and furniture that are dirty",
					"description": "Count machines, devices, work surfaces, desks, carts or storage units with dirt, dust or residue.",
					"required": true,
					"allowNotApplicable": true,
					"weight": 1,
					"scoring": {
						"kind": "choice",
						"options": [
							{
								"key": "defects-0",
								"label": "None",
								"value": 10,
								"quality": 100,
								"critical": false
							},
							{
								"key": "defects-1",
								"label": "1",
								"value": 8,
								"quality": 80,
								"critical": false
							},
							{
								"key": "defects-2",
								"label": "2",
								"value": 6,
								"quality": 60,
								"critical": false
							},
							{
								"key": "defects-3",
								"label": "3",
								"value": 4,
								"quality": 40,
								"critical": false
							},
							{
								"key": "defects-4",
								"label": "4",
								"value": 2,
								"quality": 20,
								"critical": false
							},
							{
								"key": "defects-5-plus",
								"label": ">4",
								"value": 0,
								"quality": 0,
								"critical": false
							}
						]
					}
				},
				{
					"key": "shine-abnormalities",
					"title": "Abnormalities that are neither tagged nor reported",
					"description": "Cleaning is inspection. Count leaks, damage, wear, loose or missing parts and failed lights on equipment or the building that carry no tag and have no open report.",
					"required": true,
					"allowNotApplicable": true,
					"weight": 1,
					"scoring": {
						"kind": "choice",
						"options": [
							{
								"key": "defects-0",
								"label": "None",
								"value": 10,
								"quality": 100,
								"critical": false
							},
							{
								"key": "defects-1",
								"label": "1",
								"value": 8,
								"quality": 80,
								"critical": false
							},
							{
								"key": "defects-2",
								"label": "2",
								"value": 6,
								"quality": 60,
								"critical": false
							},
							{
								"key": "defects-3",
								"label": "3",
								"value": 4,
								"quality": 40,
								"critical": false
							},
							{
								"key": "defects-4",
								"label": "4",
								"value": 2,
								"quality": 20,
								"critical": false
							},
							{
								"key": "defects-5-plus",
								"label": ">4",
								"value": 0,
								"quality": 0,
								"critical": false
							}
						]
					}
				},
				{
					"key": "shine-waste",
					"title": "Waste containers that are overfilled or hold wrongly sorted waste",
					"description": "Count each bin or container that is overfilled, cannot close or holds waste that belongs in another container.",
					"required": true,
					"allowNotApplicable": true,
					"weight": 1,
					"scoring": {
						"kind": "choice",
						"options": [
							{
								"key": "defects-0",
								"label": "None",
								"value": 10,
								"quality": 100,
								"critical": false
							},
							{
								"key": "defects-1",
								"label": "1",
								"value": 8,
								"quality": 80,
								"critical": false
							},
							{
								"key": "defects-2",
								"label": "2",
								"value": 6,
								"quality": 60,
								"critical": false
							},
							{
								"key": "defects-3",
								"label": "3",
								"value": 4,
								"quality": 40,
								"critical": false
							},
							{
								"key": "defects-4",
								"label": "4",
								"value": 2,
								"quality": 20,
								"critical": false
							},
							{
								"key": "defects-5-plus",
								"label": ">4",
								"value": 0,
								"quality": 0,
								"critical": false
							}
						]
					}
				}
			]
		},
		{
			"key": "standardize",
			"title": "Standardize (Seiketsu)",
			"description": "Standards that keep the first three steps routine.",
			"weight": 1,
			"questions": [
				{
					"key": "standardize-visual-standard",
					"title": "A visual standard of the target condition is displayed and the area matches it",
					"description": "Photos or a layout show how the area should look.",
					"required": true,
					"allowNotApplicable": true,
					"weight": 1,
					"scoring": {
						"kind": "choice",
						"options": [
							{
								"key": "maturity-0",
								"label": "No visual standard",
								"value": 0,
								"quality": 0,
								"critical": false
							},
							{
								"key": "maturity-1",
								"label": "Displayed, but outdated or not matching the area",
								"value": 2.5,
								"quality": 25,
								"critical": false
							},
							{
								"key": "maturity-2",
								"label": "The area matches it in parts",
								"value": 5,
								"quality": 50,
								"critical": false
							},
							{
								"key": "maturity-3",
								"label": "The area matches it throughout, with minor gaps",
								"value": 7.5,
								"quality": 75,
								"critical": false
							},
							{
								"key": "maturity-4",
								"label": "Matches throughout, and was reviewed or updated in the current audit cycle",
								"value": 10,
								"quality": 100,
								"critical": false
							}
						]
					}
				},
				{
					"key": "standardize-routine",
					"title": "A cleaning and 5S routine states what is done, by whom and how often, and it is done",
					"description": "Compare the plan with its records for the current cycle.",
					"required": true,
					"allowNotApplicable": true,
					"weight": 1,
					"scoring": {
						"kind": "choice",
						"options": [
							{
								"key": "maturity-0",
								"label": "No routine",
								"value": 0,
								"quality": 0,
								"critical": false
							},
							{
								"key": "maturity-1",
								"label": "Written down, but not done",
								"value": 2.5,
								"quality": 25,
								"critical": false
							},
							{
								"key": "maturity-2",
								"label": "Done in parts or irregularly",
								"value": 5,
								"quality": 50,
								"critical": false
							},
							{
								"key": "maturity-3",
								"label": "Done as written, with minor gaps",
								"value": 7.5,
								"quality": 75,
								"critical": false
							},
							{
								"key": "maturity-4",
								"label": "Done as written and signed off for the whole current cycle",
								"value": 10,
								"quality": 100,
								"critical": false
							}
						]
					}
				},
				{
					"key": "standardize-labels",
					"title": "Labels, floor markings and colors follow one consistent standard",
					"description": "Compare labels, floor markings and color codes across the whole area.",
					"required": true,
					"allowNotApplicable": true,
					"weight": 1,
					"scoring": {
						"kind": "choice",
						"options": [
							{
								"key": "maturity-0",
								"label": "No standard",
								"value": 0,
								"quality": 0,
								"critical": false
							},
							{
								"key": "maturity-1",
								"label": "A standard exists but is rarely applied",
								"value": 2.5,
								"quality": 25,
								"critical": false
							},
							{
								"key": "maturity-2",
								"label": "Applied in parts of the area",
								"value": 5,
								"quality": 50,
								"critical": false
							},
							{
								"key": "maturity-3",
								"label": "Applied throughout, with minor deviations",
								"value": 7.5,
								"quality": 75,
								"critical": false
							},
							{
								"key": "maturity-4",
								"label": "Applied throughout, and the standard was reviewed in the current audit cycle",
								"value": 10,
								"quality": 100,
								"critical": false
							}
						]
					}
				},
				{
					"key": "standardize-visual-controls",
					"title": "Normal and abnormal conditions can be seen at a glance",
					"description": "For example minimum and maximum levels, due dates for calibration, inspection or expiry, and status indicators where the area needs them.",
					"required": true,
					"allowNotApplicable": true,
					"weight": 1,
					"scoring": {
						"kind": "choice",
						"options": [
							{
								"key": "maturity-0",
								"label": "No visual controls",
								"value": 0,
								"quality": 0,
								"critical": false
							},
							{
								"key": "maturity-1",
								"label": "A few, mostly outdated",
								"value": 2.5,
								"quality": 25,
								"critical": false
							},
							{
								"key": "maturity-2",
								"label": "In parts of the area",
								"value": 5,
								"quality": 50,
								"critical": false
							},
							{
								"key": "maturity-3",
								"label": "Throughout, with minor gaps",
								"value": 7.5,
								"quality": 75,
								"critical": false
							},
							{
								"key": "maturity-4",
								"label": "Throughout, and checked or updated in the current audit cycle",
								"value": 10,
								"quality": 100,
								"critical": false
							}
						]
					}
				}
			]
		},
		{
			"key": "sustain",
			"title": "Sustain (Shitsuke)",
			"description": "Discipline and follow-up that keep the standard alive.",
			"weight": 1,
			"questions": [
				{
					"key": "sustain-open-actions",
					"title": "Actions from the previous audit that are still open",
					"description": "Filled in from the findings of the area's previous audit that are still open; check it. Choose Unknown if there was no previous audit.",
					"required": true,
					"allowNotApplicable": true,
					"weight": 1,
					"scoring": {
						"kind": "choice",
						"options": [
							{
								"key": "defects-0",
								"label": "None",
								"value": 10,
								"quality": 100,
								"critical": false
							},
							{
								"key": "defects-1",
								"label": "1",
								"value": 8,
								"quality": 80,
								"critical": false
							},
							{
								"key": "defects-2",
								"label": "2",
								"value": 6,
								"quality": 60,
								"critical": false
							},
							{
								"key": "defects-3",
								"label": "3",
								"value": 4,
								"quality": 40,
								"critical": false
							},
							{
								"key": "defects-4",
								"label": "4",
								"value": 2,
								"quality": 20,
								"critical": false
							},
							{
								"key": "defects-5-plus",
								"label": ">4",
								"value": 0,
								"quality": 0,
								"critical": false
							}
						]
					},
					"prefill": "previousOpenActions"
				},
				{
					"key": "sustain-recurring-findings",
					"title": "Findings from the previous audit that have come back",
					"description": "Count findings marked as fixed after the previous audit that can be seen again today. Choose Unknown if there was no previous audit.",
					"required": true,
					"allowNotApplicable": true,
					"weight": 1,
					"scoring": {
						"kind": "choice",
						"options": [
							{
								"key": "defects-0",
								"label": "None",
								"value": 10,
								"quality": 100,
								"critical": false
							},
							{
								"key": "defects-1",
								"label": "1",
								"value": 8,
								"quality": 80,
								"critical": false
							},
							{
								"key": "defects-2",
								"label": "2",
								"value": 6,
								"quality": 60,
								"critical": false
							},
							{
								"key": "defects-3",
								"label": "3",
								"value": 4,
								"quality": 40,
								"critical": false
							},
							{
								"key": "defects-4",
								"label": "4",
								"value": 2,
								"quality": 20,
								"critical": false
							},
							{
								"key": "defects-5-plus",
								"label": ">4",
								"value": 0,
								"quality": 0,
								"critical": false
							}
						]
					}
				},
				{
					"key": "sustain-rules",
					"title": "Violations of the area's rules seen during the audit",
					"description": "Count each time a rule of the area is not followed, for example access rules, clean-as-you-go, or food and drink where they are restricted.",
					"required": true,
					"allowNotApplicable": true,
					"weight": 1,
					"scoring": {
						"kind": "choice",
						"options": [
							{
								"key": "defects-0",
								"label": "None",
								"value": 10,
								"quality": 100,
								"critical": false
							},
							{
								"key": "defects-1",
								"label": "1",
								"value": 8,
								"quality": 80,
								"critical": false
							},
							{
								"key": "defects-2",
								"label": "2",
								"value": 6,
								"quality": 60,
								"critical": false
							},
							{
								"key": "defects-3",
								"label": "3",
								"value": 4,
								"quality": 40,
								"critical": false
							},
							{
								"key": "defects-4",
								"label": "4",
								"value": 2,
								"quality": 20,
								"critical": false
							},
							{
								"key": "defects-5-plus",
								"label": ">4",
								"value": 0,
								"quality": 0,
								"critical": false
							}
						]
					}
				},
				{
					"key": "sustain-team-knowledge",
					"title": "Two team members asked can each explain the area's 5S standard and their own 5S tasks",
					"description": "Ask two people who work in the area. Answer Yes only if both can.",
					"required": true,
					"allowNotApplicable": true,
					"weight": 1,
					"scoring": {
						"kind": "choice",
						"options": [{
							"key": "yes",
							"label": "Yes",
							"value": 10,
							"quality": 100,
							"critical": false
						}, {
							"key": "no",
							"label": "No",
							"value": 0,
							"quality": 0,
							"critical": false
						}]
					}
				},
				{
					"key": "sustain-results-visible",
					"title": "The latest 5S audit result is displayed where the team sees it",
					"description": "On a board, a screen or a shared page the team uses.",
					"required": true,
					"allowNotApplicable": true,
					"weight": 1,
					"scoring": {
						"kind": "choice",
						"options": [{
							"key": "yes",
							"label": "Yes",
							"value": 10,
							"quality": 100,
							"critical": false
						}, {
							"key": "no",
							"label": "No",
							"value": 0,
							"quality": 0,
							"critical": false
						}]
					}
				}
			]
		}
	]
};
var translations = {
	"de": {
		"title": "5S-Audit",
		"description": "Universelles 5S-Audit für jede Art von Bereich: Sortieren, Systematisieren, Säubern, Standardisieren und Selbstdisziplin, mit gesondert ausgewiesenen Sicherheitsprüfungen.",
		"category.safety.title": "Sicherheitsprüfungen",
		"category.safety.description": "Nicht Teil der Bewertung. Jede Feststellung hier wird als Sicherheitsfeststellung neben der Bewertung ausgewiesen.",
		"question.safety-emergency-equipment.title": "Notfalleinrichtungen, die verstellt, unvollständig oder nicht ausgeschildert sind",
		"question.safety-emergency-equipment.description": "Zählen Sie jeden Feuerlöscher, Erste-Hilfe-Kasten, jede Augendusche, jeden Defibrillator und jeden Not-Halt, der verstellt oder unvollständig ist oder kein Schild hat.",
		"question.safety-emergency-equipment.option.defects-0.label": "Keine",
		"question.safety-emergency-equipment.option.defects-1.label": "1",
		"question.safety-emergency-equipment.option.defects-2.label": "2",
		"question.safety-emergency-equipment.option.defects-3.label": "3",
		"question.safety-emergency-equipment.option.defects-4.label": "4",
		"question.safety-emergency-equipment.option.defects-5-plus.label": ">4",
		"question.safety-escape-routes.title": "Hindernisse auf Fluchtwegen, an Notausgängen und Brandschutztüren",
		"question.safety-escape-routes.description": "Zählen Sie jeden Gegenstand, der einen Fluchtweg, einen Notausgang, eine Brandschutztür oder den Bereich vor einem Schaltschrank einengt oder versperrt.",
		"question.safety-escape-routes.option.defects-0.label": "Keine",
		"question.safety-escape-routes.option.defects-1.label": "1",
		"question.safety-escape-routes.option.defects-2.label": "2",
		"question.safety-escape-routes.option.defects-3.label": "3",
		"question.safety-escape-routes.option.defects-4.label": "4",
		"question.safety-escape-routes.option.defects-5-plus.label": ">4",
		"question.safety-trip-hazards.title": "Stolper- und Rutschgefahren auf Böden und Wegen",
		"question.safety-trip-hazards.description": "Zählen Sie lose Kabel oder Schläuche, Gegenstände auf dem Boden, verschüttete Flüssigkeiten und Leckagen dort, wo Menschen gehen.",
		"question.safety-trip-hazards.option.defects-0.label": "Keine",
		"question.safety-trip-hazards.option.defects-1.label": "1",
		"question.safety-trip-hazards.option.defects-2.label": "2",
		"question.safety-trip-hazards.option.defects-3.label": "3",
		"question.safety-trip-hazards.option.defects-4.label": "4",
		"question.safety-trip-hazards.option.defects-5-plus.label": ">4",
		"question.safety-hazardous-substances.title": "Gefahrstoffe ohne Kennzeichnung oder außerhalb ihres vorgesehenen Lagerorts",
		"question.safety-hazardous-substances.description": "Zählen Sie jeden Behälter mit Chemikalien, Kraftstoffen, Gasen oder Reinigungsmitteln ohne korrekte Kennzeichnung oder außerhalb seines Lagerorts.",
		"question.safety-hazardous-substances.option.defects-0.label": "Keine",
		"question.safety-hazardous-substances.option.defects-1.label": "1",
		"question.safety-hazardous-substances.option.defects-2.label": "2",
		"question.safety-hazardous-substances.option.defects-3.label": "3",
		"question.safety-hazardous-substances.option.defects-4.label": "4",
		"question.safety-hazardous-substances.option.defects-5-plus.label": ">4",
		"question.safety-ppe.title": "Personen ohne die im Bereich vorgeschriebene Schutzausrüstung",
		"question.safety-ppe.description": "Zählen Sie jede Person, die ohne die vorgeschriebene Schutzausrüstung gesehen wird, zum Beispiel Sicherheitsschuhe, Schutzbrille, Handschuhe oder Gehörschutz.",
		"question.safety-ppe.option.defects-0.label": "Keine",
		"question.safety-ppe.option.defects-1.label": "1",
		"question.safety-ppe.option.defects-2.label": "2",
		"question.safety-ppe.option.defects-3.label": "3",
		"question.safety-ppe.option.defects-4.label": "4",
		"question.safety-ppe.option.defects-5-plus.label": ">4",
		"category.sort.title": "Sortieren (Seiri)",
		"category.sort.description": "Entfernen, was nicht gebraucht wird.",
		"question.sort-unneeded-items.title": "Gegenstände, die für die Arbeit in diesem Bereich nicht gebraucht werden",
		"question.sort-unneeded-items.description": "Zählen Sie Geräte, Material, Möbel, Dokumente oder persönliche Gegenstände, die im normalen Arbeitsablauf nicht genutzt werden und keine vorgeschriebene Reserve sind.",
		"question.sort-unneeded-items.option.defects-0.label": "Keine",
		"question.sort-unneeded-items.option.defects-1.label": "1",
		"question.sort-unneeded-items.option.defects-2.label": "2",
		"question.sort-unneeded-items.option.defects-3.label": "3",
		"question.sort-unneeded-items.option.defects-4.label": "4",
		"question.sort-unneeded-items.option.defects-5-plus.label": ">4",
		"question.sort-defective-items.title": "Defekte, abgelaufene oder überfällige Gegenstände, die weder entfernt noch gekennzeichnet sind",
		"question.sort-defective-items.description": "Zählen Sie Werkzeuge, Instrumente, Verbrauchsmaterial oder Bestände, die defekt, abgelaufen oder überfällig für Kalibrierung oder Prüfung sind und noch benutzt werden können, weil sie keine Sperrkennzeichnung tragen.",
		"question.sort-defective-items.option.defects-0.label": "Keine",
		"question.sort-defective-items.option.defects-1.label": "1",
		"question.sort-defective-items.option.defects-2.label": "2",
		"question.sort-defective-items.option.defects-3.label": "3",
		"question.sort-defective-items.option.defects-4.label": "4",
		"question.sort-defective-items.option.defects-5-plus.label": ">4",
		"question.sort-overstock.title": "Lagerplätze mit mehr als dem vereinbarten Höchstbestand",
		"question.sort-overstock.description": "Zählen Sie Regale, Behälter, Gestelle oder Schränke, die über ihre Höchstmarke gefüllt oder, wo kein Höchstwert festgelegt ist, sichtbar überfüllt sind.",
		"question.sort-overstock.option.defects-0.label": "Keine",
		"question.sort-overstock.option.defects-1.label": "1",
		"question.sort-overstock.option.defects-2.label": "2",
		"question.sort-overstock.option.defects-3.label": "3",
		"question.sort-overstock.option.defects-4.label": "4",
		"question.sort-overstock.option.defects-5-plus.label": ">4",
		"question.sort-outdated-information.title": "Veraltete Aushänge und Dokumente an Wänden oder Arbeitsplätzen",
		"question.sort-outdated-information.description": "Zählen Sie Aushänge, Schilder, Listen, Anweisungen oder Papiere, die nicht mehr gültig sind.",
		"question.sort-outdated-information.option.defects-0.label": "Keine",
		"question.sort-outdated-information.option.defects-1.label": "1",
		"question.sort-outdated-information.option.defects-2.label": "2",
		"question.sort-outdated-information.option.defects-3.label": "3",
		"question.sort-outdated-information.option.defects-4.label": "4",
		"question.sort-outdated-information.option.defects-5-plus.label": ">4",
		"question.sort-red-tag.title": "Gegenstände, deren Bedarf unklar ist, werden mit einem roten Anhänger (Red Tag) gekennzeichnet, und innerhalb einer festen Frist wird über sie entschieden",
		"question.sort-red-tag.description": "Fragen Sie, wohin Gegenstände kommen, deren Bedarf unklar ist, und sehen Sie sich die Red-Tag-Liste oder die Red-Tag-Fläche (Zwischenlager für ungeklärte Gegenstände) an.",
		"question.sort-red-tag.option.maturity-0.label": "Red Tags werden nicht verwendet",
		"question.sort-red-tag.option.maturity-1.label": "Red Tags werden angebracht, aber über die markierten Gegenstände wird nicht entschieden",
		"question.sort-red-tag.option.maturity-2.label": "In Teilen des Bereichs genutzt",
		"question.sort-red-tag.option.maturity-3.label": "Überall genutzt, und meist wird fristgerecht entschieden",
		"question.sort-red-tag.option.maturity-4.label": "Überall genutzt, und die Red-Tag-Liste wurde im laufenden Auditzyklus geprüft",
		"question.sort-digital-files.title": "Gemeinsame Ordner mit veralteten, doppelten oder persönlichen Dateien",
		"question.sort-digital-files.description": "Zählen Sie jeden solchen Ordner im gemeinsamen Laufwerk des Teams; prüfen Sie das gemeinsam mit dem Team.",
		"question.sort-digital-files.option.defects-0.label": "Keine",
		"question.sort-digital-files.option.defects-1.label": "1",
		"question.sort-digital-files.option.defects-2.label": "2",
		"question.sort-digital-files.option.defects-3.label": "3",
		"question.sort-digital-files.option.defects-4.label": "4",
		"question.sort-digital-files.option.defects-5-plus.label": ">4",
		"category.set-in-order.title": "Systematisieren (Seiton)",
		"category.set-in-order.description": "Ein Platz für alles, und alles an seinem Platz.",
		"question.set-in-order-home.title": "Gegenstände oder Lagerplätze ohne festgelegten, gekennzeichneten Platz",
		"question.set-in-order-home.description": "Zählen Sie Gegenstände oder Lagerplätze ohne Beschriftung, Umrissmarkierung, Platz am Schattenbrett, Lagerplatznummer oder markierte Zone sowie solche, deren Kennzeichnung nicht mehr lesbar ist.",
		"question.set-in-order-home.option.defects-0.label": "Keine",
		"question.set-in-order-home.option.defects-1.label": "1",
		"question.set-in-order-home.option.defects-2.label": "2",
		"question.set-in-order-home.option.defects-3.label": "3",
		"question.set-in-order-home.option.defects-4.label": "4",
		"question.set-in-order-home.option.defects-5-plus.label": ">4",
		"question.set-in-order-out-of-place.title": "Gegenstände, die weder an ihrem Platz noch in Benutzung sind",
		"question.set-in-order-out-of-place.description": "Zählen Sie Gegenstände, die anderswo liegen, auch solche an behelfsmäßigen Ablagen wie auf Schränken, Fensterbänken oder unter Werkbänken.",
		"question.set-in-order-out-of-place.option.defects-0.label": "Keine",
		"question.set-in-order-out-of-place.option.defects-1.label": "1",
		"question.set-in-order-out-of-place.option.defects-2.label": "2",
		"question.set-in-order-out-of-place.option.defects-3.label": "3",
		"question.set-in-order-out-of-place.option.defects-4.label": "4",
		"question.set-in-order-out-of-place.option.defects-5-plus.label": ">4",
		"question.set-in-order-zones.title": "Gegenstände außerhalb ihrer markierten Zone",
		"question.set-in-order-zones.description": "Zählen Sie Gegenstände, Geräte, Wagen oder Fahrzeuge außerhalb der markierten Zone zum Lagern, Abstellen oder Arbeiten.",
		"question.set-in-order-zones.option.defects-0.label": "Keine",
		"question.set-in-order-zones.option.defects-1.label": "1",
		"question.set-in-order-zones.option.defects-2.label": "2",
		"question.set-in-order-zones.option.defects-3.label": "3",
		"question.set-in-order-zones.option.defects-4.label": "4",
		"question.set-in-order-zones.option.defects-5-plus.label": ">4",
		"question.set-in-order-point-of-use.title": "Häufig genutzte Gegenstände, die nicht griffbereit dort liegen, wo sie gebraucht werden",
		"question.set-in-order-point-of-use.description": "Zählen Sie Gegenstände, die täglich gebraucht, aber von woanders geholt werden müssen. Fragen Sie im Zweifel das Team.",
		"question.set-in-order-point-of-use.option.defects-0.label": "Keine",
		"question.set-in-order-point-of-use.option.defects-1.label": "1",
		"question.set-in-order-point-of-use.option.defects-2.label": "2",
		"question.set-in-order-point-of-use.option.defects-3.label": "3",
		"question.set-in-order-point-of-use.option.defects-4.label": "4",
		"question.set-in-order-point-of-use.option.defects-5-plus.label": ">4",
		"question.set-in-order-digital-files.title": "Gemeinsame Ordner, die gegen die vereinbarte Struktur oder Benennungsregeln verstoßen",
		"question.set-in-order-digital-files.description": "Zählen Sie jeden solchen Ordner im gemeinsamen Laufwerk des Teams; prüfen Sie das gemeinsam mit dem Team.",
		"question.set-in-order-digital-files.option.defects-0.label": "Keine",
		"question.set-in-order-digital-files.option.defects-1.label": "1",
		"question.set-in-order-digital-files.option.defects-2.label": "2",
		"question.set-in-order-digital-files.option.defects-3.label": "3",
		"question.set-in-order-digital-files.option.defects-4.label": "4",
		"question.set-in-order-digital-files.option.defects-5-plus.label": ">4",
		"category.shine.title": "Säubern (Seiso)",
		"category.shine.description": "Reinigen und beim Reinigen prüfen.",
		"question.shine-surfaces.title": "Stellen, an denen Böden, Wände, Decken oder Leuchten verschmutzt sind",
		"question.shine-surfaces.description": "Zählen Sie Stellen mit Schmutz, Staub, Rückständen oder Abfall auf Böden, Wänden, Decken, Fenstern oder Leuchten.",
		"question.shine-surfaces.option.defects-0.label": "Keine",
		"question.shine-surfaces.option.defects-1.label": "1",
		"question.shine-surfaces.option.defects-2.label": "2",
		"question.shine-surfaces.option.defects-3.label": "3",
		"question.shine-surfaces.option.defects-4.label": "4",
		"question.shine-surfaces.option.defects-5-plus.label": ">4",
		"question.shine-equipment.title": "Verschmutzte Geräte, Arbeitsflächen und Möbel",
		"question.shine-equipment.description": "Zählen Sie Maschinen, Geräte, Arbeitsflächen, Schreibtische, Wagen oder Aufbewahrungsmöbel mit Schmutz, Staub oder Rückständen.",
		"question.shine-equipment.option.defects-0.label": "Keine",
		"question.shine-equipment.option.defects-1.label": "1",
		"question.shine-equipment.option.defects-2.label": "2",
		"question.shine-equipment.option.defects-3.label": "3",
		"question.shine-equipment.option.defects-4.label": "4",
		"question.shine-equipment.option.defects-5-plus.label": ">4",
		"question.shine-abnormalities.title": "Auffälligkeiten, die weder gekennzeichnet noch gemeldet sind",
		"question.shine-abnormalities.description": "Reinigen heißt Prüfen. Zählen Sie Leckagen, Schäden, Verschleiß, lose oder fehlende Teile und ausgefallene Leuchten an Geräten oder am Gebäude, die keine Kennzeichnung und keine offene Meldung haben.",
		"question.shine-abnormalities.option.defects-0.label": "Keine",
		"question.shine-abnormalities.option.defects-1.label": "1",
		"question.shine-abnormalities.option.defects-2.label": "2",
		"question.shine-abnormalities.option.defects-3.label": "3",
		"question.shine-abnormalities.option.defects-4.label": "4",
		"question.shine-abnormalities.option.defects-5-plus.label": ">4",
		"question.shine-waste.title": "Abfallbehälter, die überfüllt sind oder falsch getrennten Abfall enthalten",
		"question.shine-waste.description": "Zählen Sie jeden Behälter, der überfüllt ist, nicht schließt oder Abfall enthält, der in einen anderen Behälter gehört.",
		"question.shine-waste.option.defects-0.label": "Keine",
		"question.shine-waste.option.defects-1.label": "1",
		"question.shine-waste.option.defects-2.label": "2",
		"question.shine-waste.option.defects-3.label": "3",
		"question.shine-waste.option.defects-4.label": "4",
		"question.shine-waste.option.defects-5-plus.label": ">4",
		"category.standardize.title": "Standardisieren (Seiketsu)",
		"category.standardize.description": "Standards, die die ersten drei Schritte zur Routine machen.",
		"question.standardize-visual-standard.title": "Der Sollzustand ist bildlich dargestellt und aufgehängt, und der Bereich entspricht ihm",
		"question.standardize-visual-standard.description": "Fotos oder ein Layout zeigen, wie der Bereich aussehen soll.",
		"question.standardize-visual-standard.option.maturity-0.label": "Keine bildliche Darstellung des Sollzustands",
		"question.standardize-visual-standard.option.maturity-1.label": "Hängt aus, ist aber veraltet oder passt nicht zum Bereich",
		"question.standardize-visual-standard.option.maturity-2.label": "Der Bereich entspricht ihm teilweise",
		"question.standardize-visual-standard.option.maturity-3.label": "Der Bereich entspricht ihm überall, mit kleinen Lücken",
		"question.standardize-visual-standard.option.maturity-4.label": "Entspricht überall und wurde im laufenden Auditzyklus geprüft oder aktualisiert",
		"question.standardize-routine.title": "Ein Reinigungs- und 5S-Plan legt fest, was wer wie oft erledigt, und er wird eingehalten",
		"question.standardize-routine.description": "Vergleichen Sie den Plan mit seinen Nachweisen für den laufenden Zyklus.",
		"question.standardize-routine.option.maturity-0.label": "Keine Routine",
		"question.standardize-routine.option.maturity-1.label": "Aufgeschrieben, aber nicht umgesetzt",
		"question.standardize-routine.option.maturity-2.label": "Teilweise oder unregelmäßig umgesetzt",
		"question.standardize-routine.option.maturity-3.label": "Wie festgelegt umgesetzt, mit kleinen Lücken",
		"question.standardize-routine.option.maturity-4.label": "Wie festgelegt umgesetzt und für den ganzen laufenden Zyklus abgezeichnet",
		"question.standardize-labels.title": "Beschriftungen, Bodenmarkierungen und Farben folgen einem einheitlichen Standard",
		"question.standardize-labels.description": "Vergleichen Sie Beschriftungen, Bodenmarkierungen und Farbcodes im gesamten Bereich.",
		"question.standardize-labels.option.maturity-0.label": "Kein Standard",
		"question.standardize-labels.option.maturity-1.label": "Ein Standard existiert, wird aber selten angewendet",
		"question.standardize-labels.option.maturity-2.label": "In Teilen des Bereichs angewendet",
		"question.standardize-labels.option.maturity-3.label": "Überall angewendet, mit kleinen Abweichungen",
		"question.standardize-labels.option.maturity-4.label": "Überall angewendet, und der Standard wurde im laufenden Auditzyklus geprüft",
		"question.standardize-visual-controls.title": "Auf einen Blick ist erkennbar, ob alles im Sollzustand ist oder etwas abweicht",
		"question.standardize-visual-controls.description": "Zum Beispiel Markierungen für Mindest- und Höchstbestand, sichtbare Kalibrier-, Prüf- oder Ablaufdaten und Statusanzeigen, wo der Bereich sie braucht.",
		"question.standardize-visual-controls.option.maturity-0.label": "Keine visuellen Anzeigen",
		"question.standardize-visual-controls.option.maturity-1.label": "Wenige, meist veraltet",
		"question.standardize-visual-controls.option.maturity-2.label": "In Teilen des Bereichs",
		"question.standardize-visual-controls.option.maturity-3.label": "Überall, mit kleinen Lücken",
		"question.standardize-visual-controls.option.maturity-4.label": "Überall, und im laufenden Auditzyklus geprüft oder aktualisiert",
		"category.sustain.title": "Selbstdisziplin (Shitsuke)",
		"category.sustain.description": "Disziplin und Nachverfolgung, die den Standard lebendig halten.",
		"question.sustain-open-actions.title": "Maßnahmen aus dem letzten Audit, die noch offen sind",
		"question.sustain-open-actions.description": "Wird aus den noch offenen Feststellungen des letzten Audits dieses Bereichs ausgefüllt; bitte prüfen. Gab es kein letztes Audit, wählen Sie „Unbekannt“.",
		"question.sustain-open-actions.option.defects-0.label": "Keine",
		"question.sustain-open-actions.option.defects-1.label": "1",
		"question.sustain-open-actions.option.defects-2.label": "2",
		"question.sustain-open-actions.option.defects-3.label": "3",
		"question.sustain-open-actions.option.defects-4.label": "4",
		"question.sustain-open-actions.option.defects-5-plus.label": ">4",
		"question.sustain-recurring-findings.title": "Feststellungen aus dem letzten Audit, die wieder aufgetreten sind",
		"question.sustain-recurring-findings.description": "Zählen Sie Feststellungen, die nach dem letzten Audit als behoben markiert wurden und heute wieder zu sehen sind. Gab es kein letztes Audit, wählen Sie „Unbekannt“.",
		"question.sustain-recurring-findings.option.defects-0.label": "Keine",
		"question.sustain-recurring-findings.option.defects-1.label": "1",
		"question.sustain-recurring-findings.option.defects-2.label": "2",
		"question.sustain-recurring-findings.option.defects-3.label": "3",
		"question.sustain-recurring-findings.option.defects-4.label": "4",
		"question.sustain-recurring-findings.option.defects-5-plus.label": ">4",
		"question.sustain-rules.title": "Während des Audits beobachtete Verstöße gegen die Regeln des Bereichs",
		"question.sustain-rules.description": "Zählen Sie jedes Mal, wenn eine Regel des Bereichs nicht eingehalten wird, zum Beispiel Zutrittsregeln, Aufräumen und Reinigen direkt während der Arbeit oder Essen und Trinken, wo es eingeschränkt ist.",
		"question.sustain-rules.option.defects-0.label": "Keine",
		"question.sustain-rules.option.defects-1.label": "1",
		"question.sustain-rules.option.defects-2.label": "2",
		"question.sustain-rules.option.defects-3.label": "3",
		"question.sustain-rules.option.defects-4.label": "4",
		"question.sustain-rules.option.defects-5-plus.label": ">4",
		"question.sustain-team-knowledge.title": "Zwei befragte Teammitglieder können jeweils den 5S-Standard des Bereichs und ihre eigenen 5S-Aufgaben erklären",
		"question.sustain-team-knowledge.description": "Fragen Sie zwei Personen, die im Bereich arbeiten. Antworten Sie nur mit Ja, wenn beide es können.",
		"question.sustain-team-knowledge.option.yes.label": "Ja",
		"question.sustain-team-knowledge.option.no.label": "Nein",
		"question.sustain-results-visible.title": "Das letzte 5S-Auditergebnis hängt dort aus, wo das Team es sieht",
		"question.sustain-results-visible.description": "Auf einer Tafel, einem Bildschirm oder einer gemeinsamen Seite, die das Team nutzt.",
		"question.sustain-results-visible.option.yes.label": "Ja",
		"question.sustain-results-visible.option.no.label": "Nein"
	},
	"es": {
		"title": "Auditoría 5S",
		"description": "Auditoría 5S universal para cualquier tipo de área: clasificar, ordenar, limpiar, estandarizar y mantener, con controles de seguridad informados por separado.",
		"category.safety.title": "Controles de seguridad",
		"category.safety.description": "No forma parte de la puntuación. Cada hallazgo de aquí se informa como hallazgo de seguridad junto a la puntuación.",
		"question.safety-emergency-equipment.title": "Equipos de emergencia bloqueados, incompletos o sin señalizar",
		"question.safety-emergency-equipment.description": "Cuente cada extintor, botiquín, lavaojos, desfibrilador o parada de emergencia que esté bloqueado, incompleto o sin señal.",
		"question.safety-emergency-equipment.option.defects-0.label": "Ninguno",
		"question.safety-emergency-equipment.option.defects-1.label": "1",
		"question.safety-emergency-equipment.option.defects-2.label": "2",
		"question.safety-emergency-equipment.option.defects-3.label": "3",
		"question.safety-emergency-equipment.option.defects-4.label": "4",
		"question.safety-emergency-equipment.option.defects-5-plus.label": ">4",
		"question.safety-escape-routes.title": "Obstáculos en vías de evacuación, salidas de emergencia y puertas cortafuegos",
		"question.safety-escape-routes.description": "Cuente cada objeto que estreche o bloquee una vía de evacuación, una salida de emergencia, una puerta cortafuegos o el espacio delante de un cuadro eléctrico.",
		"question.safety-escape-routes.option.defects-0.label": "Ninguno",
		"question.safety-escape-routes.option.defects-1.label": "1",
		"question.safety-escape-routes.option.defects-2.label": "2",
		"question.safety-escape-routes.option.defects-3.label": "3",
		"question.safety-escape-routes.option.defects-4.label": "4",
		"question.safety-escape-routes.option.defects-5-plus.label": ">4",
		"question.safety-trip-hazards.title": "Riesgos de tropiezo y resbalón en suelos y pasillos",
		"question.safety-trip-hazards.description": "Cuente cables o mangueras sueltos, objetos en el suelo, derrames y fugas donde camina la gente.",
		"question.safety-trip-hazards.option.defects-0.label": "Ninguno",
		"question.safety-trip-hazards.option.defects-1.label": "1",
		"question.safety-trip-hazards.option.defects-2.label": "2",
		"question.safety-trip-hazards.option.defects-3.label": "3",
		"question.safety-trip-hazards.option.defects-4.label": "4",
		"question.safety-trip-hazards.option.defects-5-plus.label": ">4",
		"question.safety-hazardous-substances.title": "Sustancias peligrosas sin etiquetar o fuera de su almacenamiento designado",
		"question.safety-hazardous-substances.description": "Cuente cada recipiente de productos químicos, combustibles, gases o productos de limpieza sin etiqueta correcta o que no esté guardado donde corresponde.",
		"question.safety-hazardous-substances.option.defects-0.label": "Ninguno",
		"question.safety-hazardous-substances.option.defects-1.label": "1",
		"question.safety-hazardous-substances.option.defects-2.label": "2",
		"question.safety-hazardous-substances.option.defects-3.label": "3",
		"question.safety-hazardous-substances.option.defects-4.label": "4",
		"question.safety-hazardous-substances.option.defects-5-plus.label": ">4",
		"question.safety-ppe.title": "Personas sin el equipo de protección obligatorio en el área",
		"question.safety-ppe.description": "Cuente cada persona vista sin el equipo de protección obligatorio, como calzado de seguridad, gafas, guantes o protección auditiva.",
		"question.safety-ppe.option.defects-0.label": "Ninguno",
		"question.safety-ppe.option.defects-1.label": "1",
		"question.safety-ppe.option.defects-2.label": "2",
		"question.safety-ppe.option.defects-3.label": "3",
		"question.safety-ppe.option.defects-4.label": "4",
		"question.safety-ppe.option.defects-5-plus.label": ">4",
		"category.sort.title": "Clasificar (Seiri)",
		"category.sort.description": "Retirar lo que no se necesita.",
		"question.sort-unneeded-items.title": "Objetos que no se necesitan para el trabajo en esta área",
		"question.sort-unneeded-items.description": "Cuente equipos, materiales, muebles, documentos u objetos personales que no se usan en el ciclo normal de trabajo y no son una reserva obligatoria.",
		"question.sort-unneeded-items.option.defects-0.label": "Ninguno",
		"question.sort-unneeded-items.option.defects-1.label": "1",
		"question.sort-unneeded-items.option.defects-2.label": "2",
		"question.sort-unneeded-items.option.defects-3.label": "3",
		"question.sort-unneeded-items.option.defects-4.label": "4",
		"question.sort-unneeded-items.option.defects-5-plus.label": ">4",
		"question.sort-defective-items.title": "Objetos defectuosos, caducados o vencidos que no se han retirado ni etiquetado",
		"question.sort-defective-items.description": "Cuente herramientas, instrumentos, suministros o existencias rotos, caducados o con la calibración o inspección vencida que aún pueden usarse porque no llevan etiqueta de bloqueo.",
		"question.sort-defective-items.option.defects-0.label": "Ninguno",
		"question.sort-defective-items.option.defects-1.label": "1",
		"question.sort-defective-items.option.defects-2.label": "2",
		"question.sort-defective-items.option.defects-3.label": "3",
		"question.sort-defective-items.option.defects-4.label": "4",
		"question.sort-defective-items.option.defects-5-plus.label": ">4",
		"question.sort-overstock.title": "Lugares de almacenamiento con más de su máximo acordado",
		"question.sort-overstock.description": "Cuente estantes, cajas, estanterías o armarios llenos por encima de su marca máxima o, donde no hay máximo, visiblemente desbordados.",
		"question.sort-overstock.option.defects-0.label": "Ninguno",
		"question.sort-overstock.option.defects-1.label": "1",
		"question.sort-overstock.option.defects-2.label": "2",
		"question.sort-overstock.option.defects-3.label": "3",
		"question.sort-overstock.option.defects-4.label": "4",
		"question.sort-overstock.option.defects-5-plus.label": ">4",
		"question.sort-outdated-information.title": "Avisos y documentos obsoletos expuestos o en los puestos de trabajo",
		"question.sort-outdated-information.description": "Cuente avisos, carteles, listas, instrucciones o papeles que ya no son válidos.",
		"question.sort-outdated-information.option.defects-0.label": "Ninguno",
		"question.sort-outdated-information.option.defects-1.label": "1",
		"question.sort-outdated-information.option.defects-2.label": "2",
		"question.sort-outdated-information.option.defects-3.label": "3",
		"question.sort-outdated-information.option.defects-4.label": "4",
		"question.sort-outdated-information.option.defects-5-plus.label": ">4",
		"question.sort-red-tag.title": "Los objetos de los que no está claro si se necesitan se marcan con tarjeta roja y se decide qué hacer con ellos en un plazo fijado",
		"question.sort-red-tag.description": "Pregunte adónde van los objetos de los que no está claro si se necesitan y revise la lista de tarjetas rojas o el área de tarjetas rojas.",
		"question.sort-red-tag.option.maturity-0.label": "No se usan tarjetas rojas",
		"question.sort-red-tag.option.maturity-1.label": "Se ponen tarjetas rojas, pero nadie decide qué hacer con los objetos marcados",
		"question.sort-red-tag.option.maturity-2.label": "Se usa en partes del área",
		"question.sort-red-tag.option.maturity-3.label": "Se usa en toda el área y las decisiones suelen llegar a tiempo",
		"question.sort-red-tag.option.maturity-4.label": "Se usa en toda el área y la lista de tarjetas rojas se revisó en el ciclo de auditoría actual",
		"question.sort-digital-files.title": "Carpetas compartidas con archivos obsoletos, duplicados o personales",
		"question.sort-digital-files.description": "Cuente cada carpeta de ese tipo en la unidad compartida del equipo; revíselo junto con el equipo.",
		"question.sort-digital-files.option.defects-0.label": "Ninguno",
		"question.sort-digital-files.option.defects-1.label": "1",
		"question.sort-digital-files.option.defects-2.label": "2",
		"question.sort-digital-files.option.defects-3.label": "3",
		"question.sort-digital-files.option.defects-4.label": "4",
		"question.sort-digital-files.option.defects-5-plus.label": ">4",
		"category.set-in-order.title": "Ordenar (Seiton)",
		"category.set-in-order.description": "Un lugar para cada cosa y cada cosa en su lugar.",
		"question.set-in-order-home.title": "Objetos o lugares de almacenamiento sin un sitio identificado",
		"question.set-in-order-home.description": "Cuente objetos o lugares de almacenamiento sin etiqueta, contorno marcado, posición en el panel de siluetas, código de ubicación o zona marcada, o cuya marca ya no se puede leer.",
		"question.set-in-order-home.option.defects-0.label": "Ninguno",
		"question.set-in-order-home.option.defects-1.label": "1",
		"question.set-in-order-home.option.defects-2.label": "2",
		"question.set-in-order-home.option.defects-3.label": "3",
		"question.set-in-order-home.option.defects-4.label": "4",
		"question.set-in-order-home.option.defects-5-plus.label": ">4",
		"question.set-in-order-out-of-place.title": "Objetos que no están en su sitio ni en uso",
		"question.set-in-order-out-of-place.description": "Cuente los objetos dejados en otro lugar, incluidos los guardados en sitios improvisados como encima de armarios, alféizares o debajo de bancos.",
		"question.set-in-order-out-of-place.option.defects-0.label": "Ninguno",
		"question.set-in-order-out-of-place.option.defects-1.label": "1",
		"question.set-in-order-out-of-place.option.defects-2.label": "2",
		"question.set-in-order-out-of-place.option.defects-3.label": "3",
		"question.set-in-order-out-of-place.option.defects-4.label": "4",
		"question.set-in-order-out-of-place.option.defects-5-plus.label": ">4",
		"question.set-in-order-zones.title": "Objetos fuera de su zona marcada",
		"question.set-in-order-zones.description": "Cuente objetos, equipos, carros o vehículos fuera de la zona marcada para almacenar, estacionar o trabajar.",
		"question.set-in-order-zones.option.defects-0.label": "Ninguno",
		"question.set-in-order-zones.option.defects-1.label": "1",
		"question.set-in-order-zones.option.defects-2.label": "2",
		"question.set-in-order-zones.option.defects-3.label": "3",
		"question.set-in-order-zones.option.defects-4.label": "4",
		"question.set-in-order-zones.option.defects-5-plus.label": ">4",
		"question.set-in-order-point-of-use.title": "Objetos de uso frecuente que no se guardan en el punto de uso",
		"question.set-in-order-point-of-use.description": "Cuente los objetos que se necesitan cada día pero hay que ir a buscar a otro sitio. Si tiene dudas, pregunte al equipo.",
		"question.set-in-order-point-of-use.option.defects-0.label": "Ninguno",
		"question.set-in-order-point-of-use.option.defects-1.label": "1",
		"question.set-in-order-point-of-use.option.defects-2.label": "2",
		"question.set-in-order-point-of-use.option.defects-3.label": "3",
		"question.set-in-order-point-of-use.option.defects-4.label": "4",
		"question.set-in-order-point-of-use.option.defects-5-plus.label": ">4",
		"question.set-in-order-digital-files.title": "Carpetas compartidas que incumplen la estructura o las reglas de nombres acordadas",
		"question.set-in-order-digital-files.description": "Cuente cada carpeta de ese tipo en la unidad compartida del equipo; revíselo junto con el equipo.",
		"question.set-in-order-digital-files.option.defects-0.label": "Ninguno",
		"question.set-in-order-digital-files.option.defects-1.label": "1",
		"question.set-in-order-digital-files.option.defects-2.label": "2",
		"question.set-in-order-digital-files.option.defects-3.label": "3",
		"question.set-in-order-digital-files.option.defects-4.label": "4",
		"question.set-in-order-digital-files.option.defects-5-plus.label": ">4",
		"category.shine.title": "Limpiar (Seiso)",
		"category.shine.description": "Limpiar e inspeccionar al limpiar.",
		"question.shine-surfaces.title": "Lugares donde suelos, paredes, techos o luces están sucios",
		"question.shine-surfaces.description": "Cuente lugares con suciedad, polvo, restos o residuos en suelos, paredes, techos, ventanas o luminarias.",
		"question.shine-surfaces.option.defects-0.label": "Ninguno",
		"question.shine-surfaces.option.defects-1.label": "1",
		"question.shine-surfaces.option.defects-2.label": "2",
		"question.shine-surfaces.option.defects-3.label": "3",
		"question.shine-surfaces.option.defects-4.label": "4",
		"question.shine-surfaces.option.defects-5-plus.label": ">4",
		"question.shine-equipment.title": "Equipos, superficies de trabajo y muebles sucios",
		"question.shine-equipment.description": "Cuente máquinas, aparatos, superficies de trabajo, escritorios, carros o muebles de almacenamiento con suciedad, polvo o restos.",
		"question.shine-equipment.option.defects-0.label": "Ninguno",
		"question.shine-equipment.option.defects-1.label": "1",
		"question.shine-equipment.option.defects-2.label": "2",
		"question.shine-equipment.option.defects-3.label": "3",
		"question.shine-equipment.option.defects-4.label": "4",
		"question.shine-equipment.option.defects-5-plus.label": ">4",
		"question.shine-abnormalities.title": "Anomalías que no están etiquetadas ni comunicadas",
		"question.shine-abnormalities.description": "Limpiar es inspeccionar. Cuente fugas, daños, desgaste, piezas sueltas o que faltan y luces fundidas en equipos o en el edificio que no tienen etiqueta ni aviso abierto.",
		"question.shine-abnormalities.option.defects-0.label": "Ninguno",
		"question.shine-abnormalities.option.defects-1.label": "1",
		"question.shine-abnormalities.option.defects-2.label": "2",
		"question.shine-abnormalities.option.defects-3.label": "3",
		"question.shine-abnormalities.option.defects-4.label": "4",
		"question.shine-abnormalities.option.defects-5-plus.label": ">4",
		"question.shine-waste.title": "Contenedores de residuos desbordados o con residuos mal separados",
		"question.shine-waste.description": "Cuente cada papelera o contenedor desbordado, que no cierra o que contiene residuos que van en otro contenedor.",
		"question.shine-waste.option.defects-0.label": "Ninguno",
		"question.shine-waste.option.defects-1.label": "1",
		"question.shine-waste.option.defects-2.label": "2",
		"question.shine-waste.option.defects-3.label": "3",
		"question.shine-waste.option.defects-4.label": "4",
		"question.shine-waste.option.defects-5-plus.label": ">4",
		"category.standardize.title": "Estandarizar (Seiketsu)",
		"category.standardize.description": "Estándares que convierten los tres primeros pasos en rutina.",
		"question.standardize-visual-standard.title": "Se muestra un estándar visual del estado objetivo y el área lo cumple",
		"question.standardize-visual-standard.description": "Fotos o un plano muestran cómo debe verse el área.",
		"question.standardize-visual-standard.option.maturity-0.label": "No hay estándar visual",
		"question.standardize-visual-standard.option.maturity-1.label": "Se muestra, pero está desactualizado o no coincide con el área",
		"question.standardize-visual-standard.option.maturity-2.label": "El área lo cumple en parte",
		"question.standardize-visual-standard.option.maturity-3.label": "El área lo cumple en todas partes, con pequeñas carencias",
		"question.standardize-visual-standard.option.maturity-4.label": "Lo cumple en todas partes y se revisó o actualizó en el ciclo de auditoría actual",
		"question.standardize-routine.title": "Una rutina de limpieza y 5S indica qué se hace, quién y con qué frecuencia, y se cumple",
		"question.standardize-routine.description": "Compare el plan con sus registros del ciclo actual.",
		"question.standardize-routine.option.maturity-0.label": "No hay rutina",
		"question.standardize-routine.option.maturity-1.label": "Está escrita, pero no se cumple",
		"question.standardize-routine.option.maturity-2.label": "Se cumple en parte o de forma irregular",
		"question.standardize-routine.option.maturity-3.label": "Se cumple tal como está escrita, con pequeñas carencias",
		"question.standardize-routine.option.maturity-4.label": "Se cumple tal como está escrita y hay registros firmados de todo el ciclo actual",
		"question.standardize-labels.title": "Etiquetas, marcas en el suelo y colores siguen un único estándar coherente",
		"question.standardize-labels.description": "Compare etiquetas, marcas en el suelo y códigos de color en toda el área.",
		"question.standardize-labels.option.maturity-0.label": "No hay estándar",
		"question.standardize-labels.option.maturity-1.label": "Existe un estándar, pero rara vez se aplica",
		"question.standardize-labels.option.maturity-2.label": "Se aplica en partes del área",
		"question.standardize-labels.option.maturity-3.label": "Se aplica en toda el área, con pequeñas desviaciones",
		"question.standardize-labels.option.maturity-4.label": "Se aplica en toda el área y el estándar se revisó en el ciclo de auditoría actual",
		"question.standardize-visual-controls.title": "Las situaciones normales y anormales se ven de un vistazo",
		"question.standardize-visual-controls.description": "Por ejemplo, niveles mínimos y máximos, fechas de calibración, inspección o caducidad e indicadores de estado donde el área los necesita.",
		"question.standardize-visual-controls.option.maturity-0.label": "No hay controles visuales",
		"question.standardize-visual-controls.option.maturity-1.label": "Algunos, en su mayoría desactualizados",
		"question.standardize-visual-controls.option.maturity-2.label": "En partes del área",
		"question.standardize-visual-controls.option.maturity-3.label": "En toda el área, con pequeñas carencias",
		"question.standardize-visual-controls.option.maturity-4.label": "En toda el área, y revisados o actualizados en el ciclo de auditoría actual",
		"category.sustain.title": "Mantener (Shitsuke)",
		"category.sustain.description": "Disciplina y seguimiento que mantienen vivo el estándar.",
		"question.sustain-open-actions.title": "Acciones de la auditoría anterior que siguen abiertas",
		"question.sustain-open-actions.description": "Se rellena con los hallazgos aún abiertos de la auditoría anterior del área; compruébelo. Si no hubo auditoría anterior, elija «Desconocido».",
		"question.sustain-open-actions.option.defects-0.label": "Ninguno",
		"question.sustain-open-actions.option.defects-1.label": "1",
		"question.sustain-open-actions.option.defects-2.label": "2",
		"question.sustain-open-actions.option.defects-3.label": "3",
		"question.sustain-open-actions.option.defects-4.label": "4",
		"question.sustain-open-actions.option.defects-5-plus.label": ">4",
		"question.sustain-recurring-findings.title": "Hallazgos de la auditoría anterior que han vuelto a aparecer",
		"question.sustain-recurring-findings.description": "Cuente los hallazgos marcados como resueltos después de la auditoría anterior que hoy vuelven a verse. Si no hubo auditoría anterior, elija «Desconocido».",
		"question.sustain-recurring-findings.option.defects-0.label": "Ninguno",
		"question.sustain-recurring-findings.option.defects-1.label": "1",
		"question.sustain-recurring-findings.option.defects-2.label": "2",
		"question.sustain-recurring-findings.option.defects-3.label": "3",
		"question.sustain-recurring-findings.option.defects-4.label": "4",
		"question.sustain-recurring-findings.option.defects-5-plus.label": ">4",
		"question.sustain-rules.title": "Incumplimientos de las normas del área vistos durante la auditoría",
		"question.sustain-rules.description": "Cuente cada vez que no se cumple una norma del área, por ejemplo normas de acceso, limpiar sobre la marcha, o comida y bebida donde están restringidas.",
		"question.sustain-rules.option.defects-0.label": "Ninguno",
		"question.sustain-rules.option.defects-1.label": "1",
		"question.sustain-rules.option.defects-2.label": "2",
		"question.sustain-rules.option.defects-3.label": "3",
		"question.sustain-rules.option.defects-4.label": "4",
		"question.sustain-rules.option.defects-5-plus.label": ">4",
		"question.sustain-team-knowledge.title": "Al preguntarles, dos miembros del equipo saben explicar cada uno el estándar 5S del área y sus propias tareas 5S",
		"question.sustain-team-knowledge.description": "Pregunte a dos personas que trabajan en el área. Responda Sí solo si ambas pueden.",
		"question.sustain-team-knowledge.option.yes.label": "Sí",
		"question.sustain-team-knowledge.option.no.label": "No",
		"question.sustain-results-visible.title": "El último resultado de la auditoría 5S se muestra donde el equipo lo ve",
		"question.sustain-results-visible.description": "En un tablero, una pantalla o una página compartida que usa el equipo.",
		"question.sustain-results-visible.option.yes.label": "Sí",
		"question.sustain-results-visible.option.no.label": "No"
	},
	"fr": {
		"title": "Audit 5S",
		"description": "Audit 5S universel pour tout type de zone : trier, ranger, nettoyer, standardiser et maintenir, avec des contrôles de sécurité présentés à part.",
		"category.safety.title": "Contrôles de sécurité",
		"category.safety.description": "Ne compte pas dans le score. Chaque constat ici est signalé comme constat de sécurité à côté du score.",
		"question.safety-emergency-equipment.title": "Équipements d'urgence dont l'accès est encombré, incomplets ou non signalés",
		"question.safety-emergency-equipment.description": "Comptez chaque extincteur, trousse de secours, douche oculaire, défibrillateur ou arrêt d'urgence dont l'accès est encombré, incomplet ou sans signalisation.",
		"question.safety-emergency-equipment.option.defects-0.label": "Aucun",
		"question.safety-emergency-equipment.option.defects-1.label": "1",
		"question.safety-emergency-equipment.option.defects-2.label": "2",
		"question.safety-emergency-equipment.option.defects-3.label": "3",
		"question.safety-emergency-equipment.option.defects-4.label": "4",
		"question.safety-emergency-equipment.option.defects-5-plus.label": ">4",
		"question.safety-escape-routes.title": "Obstacles sur les voies d'évacuation, les issues de secours et les portes coupe-feu",
		"question.safety-escape-routes.description": "Comptez chaque objet qui rétrécit ou bloque une voie d'évacuation, une issue de secours, une porte coupe-feu ou l'espace devant une armoire électrique.",
		"question.safety-escape-routes.option.defects-0.label": "Aucun",
		"question.safety-escape-routes.option.defects-1.label": "1",
		"question.safety-escape-routes.option.defects-2.label": "2",
		"question.safety-escape-routes.option.defects-3.label": "3",
		"question.safety-escape-routes.option.defects-4.label": "4",
		"question.safety-escape-routes.option.defects-5-plus.label": ">4",
		"question.safety-trip-hazards.title": "Risques de trébuchement et de glissade sur les sols et les allées",
		"question.safety-trip-hazards.description": "Comptez les câbles ou tuyaux qui traînent, les objets au sol, les déversements et les fuites là où les gens marchent.",
		"question.safety-trip-hazards.option.defects-0.label": "Aucun",
		"question.safety-trip-hazards.option.defects-1.label": "1",
		"question.safety-trip-hazards.option.defects-2.label": "2",
		"question.safety-trip-hazards.option.defects-3.label": "3",
		"question.safety-trip-hazards.option.defects-4.label": "4",
		"question.safety-trip-hazards.option.defects-5-plus.label": ">4",
		"question.safety-hazardous-substances.title": "Substances dangereuses non étiquetées ou hors de leur stockage prévu",
		"question.safety-hazardous-substances.description": "Comptez chaque récipient de produits chimiques, de carburants, de gaz ou de produits de nettoyage sans étiquette correcte ou qui n'est pas rangé à sa place.",
		"question.safety-hazardous-substances.option.defects-0.label": "Aucun",
		"question.safety-hazardous-substances.option.defects-1.label": "1",
		"question.safety-hazardous-substances.option.defects-2.label": "2",
		"question.safety-hazardous-substances.option.defects-3.label": "3",
		"question.safety-hazardous-substances.option.defects-4.label": "4",
		"question.safety-hazardous-substances.option.defects-5-plus.label": ">4",
		"question.safety-ppe.title": "Personnes sans l'équipement de protection exigé dans la zone",
		"question.safety-ppe.description": "Comptez chaque personne vue sans l'équipement de protection exigé, comme les chaussures de sécurité, les lunettes, les gants ou les protections auditives.",
		"question.safety-ppe.option.defects-0.label": "Aucun",
		"question.safety-ppe.option.defects-1.label": "1",
		"question.safety-ppe.option.defects-2.label": "2",
		"question.safety-ppe.option.defects-3.label": "3",
		"question.safety-ppe.option.defects-4.label": "4",
		"question.safety-ppe.option.defects-5-plus.label": ">4",
		"category.sort.title": "Trier (Seiri)",
		"category.sort.description": "Retirer ce qui n'est pas nécessaire.",
		"question.sort-unneeded-items.title": "Objets qui ne sont pas nécessaires au travail dans cette zone",
		"question.sort-unneeded-items.description": "Comptez les équipements, matériaux, meubles, documents ou objets personnels qui ne servent pas dans le cycle de travail normal et ne sont pas une réserve obligatoire.",
		"question.sort-unneeded-items.option.defects-0.label": "Aucun",
		"question.sort-unneeded-items.option.defects-1.label": "1",
		"question.sort-unneeded-items.option.defects-2.label": "2",
		"question.sort-unneeded-items.option.defects-3.label": "3",
		"question.sort-unneeded-items.option.defects-4.label": "4",
		"question.sort-unneeded-items.option.defects-5-plus.label": ">4",
		"question.sort-defective-items.title": "Objets défectueux, périmés ou en retard de contrôle qui ne sont ni retirés ni marqués",
		"question.sort-defective-items.description": "Comptez les outils, instruments, fournitures ou stocks cassés, périmés ou en retard d'étalonnage ou de contrôle qui peuvent encore être utilisés faute d'étiquette de blocage.",
		"question.sort-defective-items.option.defects-0.label": "Aucun",
		"question.sort-defective-items.option.defects-1.label": "1",
		"question.sort-defective-items.option.defects-2.label": "2",
		"question.sort-defective-items.option.defects-3.label": "3",
		"question.sort-defective-items.option.defects-4.label": "4",
		"question.sort-defective-items.option.defects-5-plus.label": ">4",
		"question.sort-overstock.title": "Emplacements de stockage qui dépassent leur maximum convenu",
		"question.sort-overstock.description": "Comptez les étagères, bacs, racks ou armoires remplis au-delà de leur repère maximum ou, sans maximum défini, visiblement trop pleins.",
		"question.sort-overstock.option.defects-0.label": "Aucun",
		"question.sort-overstock.option.defects-1.label": "1",
		"question.sort-overstock.option.defects-2.label": "2",
		"question.sort-overstock.option.defects-3.label": "3",
		"question.sort-overstock.option.defects-4.label": "4",
		"question.sort-overstock.option.defects-5-plus.label": ">4",
		"question.sort-outdated-information.title": "Affichages et documents périmés exposés ou aux postes de travail",
		"question.sort-outdated-information.description": "Comptez les affichages, panneaux, listes, instructions ou papiers qui ne sont plus valables.",
		"question.sort-outdated-information.option.defects-0.label": "Aucun",
		"question.sort-outdated-information.option.defects-1.label": "1",
		"question.sort-outdated-information.option.defects-2.label": "2",
		"question.sort-outdated-information.option.defects-3.label": "3",
		"question.sort-outdated-information.option.defects-4.label": "4",
		"question.sort-outdated-information.option.defects-5-plus.label": ">4",
		"question.sort-red-tag.title": "Les objets dont on ne sait pas s'ils sont nécessaires reçoivent une étiquette rouge, et leur sort est décidé dans un délai fixé",
		"question.sort-red-tag.description": "Demandez où vont les objets dont on ne sait pas s'ils sont nécessaires, et regardez la liste des étiquettes rouges ou la zone de quarantaine.",
		"question.sort-red-tag.option.maturity-0.label": "Pas de pratique d'étiquette rouge",
		"question.sort-red-tag.option.maturity-1.label": "Des étiquettes rouges sont posées, mais le sort des objets marqués reste en suspens",
		"question.sort-red-tag.option.maturity-2.label": "Utilisée dans une partie de la zone",
		"question.sort-red-tag.option.maturity-3.label": "Utilisée partout, et les décisions sont le plus souvent prises à temps",
		"question.sort-red-tag.option.maturity-4.label": "Utilisée partout, et la liste des étiquettes rouges a été revue pendant le cycle d'audit en cours",
		"question.sort-digital-files.title": "Dossiers partagés contenant des fichiers périmés, en double ou personnels",
		"question.sort-digital-files.description": "Comptez chaque dossier de ce type dans le lecteur partagé de l'équipe ; vérifiez-le avec l'équipe.",
		"question.sort-digital-files.option.defects-0.label": "Aucun",
		"question.sort-digital-files.option.defects-1.label": "1",
		"question.sort-digital-files.option.defects-2.label": "2",
		"question.sort-digital-files.option.defects-3.label": "3",
		"question.sort-digital-files.option.defects-4.label": "4",
		"question.sort-digital-files.option.defects-5-plus.label": ">4",
		"category.set-in-order.title": "Ranger (Seiton)",
		"category.set-in-order.description": "Une place pour chaque chose, et chaque chose à sa place.",
		"question.set-in-order-home.title": "Objets ou emplacements de stockage sans place identifiée",
		"question.set-in-order-home.description": "Comptez les objets ou emplacements sans étiquette, contour tracé, place sur un tableau d'ombres, code d'emplacement ou zone marquée, ou dont le marquage n'est plus lisible.",
		"question.set-in-order-home.option.defects-0.label": "Aucun",
		"question.set-in-order-home.option.defects-1.label": "1",
		"question.set-in-order-home.option.defects-2.label": "2",
		"question.set-in-order-home.option.defects-3.label": "3",
		"question.set-in-order-home.option.defects-4.label": "4",
		"question.set-in-order-home.option.defects-5-plus.label": ">4",
		"question.set-in-order-out-of-place.title": "Objets qui ne sont ni à leur place ni en cours d'utilisation",
		"question.set-in-order-out-of-place.description": "Comptez les objets laissés ailleurs, y compris ceux posés dans des endroits improvisés comme le dessus des armoires, les rebords de fenêtre ou sous les établis.",
		"question.set-in-order-out-of-place.option.defects-0.label": "Aucun",
		"question.set-in-order-out-of-place.option.defects-1.label": "1",
		"question.set-in-order-out-of-place.option.defects-2.label": "2",
		"question.set-in-order-out-of-place.option.defects-3.label": "3",
		"question.set-in-order-out-of-place.option.defects-4.label": "4",
		"question.set-in-order-out-of-place.option.defects-5-plus.label": ">4",
		"question.set-in-order-zones.title": "Objets en dehors de leur zone marquée",
		"question.set-in-order-zones.description": "Comptez les objets, équipements, chariots ou véhicules en dehors de la zone marquée de stockage, de stationnement ou de travail.",
		"question.set-in-order-zones.option.defects-0.label": "Aucun",
		"question.set-in-order-zones.option.defects-1.label": "1",
		"question.set-in-order-zones.option.defects-2.label": "2",
		"question.set-in-order-zones.option.defects-3.label": "3",
		"question.set-in-order-zones.option.defects-4.label": "4",
		"question.set-in-order-zones.option.defects-5-plus.label": ">4",
		"question.set-in-order-point-of-use.title": "Objets souvent utilisés qui ne sont pas rangés au point d'utilisation",
		"question.set-in-order-point-of-use.description": "Comptez les objets nécessaires chaque jour qu'il faut aller chercher ailleurs. En cas de doute, demandez à l'équipe.",
		"question.set-in-order-point-of-use.option.defects-0.label": "Aucun",
		"question.set-in-order-point-of-use.option.defects-1.label": "1",
		"question.set-in-order-point-of-use.option.defects-2.label": "2",
		"question.set-in-order-point-of-use.option.defects-3.label": "3",
		"question.set-in-order-point-of-use.option.defects-4.label": "4",
		"question.set-in-order-point-of-use.option.defects-5-plus.label": ">4",
		"question.set-in-order-digital-files.title": "Dossiers partagés qui ne respectent pas la structure ou les règles de nommage convenues",
		"question.set-in-order-digital-files.description": "Comptez chaque dossier de ce type dans le lecteur partagé de l'équipe ; vérifiez-le avec l'équipe.",
		"question.set-in-order-digital-files.option.defects-0.label": "Aucun",
		"question.set-in-order-digital-files.option.defects-1.label": "1",
		"question.set-in-order-digital-files.option.defects-2.label": "2",
		"question.set-in-order-digital-files.option.defects-3.label": "3",
		"question.set-in-order-digital-files.option.defects-4.label": "4",
		"question.set-in-order-digital-files.option.defects-5-plus.label": ">4",
		"category.shine.title": "Nettoyer (Seiso)",
		"category.shine.description": "Nettoyer, et inspecter en nettoyant.",
		"question.shine-surfaces.title": "Endroits où les sols, les murs, les plafonds ou les éclairages sont sales",
		"question.shine-surfaces.description": "Comptez les endroits avec de la saleté, de la poussière, des résidus ou des déchets sur les sols, murs, plafonds, fenêtres ou luminaires.",
		"question.shine-surfaces.option.defects-0.label": "Aucun",
		"question.shine-surfaces.option.defects-1.label": "1",
		"question.shine-surfaces.option.defects-2.label": "2",
		"question.shine-surfaces.option.defects-3.label": "3",
		"question.shine-surfaces.option.defects-4.label": "4",
		"question.shine-surfaces.option.defects-5-plus.label": ">4",
		"question.shine-equipment.title": "Équipements, surfaces de travail et meubles sales",
		"question.shine-equipment.description": "Comptez les machines, appareils, surfaces de travail, bureaux, chariots ou meubles de rangement avec de la saleté, de la poussière ou des résidus.",
		"question.shine-equipment.option.defects-0.label": "Aucun",
		"question.shine-equipment.option.defects-1.label": "1",
		"question.shine-equipment.option.defects-2.label": "2",
		"question.shine-equipment.option.defects-3.label": "3",
		"question.shine-equipment.option.defects-4.label": "4",
		"question.shine-equipment.option.defects-5-plus.label": ">4",
		"question.shine-abnormalities.title": "Anomalies qui ne sont ni signalées par une étiquette ni déclarées",
		"question.shine-abnormalities.description": "Nettoyer, c'est inspecter. Comptez les fuites, dommages, usures, pièces desserrées ou manquantes et éclairages en panne sur les équipements ou le bâtiment qui n'ont ni étiquette ni signalement ouvert.",
		"question.shine-abnormalities.option.defects-0.label": "Aucun",
		"question.shine-abnormalities.option.defects-1.label": "1",
		"question.shine-abnormalities.option.defects-2.label": "2",
		"question.shine-abnormalities.option.defects-3.label": "3",
		"question.shine-abnormalities.option.defects-4.label": "4",
		"question.shine-abnormalities.option.defects-5-plus.label": ">4",
		"question.shine-waste.title": "Conteneurs à déchets trop pleins ou contenant des déchets mal triés",
		"question.shine-waste.description": "Comptez chaque poubelle ou conteneur trop plein, qui ne ferme pas ou qui contient des déchets destinés à un autre conteneur.",
		"question.shine-waste.option.defects-0.label": "Aucun",
		"question.shine-waste.option.defects-1.label": "1",
		"question.shine-waste.option.defects-2.label": "2",
		"question.shine-waste.option.defects-3.label": "3",
		"question.shine-waste.option.defects-4.label": "4",
		"question.shine-waste.option.defects-5-plus.label": ">4",
		"category.standardize.title": "Standardiser (Seiketsu)",
		"category.standardize.description": "Des standards qui font des trois premières étapes une routine.",
		"question.standardize-visual-standard.title": "Un standard visuel de l'état cible est affiché et la zone y correspond",
		"question.standardize-visual-standard.description": "Des photos ou un plan montrent à quoi la zone doit ressembler.",
		"question.standardize-visual-standard.option.maturity-0.label": "Pas de standard visuel",
		"question.standardize-visual-standard.option.maturity-1.label": "Affiché, mais périmé ou ne correspondant pas à la zone",
		"question.standardize-visual-standard.option.maturity-2.label": "La zone y correspond en partie",
		"question.standardize-visual-standard.option.maturity-3.label": "La zone y correspond partout, avec de petits écarts",
		"question.standardize-visual-standard.option.maturity-4.label": "Correspond partout, et a été revu ou mis à jour pendant le cycle d'audit en cours",
		"question.standardize-routine.title": "Une routine de nettoyage et 5S précise quoi faire, par qui et à quelle fréquence, et elle est suivie",
		"question.standardize-routine.description": "Comparez le plan avec ses enregistrements du cycle en cours.",
		"question.standardize-routine.option.maturity-0.label": "Pas de routine",
		"question.standardize-routine.option.maturity-1.label": "Écrite, mais pas suivie",
		"question.standardize-routine.option.maturity-2.label": "Suivie en partie ou irrégulièrement",
		"question.standardize-routine.option.maturity-3.label": "Suivie comme prévu, avec de petits écarts",
		"question.standardize-routine.option.maturity-4.label": "Suivie comme prévu et validée pour tout le cycle en cours",
		"question.standardize-labels.title": "Les étiquettes, marquages au sol et couleurs suivent un même standard cohérent",
		"question.standardize-labels.description": "Comparez les étiquettes, les marquages au sol et les codes couleur dans toute la zone.",
		"question.standardize-labels.option.maturity-0.label": "Pas de standard",
		"question.standardize-labels.option.maturity-1.label": "Un standard existe, mais il est rarement appliqué",
		"question.standardize-labels.option.maturity-2.label": "Appliqué dans une partie de la zone",
		"question.standardize-labels.option.maturity-3.label": "Appliqué partout, avec de petits écarts",
		"question.standardize-labels.option.maturity-4.label": "Appliqué partout, et le standard a été revu pendant le cycle d'audit en cours",
		"question.standardize-visual-controls.title": "Les états normaux et anormaux se voient d'un coup d'œil",
		"question.standardize-visual-controls.description": "Par exemple des niveaux minimum et maximum, des échéances d'étalonnage, de contrôle ou de péremption et des indicateurs d'état là où la zone en a besoin.",
		"question.standardize-visual-controls.option.maturity-0.label": "Pas d'indicateurs visuels",
		"question.standardize-visual-controls.option.maturity-1.label": "Quelques-uns, souvent périmés",
		"question.standardize-visual-controls.option.maturity-2.label": "Dans une partie de la zone",
		"question.standardize-visual-controls.option.maturity-3.label": "Partout, avec de petits écarts",
		"question.standardize-visual-controls.option.maturity-4.label": "Partout, et vérifiés ou mis à jour pendant le cycle d'audit en cours",
		"category.sustain.title": "Maintenir (Shitsuke)",
		"category.sustain.description": "Discipline et suivi qui font vivre le standard.",
		"question.sustain-open-actions.title": "Actions de l'audit précédent encore ouvertes",
		"question.sustain-open-actions.description": "Rempli à partir des constats encore ouverts du dernier audit de la zone ; vérifiez-le. S'il n'y a pas eu d'audit précédent, choisissez « Inconnu ».",
		"question.sustain-open-actions.option.defects-0.label": "Aucun",
		"question.sustain-open-actions.option.defects-1.label": "1",
		"question.sustain-open-actions.option.defects-2.label": "2",
		"question.sustain-open-actions.option.defects-3.label": "3",
		"question.sustain-open-actions.option.defects-4.label": "4",
		"question.sustain-open-actions.option.defects-5-plus.label": ">4",
		"question.sustain-recurring-findings.title": "Constats de l'audit précédent qui sont réapparus",
		"question.sustain-recurring-findings.description": "Comptez les constats marqués comme corrigés après l'audit précédent qui sont à nouveau visibles aujourd'hui. S'il n'y a pas eu d'audit précédent, choisissez « Inconnu ».",
		"question.sustain-recurring-findings.option.defects-0.label": "Aucun",
		"question.sustain-recurring-findings.option.defects-1.label": "1",
		"question.sustain-recurring-findings.option.defects-2.label": "2",
		"question.sustain-recurring-findings.option.defects-3.label": "3",
		"question.sustain-recurring-findings.option.defects-4.label": "4",
		"question.sustain-recurring-findings.option.defects-5-plus.label": ">4",
		"question.sustain-rules.title": "Manquements aux règles de la zone observés pendant l'audit",
		"question.sustain-rules.description": "Comptez chaque fois qu'une règle de la zone n'est pas respectée, par exemple les règles d'accès, le nettoyage au fil du travail, ou la nourriture et les boissons là où elles sont limitées.",
		"question.sustain-rules.option.defects-0.label": "Aucun",
		"question.sustain-rules.option.defects-1.label": "1",
		"question.sustain-rules.option.defects-2.label": "2",
		"question.sustain-rules.option.defects-3.label": "3",
		"question.sustain-rules.option.defects-4.label": "4",
		"question.sustain-rules.option.defects-5-plus.label": ">4",
		"question.sustain-team-knowledge.title": "Deux membres de l'équipe interrogés savent chacun expliquer le standard 5S de la zone et leurs propres tâches 5S",
		"question.sustain-team-knowledge.description": "Interrogez deux personnes qui travaillent dans la zone. Répondez Oui seulement si les deux le peuvent.",
		"question.sustain-team-knowledge.option.yes.label": "Oui",
		"question.sustain-team-knowledge.option.no.label": "Non",
		"question.sustain-results-visible.title": "Le dernier résultat d'audit 5S est affiché là où l'équipe le voit",
		"question.sustain-results-visible.description": "Sur un tableau, un écran ou une page partagée que l'équipe utilise.",
		"question.sustain-results-visible.option.yes.label": "Oui",
		"question.sustain-results-visible.option.no.label": "Non"
	},
	"tr": {
		"title": "5S Denetimi",
		"description": "Her tür alan için evrensel 5S denetimi: ayıklama, düzenleme, temizlik, standartlaştırma ve sürdürme; güvenlik kontrolleri ayrı raporlanır.",
		"category.safety.title": "Güvenlik kontrolleri",
		"category.safety.description": "Puana dahil değildir. Buradaki her bulgu, puanın yanında güvenlik bulgusu olarak raporlanır.",
		"question.safety-emergency-equipment.title": "Önü kapalı, eksik veya işaretsiz acil durum ekipmanları",
		"question.safety-emergency-equipment.description": "Önü kapalı, eksik veya işaretsiz her yangın söndürücüyü, ilk yardım çantasını, göz duşunu, defibrilatörü ve acil durdurma düğmesini sayın.",
		"question.safety-emergency-equipment.option.defects-0.label": "Yok",
		"question.safety-emergency-equipment.option.defects-1.label": "1",
		"question.safety-emergency-equipment.option.defects-2.label": "2",
		"question.safety-emergency-equipment.option.defects-3.label": "3",
		"question.safety-emergency-equipment.option.defects-4.label": "4",
		"question.safety-emergency-equipment.option.defects-5-plus.label": ">4",
		"question.safety-escape-routes.title": "Kaçış yollarında, acil çıkışlarda ve yangın kapılarında engeller",
		"question.safety-escape-routes.description": "Bir kaçış yolunu, acil çıkışı, yangın kapısını veya elektrik panosunun önündeki alanı daraltan ya da kapatan her nesneyi sayın.",
		"question.safety-escape-routes.option.defects-0.label": "Yok",
		"question.safety-escape-routes.option.defects-1.label": "1",
		"question.safety-escape-routes.option.defects-2.label": "2",
		"question.safety-escape-routes.option.defects-3.label": "3",
		"question.safety-escape-routes.option.defects-4.label": "4",
		"question.safety-escape-routes.option.defects-5-plus.label": ">4",
		"question.safety-trip-hazards.title": "Zeminlerde ve yürüyüş yollarında takılma ve kayma tehlikeleri",
		"question.safety-trip-hazards.description": "İnsanların yürüdüğü yerlerdeki gevşek kabloları veya hortumları, yerdeki nesneleri, dökülmeleri ve sızıntıları sayın.",
		"question.safety-trip-hazards.option.defects-0.label": "Yok",
		"question.safety-trip-hazards.option.defects-1.label": "1",
		"question.safety-trip-hazards.option.defects-2.label": "2",
		"question.safety-trip-hazards.option.defects-3.label": "3",
		"question.safety-trip-hazards.option.defects-4.label": "4",
		"question.safety-trip-hazards.option.defects-5-plus.label": ">4",
		"question.safety-hazardous-substances.title": "Etiketsiz veya belirlenmiş depolama yeri dışında bulunan tehlikeli maddeler",
		"question.safety-hazardous-substances.description": "Doğru etiketi olmayan veya ait olduğu yerde saklanmayan her kimyasal, yakıt, gaz veya temizlik maddesi kabını sayın.",
		"question.safety-hazardous-substances.option.defects-0.label": "Yok",
		"question.safety-hazardous-substances.option.defects-1.label": "1",
		"question.safety-hazardous-substances.option.defects-2.label": "2",
		"question.safety-hazardous-substances.option.defects-3.label": "3",
		"question.safety-hazardous-substances.option.defects-4.label": "4",
		"question.safety-hazardous-substances.option.defects-5-plus.label": ">4",
		"question.safety-ppe.title": "Alanda zorunlu koruyucu donanımı kullanmayan kişiler",
		"question.safety-ppe.description": "Zorunlu koruyucu donanım olmadan görülen her kişiyi sayın; örneğin iş ayakkabısı, gözlük, eldiven veya kulak koruyucu.",
		"question.safety-ppe.option.defects-0.label": "Yok",
		"question.safety-ppe.option.defects-1.label": "1",
		"question.safety-ppe.option.defects-2.label": "2",
		"question.safety-ppe.option.defects-3.label": "3",
		"question.safety-ppe.option.defects-4.label": "4",
		"question.safety-ppe.option.defects-5-plus.label": ">4",
		"category.sort.title": "Ayıklama (Seiri)",
		"category.sort.description": "Gerekmeyeni kaldırın.",
		"question.sort-unneeded-items.title": "Bu alandaki iş için gerekmeyen eşyalar",
		"question.sort-unneeded-items.description": "Normal iş döngüsünde kullanılmayan ve zorunlu yedek olmayan ekipman, malzeme, mobilya, belge veya kişisel eşyaları sayın.",
		"question.sort-unneeded-items.option.defects-0.label": "Yok",
		"question.sort-unneeded-items.option.defects-1.label": "1",
		"question.sort-unneeded-items.option.defects-2.label": "2",
		"question.sort-unneeded-items.option.defects-3.label": "3",
		"question.sort-unneeded-items.option.defects-4.label": "4",
		"question.sort-unneeded-items.option.defects-5-plus.label": ">4",
		"question.sort-defective-items.title": "Ne kaldırılmış ne de etiketlenmiş arızalı, son kullanma tarihi geçmiş veya kontrol tarihi gecikmiş eşyalar",
		"question.sort-defective-items.description": "Kırık, son kullanma tarihi geçmiş ya da kalibrasyonu veya muayenesi gecikmiş olup engelleme etiketi taşımadığı için hâlâ kullanılabilen aletleri, cihazları, sarf malzemelerini veya stokları sayın.",
		"question.sort-defective-items.option.defects-0.label": "Yok",
		"question.sort-defective-items.option.defects-1.label": "1",
		"question.sort-defective-items.option.defects-2.label": "2",
		"question.sort-defective-items.option.defects-3.label": "3",
		"question.sort-defective-items.option.defects-4.label": "4",
		"question.sort-defective-items.option.defects-5-plus.label": ">4",
		"question.sort-overstock.title": "Belirlenen azami miktarın üzerinde dolu depolama yerleri",
		"question.sort-overstock.description": "Azami işaretinin üzerinde doldurulmuş ya da azami belirlenmemişse gözle görülür biçimde aşırı dolu rafları, kutuları, raf sistemlerini veya dolapları sayın.",
		"question.sort-overstock.option.defects-0.label": "Yok",
		"question.sort-overstock.option.defects-1.label": "1",
		"question.sort-overstock.option.defects-2.label": "2",
		"question.sort-overstock.option.defects-3.label": "3",
		"question.sort-overstock.option.defects-4.label": "4",
		"question.sort-overstock.option.defects-5-plus.label": ">4",
		"question.sort-outdated-information.title": "Asılı duran veya çalışma yerlerinde bulunan güncelliğini yitirmiş duyurular ve belgeler",
		"question.sort-outdated-information.description": "Artık geçerli olmayan duyuruları, levhaları, listeleri, talimatları veya kâğıtları sayın.",
		"question.sort-outdated-information.option.defects-0.label": "Yok",
		"question.sort-outdated-information.option.defects-1.label": "1",
		"question.sort-outdated-information.option.defects-2.label": "2",
		"question.sort-outdated-information.option.defects-3.label": "3",
		"question.sort-outdated-information.option.defects-4.label": "4",
		"question.sort-outdated-information.option.defects-5-plus.label": ">4",
		"question.sort-red-tag.title": "Gerekliliği belirsiz eşyalar kırmızı etiketle işaretlenir ve belirli bir sürede karara bağlanır",
		"question.sort-red-tag.description": "Gerekliliği belirsiz eşyaların nereye gittiğini sorun ve kırmızı etiket listesine veya kırmızı etiket alanına bakın.",
		"question.sort-red-tag.option.maturity-0.label": "Kırmızı etiket uygulaması yok",
		"question.sort-red-tag.option.maturity-1.label": "Kırmızı etiket kullanılıyor ama etiketli eşyalar hakkında karar verilmiyor",
		"question.sort-red-tag.option.maturity-2.label": "Alanın bazı bölümlerinde kullanılıyor",
		"question.sort-red-tag.option.maturity-3.label": "Her yerde kullanılıyor ve kararlar çoğunlukla zamanında veriliyor",
		"question.sort-red-tag.option.maturity-4.label": "Her yerde kullanılıyor ve kırmızı etiket listesi mevcut denetim döngüsünde gözden geçirildi",
		"question.sort-digital-files.title": "Güncelliğini yitirmiş, yinelenen veya kişisel dosyalar içeren ortak klasörler",
		"question.sort-digital-files.description": "Ekibin ortak sürücüsündeki bu tür her klasörü sayın; ekiple birlikte kontrol edin.",
		"question.sort-digital-files.option.defects-0.label": "Yok",
		"question.sort-digital-files.option.defects-1.label": "1",
		"question.sort-digital-files.option.defects-2.label": "2",
		"question.sort-digital-files.option.defects-3.label": "3",
		"question.sort-digital-files.option.defects-4.label": "4",
		"question.sort-digital-files.option.defects-5-plus.label": ">4",
		"category.set-in-order.title": "Düzenleme (Seiton)",
		"category.set-in-order.description": "Her şeyin bir yeri var ve her şey yerinde.",
		"question.set-in-order-home.title": "Belirlenmiş ve işaretlenmiş yeri olmayan eşyalar veya depolama yerleri",
		"question.set-in-order-home.description": "Etiketi, dış hatları, gölge panodaki yeri, adresi veya işaretli bölgesi olmayan ya da işareti artık okunamayan eşyaları veya depolama yerlerini sayın.",
		"question.set-in-order-home.option.defects-0.label": "Yok",
		"question.set-in-order-home.option.defects-1.label": "1",
		"question.set-in-order-home.option.defects-2.label": "2",
		"question.set-in-order-home.option.defects-3.label": "3",
		"question.set-in-order-home.option.defects-4.label": "4",
		"question.set-in-order-home.option.defects-5-plus.label": ">4",
		"question.set-in-order-out-of-place.title": "Ne yerinde ne de kullanımda olan eşyalar",
		"question.set-in-order-out-of-place.description": "Başka yerlerde bırakılmış eşyaları sayın; dolap üstleri, pencere pervazları veya tezgâh altları gibi geçici yerlerde tutulanlar da dahil.",
		"question.set-in-order-out-of-place.option.defects-0.label": "Yok",
		"question.set-in-order-out-of-place.option.defects-1.label": "1",
		"question.set-in-order-out-of-place.option.defects-2.label": "2",
		"question.set-in-order-out-of-place.option.defects-3.label": "3",
		"question.set-in-order-out-of-place.option.defects-4.label": "4",
		"question.set-in-order-out-of-place.option.defects-5-plus.label": ">4",
		"question.set-in-order-zones.title": "İşaretli bölgesinin dışında duran eşyalar",
		"question.set-in-order-zones.description": "Depolama, park veya çalışma için işaretlenmiş bölgenin dışındaki eşyaları, ekipmanları, arabaları veya araçları sayın.",
		"question.set-in-order-zones.option.defects-0.label": "Yok",
		"question.set-in-order-zones.option.defects-1.label": "1",
		"question.set-in-order-zones.option.defects-2.label": "2",
		"question.set-in-order-zones.option.defects-3.label": "3",
		"question.set-in-order-zones.option.defects-4.label": "4",
		"question.set-in-order-zones.option.defects-5-plus.label": ">4",
		"question.set-in-order-point-of-use.title": "Kullanıldığı yerde tutulmayan sık kullanılan eşyalar",
		"question.set-in-order-point-of-use.description": "Her gün gereken ama başka bir yerden getirilmesi gereken eşyaları sayın. Emin değilseniz ekibe sorun.",
		"question.set-in-order-point-of-use.option.defects-0.label": "Yok",
		"question.set-in-order-point-of-use.option.defects-1.label": "1",
		"question.set-in-order-point-of-use.option.defects-2.label": "2",
		"question.set-in-order-point-of-use.option.defects-3.label": "3",
		"question.set-in-order-point-of-use.option.defects-4.label": "4",
		"question.set-in-order-point-of-use.option.defects-5-plus.label": ">4",
		"question.set-in-order-digital-files.title": "Kararlaştırılan yapıya veya adlandırma kurallarına uymayan ortak klasörler",
		"question.set-in-order-digital-files.description": "Ekibin ortak sürücüsündeki bu tür her klasörü sayın; ekiple birlikte kontrol edin.",
		"question.set-in-order-digital-files.option.defects-0.label": "Yok",
		"question.set-in-order-digital-files.option.defects-1.label": "1",
		"question.set-in-order-digital-files.option.defects-2.label": "2",
		"question.set-in-order-digital-files.option.defects-3.label": "3",
		"question.set-in-order-digital-files.option.defects-4.label": "4",
		"question.set-in-order-digital-files.option.defects-5-plus.label": ">4",
		"category.shine.title": "Temizlik (Seiso)",
		"category.shine.description": "Temizleyin ve temizlerken kontrol edin.",
		"question.shine-surfaces.title": "Zeminlerin, duvarların, tavanların veya lambaların kirli olduğu yerler",
		"question.shine-surfaces.description": "Zeminlerde, duvarlarda, tavanlarda, pencerelerde veya aydınlatma armatürlerinde kir, toz, kalıntı veya atık bulunan yerleri sayın.",
		"question.shine-surfaces.option.defects-0.label": "Yok",
		"question.shine-surfaces.option.defects-1.label": "1",
		"question.shine-surfaces.option.defects-2.label": "2",
		"question.shine-surfaces.option.defects-3.label": "3",
		"question.shine-surfaces.option.defects-4.label": "4",
		"question.shine-surfaces.option.defects-5-plus.label": ">4",
		"question.shine-equipment.title": "Kirli ekipmanlar, çalışma yüzeyleri ve mobilyalar",
		"question.shine-equipment.description": "Kir, toz veya kalıntı bulunan makineleri, cihazları, çalışma yüzeylerini, masaları, arabaları veya saklama dolaplarını sayın.",
		"question.shine-equipment.option.defects-0.label": "Yok",
		"question.shine-equipment.option.defects-1.label": "1",
		"question.shine-equipment.option.defects-2.label": "2",
		"question.shine-equipment.option.defects-3.label": "3",
		"question.shine-equipment.option.defects-4.label": "4",
		"question.shine-equipment.option.defects-5-plus.label": ">4",
		"question.shine-abnormalities.title": "Etiketlenmemiş ve bildirilmemiş anormallikler",
		"question.shine-abnormalities.description": "Temizlik, kontroldür. Ekipmanda veya binada etiketi ve açık bildirimi olmayan sızıntıları, hasarları, aşınmaları, gevşek veya eksik parçaları ve yanmayan lambaları sayın.",
		"question.shine-abnormalities.option.defects-0.label": "Yok",
		"question.shine-abnormalities.option.defects-1.label": "1",
		"question.shine-abnormalities.option.defects-2.label": "2",
		"question.shine-abnormalities.option.defects-3.label": "3",
		"question.shine-abnormalities.option.defects-4.label": "4",
		"question.shine-abnormalities.option.defects-5-plus.label": ">4",
		"question.shine-waste.title": "Aşırı dolu veya yanlış ayrıştırılmış atık içeren atık kapları",
		"question.shine-waste.description": "Aşırı dolu olan, kapanmayan veya başka bir kaba ait atık içeren her çöp kutusunu ya da kabı sayın.",
		"question.shine-waste.option.defects-0.label": "Yok",
		"question.shine-waste.option.defects-1.label": "1",
		"question.shine-waste.option.defects-2.label": "2",
		"question.shine-waste.option.defects-3.label": "3",
		"question.shine-waste.option.defects-4.label": "4",
		"question.shine-waste.option.defects-5-plus.label": ">4",
		"category.standardize.title": "Standartlaştırma (Seiketsu)",
		"category.standardize.description": "İlk üç adımı rutin hâline getiren standartlar.",
		"question.standardize-visual-standard.title": "Hedef durumun görsel standardı asılıdır ve alan buna uygundur",
		"question.standardize-visual-standard.description": "Fotoğraflar veya bir yerleşim planı alanın nasıl görünmesi gerektiğini gösterir.",
		"question.standardize-visual-standard.option.maturity-0.label": "Görsel standart yok",
		"question.standardize-visual-standard.option.maturity-1.label": "Asılı ama güncel değil veya alanla uyuşmuyor",
		"question.standardize-visual-standard.option.maturity-2.label": "Alan kısmen uygun",
		"question.standardize-visual-standard.option.maturity-3.label": "Alan her yerde uygun, küçük eksikler var",
		"question.standardize-visual-standard.option.maturity-4.label": "Her yerde uygun ve mevcut denetim döngüsünde gözden geçirildi veya güncellendi",
		"question.standardize-routine.title": "Bir temizlik ve 5S rutini neyin, kim tarafından ve ne sıklıkla yapılacağını belirtir ve uygulanır",
		"question.standardize-routine.description": "Planı mevcut döngüye ait kayıtlarıyla karşılaştırın.",
		"question.standardize-routine.option.maturity-0.label": "Rutin yok",
		"question.standardize-routine.option.maturity-1.label": "Yazılı ama uygulanmıyor",
		"question.standardize-routine.option.maturity-2.label": "Kısmen veya düzensiz uygulanıyor",
		"question.standardize-routine.option.maturity-3.label": "Yazıldığı gibi uygulanıyor, küçük eksikler var",
		"question.standardize-routine.option.maturity-4.label": "Yazıldığı gibi uygulanıyor ve mevcut döngünün tamamı için imzalanmış",
		"question.standardize-labels.title": "Etiketler, zemin işaretleri ve renkler tek ve tutarlı bir standarda uyar",
		"question.standardize-labels.description": "Tüm alandaki etiketleri, zemin işaretlerini ve renk kodlarını karşılaştırın.",
		"question.standardize-labels.option.maturity-0.label": "Standart yok",
		"question.standardize-labels.option.maturity-1.label": "Bir standart var ama nadiren uygulanıyor",
		"question.standardize-labels.option.maturity-2.label": "Alanın bazı bölümlerinde uygulanıyor",
		"question.standardize-labels.option.maturity-3.label": "Her yerde uygulanıyor, küçük sapmalar var",
		"question.standardize-labels.option.maturity-4.label": "Her yerde uygulanıyor ve standart mevcut denetim döngüsünde gözden geçirildi",
		"question.standardize-visual-controls.title": "Normal ve anormal durumlar bir bakışta görülebilir",
		"question.standardize-visual-controls.description": "Örneğin alanın ihtiyaç duyduğu yerlerde asgari ve azami seviyeler, kalibrasyon, muayene veya son kullanma tarihleri ve durum göstergeleri.",
		"question.standardize-visual-controls.option.maturity-0.label": "Görsel gösterge yok",
		"question.standardize-visual-controls.option.maturity-1.label": "Birkaç tane, çoğu güncel değil",
		"question.standardize-visual-controls.option.maturity-2.label": "Alanın bazı bölümlerinde",
		"question.standardize-visual-controls.option.maturity-3.label": "Her yerde, küçük eksiklerle",
		"question.standardize-visual-controls.option.maturity-4.label": "Her yerde ve mevcut denetim döngüsünde kontrol edildi veya güncellendi",
		"category.sustain.title": "Sürdürme (Shitsuke)",
		"category.sustain.description": "Standardı canlı tutan disiplin ve takip.",
		"question.sustain-open-actions.title": "Önceki denetimden hâlâ açık olan eylemler",
		"question.sustain-open-actions.description": "Alanın önceki denetiminden hâlâ açık olan bulgulardan doldurulur; kontrol edin. Önceki bir denetim yoksa “Bilinmiyor” seçeneğini seçin.",
		"question.sustain-open-actions.option.defects-0.label": "Yok",
		"question.sustain-open-actions.option.defects-1.label": "1",
		"question.sustain-open-actions.option.defects-2.label": "2",
		"question.sustain-open-actions.option.defects-3.label": "3",
		"question.sustain-open-actions.option.defects-4.label": "4",
		"question.sustain-open-actions.option.defects-5-plus.label": ">4",
		"question.sustain-recurring-findings.title": "Önceki denetimden yeniden ortaya çıkan bulgular",
		"question.sustain-recurring-findings.description": "Önceki denetimden sonra giderildi olarak işaretlenen ve bugün yeniden görülen bulguları sayın. Önceki bir denetim yoksa “Bilinmiyor” seçeneğini seçin.",
		"question.sustain-recurring-findings.option.defects-0.label": "Yok",
		"question.sustain-recurring-findings.option.defects-1.label": "1",
		"question.sustain-recurring-findings.option.defects-2.label": "2",
		"question.sustain-recurring-findings.option.defects-3.label": "3",
		"question.sustain-recurring-findings.option.defects-4.label": "4",
		"question.sustain-recurring-findings.option.defects-5-plus.label": ">4",
		"question.sustain-rules.title": "Denetim sırasında görülen alan kuralı ihlalleri",
		"question.sustain-rules.description": "Alanın bir kuralına her uyulmadığında sayın; örneğin giriş kuralları, çalışırken temizlik veya kısıtlandığı yerlerde yiyecek ve içecek.",
		"question.sustain-rules.option.defects-0.label": "Yok",
		"question.sustain-rules.option.defects-1.label": "1",
		"question.sustain-rules.option.defects-2.label": "2",
		"question.sustain-rules.option.defects-3.label": "3",
		"question.sustain-rules.option.defects-4.label": "4",
		"question.sustain-rules.option.defects-5-plus.label": ">4",
		"question.sustain-team-knowledge.title": "Soru sorulan iki ekip üyesi alanın 5S standardını ve kendi 5S görevlerini ayrı ayrı açıklayabilir",
		"question.sustain-team-knowledge.description": "Alanda çalışan iki kişiye sorun. Yalnızca ikisi de açıklayabiliyorsa Evet deyin.",
		"question.sustain-team-knowledge.option.yes.label": "Evet",
		"question.sustain-team-knowledge.option.no.label": "Hayır",
		"question.sustain-results-visible.title": "Son 5S denetim sonucu ekibin gördüğü bir yerde asılıdır",
		"question.sustain-results-visible.description": "Ekibin kullandığı bir pano, ekran veya ortak sayfada.",
		"question.sustain-results-visible.option.yes.label": "Evet",
		"question.sustain-results-visible.option.no.label": "Hayır"
	}
};
// src/template/starter.ts
/** The reviewed HLT starter package (release 1), provisioned as an editable workspace draft. */
const starterKey = "hlt.5s.default";
const starterVersion = 1;
const starterDefinition = definition;
/** Curated translations of every starter field (field key → text) for the non-English locales. */
const starterTranslations = translations;
const conceptIndex = /* @__PURE__ */ new Map();
function starterEnglishTexts() {
	const texts = /* @__PURE__ */ new Map([["title", starterDefinition.title], ["description", starterDefinition.description]]);
	for (const category of starterDefinition.categories) {
		texts.set(`category.${category.key}.title`, category.title);
		texts.set(`category.${category.key}.description`, category.description ?? "");
		for (const question of category.questions) {
			const q = `question.${question.key}`;
			texts.set(`${q}.title`, question.title);
			texts.set(`${q}.description`, question.description);
			if (question.scoring.kind === "choice") for (const option of question.scoring.options) texts.set(`${q}.option.${option.key}.label`, option.label);
		}
	}
	return texts;
}
for (const [key, en] of starterEnglishTexts()) {
	if (!en.trim()) continue;
	const concept = {
		en,
		de: starterTranslations.de[key] ?? en,
		es: starterTranslations.es[key] ?? en,
		fr: starterTranslations.fr[key] ?? en,
		tr: starterTranslations.tr[key] ?? en
	};
	for (const [locale, text] of Object.entries(concept)) conceptIndex.set(`${locale}\u0000${text}`, concept);
}
for (const kind of [
	"yes",
	"no",
	"none"
]) {
	const concept = {
		en: answerLabels.en[kind],
		de: answerLabels.de[kind],
		es: answerLabels.es[kind],
		fr: answerLabels.fr[kind],
		tr: answerLabels.tr[kind]
	};
	for (const [locale, text] of Object.entries(concept)) conceptIndex.set(`${locale}\u0000${text}`, concept);
}
answerLabels.en.maturity.forEach((en, level) => {
	const concept = {
		en,
		de: answerLabels.de.maturity[level] ?? en,
		es: answerLabels.es.maturity[level] ?? en,
		fr: answerLabels.fr.maturity[level] ?? en,
		tr: answerLabels.tr.maturity[level] ?? en
	};
	for (const [locale, text] of Object.entries(concept)) conceptIndex.set(`${locale}\u0000${text}`, concept);
});
for (const number of [
	"1",
	"2",
	"3",
	"4",
	">4"
]) {
	const concept = {
		en: number,
		de: number,
		es: number,
		fr: number,
		tr: number
	};
	for (const locale of Object.keys(concept)) conceptIndex.set(`${locale}\u0000${number}`, concept);
}
/**
* Pre-translated standard content: the curated rendering of a starter text in `target`, or null when
* `text` is not (or no longer) exactly a starter text. Used before any AI translation, so the standard
* template never waits for or depends on a model.
*/
function standardTranslation(sourceLocale, text, target) {
	if (sourceLocale === target) return null;
	return conceptIndex.get(`${sourceLocale}\u0000${text}`)?.[target] ?? null;
}
/**
* A definition with every unchanged starter text shown in `target` from the curated translations; texts the
* customer wrote or changed stay as written. Lets the guided setup show the standard template in the
* interface language without any AI translation.
*/
function withStandardTexts(definition, sourceLocale, target) {
	const text = (value) => standardTranslation(sourceLocale, value, target) ?? value;
	return {
		...definition,
		title: text(definition.title),
		description: text(definition.description),
		categories: definition.categories.map((category) => ({
			...category,
			title: text(category.title),
			...category.description === void 0 ? {} : { description: text(category.description) },
			questions: category.questions.map((question) => ({
				...question,
				title: text(question.title),
				description: text(question.description),
				scoring: question.scoring?.kind === "choice" ? {
					...question.scoring,
					options: question.scoring.options.map((o) => ({
						...o,
						label: text(o.label)
					}))
				} : question.scoring
			}))
		}))
	};
}
// src/template/setup.ts
/**
* A workspace is set up once one of its templates is published and not retired: only then can an audit
* start. Until then administrators see the guided setup and members a pending state.
*/
function isWorkspaceConfigured(templates) {
	return templates.some((template) => template.activeVersionId !== null && !template.retired);
}
/**
* A workspace has been set up once any template was published, even if all are retired now. Until then
* the guided setup is mandatory and no other 5S page opens; afterwards existing audits and analytics stay
* reachable while the administrator publishes a new template.
*/
function wasWorkspaceSetUp(templates) {
	return templates.some((template) => template.activeVersionId !== null);
}
/**
* The HLT standard template the guided setup offers: the workspace's own starter draft (seeded at
* provisioning, so its starter updates keep working), preferring the default one. Null when the workspace
* has none left, in which case the setup creates a new draft from the starter definition.
*/
function setupStarterTemplate(templates) {
	const candidates = templates.filter((t) => t.starterKey === "hlt.5s.default" && !t.retired && t.draft !== null);
	return candidates.find((t) => t.isDefault) ?? candidates[0] ?? null;
}
/** A submitted area order for a template names each area at most once. */
function templateAreaErrors(areaIds) {
	const seen = /* @__PURE__ */ new Set();
	const errors = [];
	areaIds.forEach((id, index) => {
		if (seen.has(id)) errors.push({
			path: `areaIds.${index}`,
			message: "Each area can be listed once"
		});
		seen.add(id);
	});
	return errors;
}
// src/template/signature.ts
function scoringSemantics(scoring) {
	if (scoring.kind === "choice") return {
		kind: "choice",
		options: scoring.options.map(({ key, value, quality, critical }) => ({
			key,
			value,
			quality,
			critical
		}))
	};
	const { minimum, maximum, step, unit, bands } = scoring;
	return {
		kind: "numericBands",
		minimum,
		maximum,
		step,
		unit,
		bands: bands.map(({ lower, upper, includeUpper, quality, critical }) => ({
			lower,
			upper,
			includeUpper,
			quality,
			critical
		}))
	};
}
/**
* Scoring signature: stable keys and membership, scoring semantics, weights, aggregation,
* required/N/A policies, display maximum, safety categories and area conditions. Wording and translations
* are excluded.
*/
function scoringSignature(definition) {
	return sha256Hex(canonicalJson({
		aggregation: definition.aggregation,
		displayMaximum: definition.displayMaximum,
		categories: definition.categories.map((c) => ({
			key: c.key,
			weight: c.weight,
			...c.kind === "safety" ? { kind: c.kind } : {},
			questions: c.questions.map((q) => ({
				key: q.key,
				weight: q.weight,
				required: q.required,
				allowNotApplicable: q.allowNotApplicable,
				scoring: scoringSemantics(q.scoring),
				...q.showWhen?.length ? { showWhen: q.showWhen } : {}
			}))
		}))
	}));
}
// src/template/suggestions.ts
/**
* The few questions asked before AI suggests questions for one category: how
* many, an optional note and — only when it cannot be inferred — where the audit takes place. Answers are
* stable keys; the server owns the prompt text.
*/
/** How many questions to suggest. */
const suggestionCounts = [
	3,
	5,
	8,
	10
];
/** Questions per template (the publish limit). */
const templateQuestionLimit = 200;
/**
* Count options and the preselected one: 5 for an empty category, otherwise the option that brings the
* category closest to about eight questions (at least three). Options that would exceed the template
* limit are disabled; the default is never a disabled option (null when every option is disabled).
*/
function suggestionCountChoices(categoryQuestions, templateQuestions) {
	const room = 200 - templateQuestions;
	const choices = suggestionCounts.map((count) => ({
		count,
		disabled: count > room
	}));
	const target = categoryQuestions === 0 ? 5 : Math.max(3, 8 - categoryQuestions);
	return {
		choices,
		preselected: choices.filter((c) => !c.disabled).map((c) => c.count).reduce((best, count) => best === null || Math.abs(count - target) < Math.abs(best - target) ? count : best, null)
	};
}
/** Kind of workplace, asked only when the template's own words do not reveal it. */
const workplaceKinds = [
	"office",
	"production",
	"warehouse",
	"workshop",
	"laboratory",
	"outdoor",
	"other"
];
/** Short per-language keyword stems; a wrong inference is cheap to correct, so the list stays small. */
const workplaceKeywords = {
	office: [
		"office",
		"büro",
		"buero",
		"oficina",
		"bureau",
		"ofis"
	],
	production: [
		"production",
		"produktion",
		"fertigung",
		"montage",
		"assembly",
		"producción",
		"produccion",
		"fabricación",
		"üretim",
		"uretim",
		"manufactur"
	],
	warehouse: [
		"warehouse",
		"lager",
		"logistic",
		"logistik",
		"almacén",
		"almacen",
		"entrepôt",
		"entrepot",
		"depo"
	],
	workshop: [
		"workshop",
		"werkstatt",
		"maintenance",
		"instandhaltung",
		"taller",
		"atelier",
		"atölye",
		"atolye"
	],
	laboratory: [
		"laboratory",
		"labor",
		"laboratorio",
		"laboratoire",
		"laboratuvar"
	],
	outdoor: [
		"outdoor",
		"außen",
		"aussen",
		"yard",
		"exterior",
		"extérieur",
		"açık alan"
	]
};
/**
* Infer the workplace from the template title, category and area names (deterministic, no AI call).
* Null when nothing matches or two different kinds match, so the question is asked.
*/
function inferWorkplace(texts) {
	const haystack = texts.join(" \n ").toLocaleLowerCase();
	const matches = Object.keys(workplaceKeywords).filter((kind) => workplaceKeywords[kind].some((word) => haystack.includes(word)));
	const [only] = matches;
	return matches.length === 1 && only ? only : null;
}
// src/time/calendar.ts
/**
* Add calendar days in an IANA timezone: the result keeps the local wall-clock time, so a 30-day
* deadline across a DST change is not off by an hour. Seconds and milliseconds are preserved.
*/
function addCalendarDays(instant, days, timeZone) {
	const p = zonedParts(instant, timeZone);
	const target = new Date(Date.UTC(p.year, p.month - 1, p.day + days));
	const local = zonedTimeToUtc({
		year: target.getUTCFullYear(),
		month: target.getUTCMonth() + 1,
		day: target.getUTCDate(),
		hour: p.hour,
		minute: p.minute
	}, timeZone);
	return new Date(local.getTime() + instant.getUTCSeconds() * 1e3 + instant.getUTCMilliseconds());
}
/** Post-termination read/export period before deletion, in calendar days. */
const terminationGraceDays = 30;
/** The only purge instant compatible with a termination: 30 calendar days later in the organization's timezone. */
const purgeDeadline = (terminatedAt, timeZone) => addCalendarDays(terminatedAt, 30, timeZone);
// src/time/buckets.ts
const pad = (n) => String(n).padStart(2, "0");
function parseKey(key, bucket) {
	const [year = 0, month = 1, day = 1] = key.split("-").map(Number);
	return new Date(Date.UTC(year, month - 1, bucket === "month" ? 1 : day));
}
function formatKey(date, bucket) {
	const base = `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}`;
	return bucket === "month" ? base : `${base}-${pad(date.getUTCDate())}`;
}
/** The calendar bucket following `key` (keys come from `bucketKey`; weeks are Monday-based). */
function nextBucketKey(key, bucket) {
	const date = parseKey(key, bucket);
	if (bucket === "month") date.setUTCMonth(date.getUTCMonth() + 1);
	else date.setUTCDate(date.getUTCDate() + (bucket === "week" ? 7 : 1));
	return formatKey(date, bucket);
}
/**
* Every bucket key from `first` to `last` inclusive, so missing periods stay visible as gaps.
* When the range exceeds `maxPoints`, the most recent buckets are kept.
*/
function bucketRange(first, last, bucket, maxPoints = 500) {
	if (first > last) return [];
	const keys = [];
	for (let key = first; key <= last; key = nextBucketKey(key, bucket)) {
		keys.push(key);
		if (keys.length > maxPoints) keys.shift();
	}
	return keys;
}
export { absenceCount, addCalendarDays, addCalendarMonths, allowedOptionKeys, allowedOptionValues, allowedVariants, answerLabels, answerScoring, answerTypeOf, answerTypes, appendRows, areaFacts, arrangeGrid, arrangePage, asksForVatId, audioMimes, balancedRows, basePage, billingTimeZone, bucketKey, bucketRange, bytesPerGb, canCreateAudit, canEditAudit, canManageWorkspace, canReadAudit, canWrite, canonicalJson, catalogEntry, catalogWidget, cellsOverlap, changeDirection, chartForms, clearlyInLanguage, closestAnswerType, coarseCells, comparisonWidgetTypes, computeScore, containsMarkup, contentForms, contentWidgetTypes, contrastRatio, createWidget, csvCell, csvHeader, cutBands, dashboardAudiences, dashboardGoals, dataEntries, dataForms, dataWidgetTypes, decimalPlaces, defaultAudience, defaultColumns, defaultEntryOfForm, defaultLayoutHints, defaultOptions, defaultSize, defaultTiming, definitionForArea, definitionToCsv, detectLanguage, digitRuns, emptyDraft, entriesOfForm, entryOfWidget, euCountries, evaluateQuestion, featureEffective, findBand, firstFreeSlot, fixedDisplayMaximum, formOfWidget, formatDelta, formatScore, freeformColumns, freeformReadingOrder, freeformScale, germanVatBasisPoints, goalEntries, identifiersOf, imageMimes, importTemplateCsv, inferWorkplace, isAreaFact, isBlankRow, isContentWidget, isHeading, isHltCsvLayout, isNegative, isOnStep, isReportKey, isSafetyCategory, isUntouchedAutomaticItem, isValidNaReason, isValidTimeZone, isWorkspaceConfigured, issuesToFieldErrors, keyMessage, keyPattern, luminance, materializePage, maxDashboardGoals, mediaObjectKeys, mediaOwnerTypes, minSize, mixColors, negationCount, nextBucketKey, normalizeAreaFacts, normalizeCollaborators, normalizeDraftToAnswerTypes, normalizeTerms, normalizeToAnswerTypes, noteHeight, oneTimeAmount, optionalCsvColumns, pageMediaIds, pageReadingOrder, parseCsv, periodContaining, periodRange, periods, phoneHeight, placeholdersOf, planAllowanceKeys, preservesFacts, previousRange, prorationNet, publicMediaState, purgeDeadline, questionIndex, readableOn, readingOrder, recurringCost, reflowColumns, remarkOwnerTypes, reportKeys, reportPage, reportWidget, resolvePeriod, resolverDefaults, rewriteIssues, roleOf, roleSpec, roundHalfUp, sameEffectiveAudience, sameJson, sameMultiset, scoringSignature, sellerCountry, setupStarterTemplate, sha256Hex, showsInForm, sidebarAccent, sidebarSurfaces, sniffImageMime, standardQuestion, standardTranslation, starterDefinition, starterKey, starterTranslations, starterVersion, suggestDashboardWidgets, suggestedColumns, suggestedEntries, suggestionCountChoices, suggestionCounts, syncLayouts, templateAreaErrors, templateQuestionLimit, terminationGraceDays, toDisplay, toFreeform, translationIssues, untranslatedRatio, uploadRuleFor, validateAnswer, validateDraft, validateForPublish, validatePage, validateScoring, vatBasisPointsFor, vatOf, vatTreatment, wasWorkspaceSetUp, widgetCatalog, withAuthoringDefaults, withChartForm, withEntry, withForm, withStandardTexts, workplaceKinds, zonedParts, zonedTimeToUtc };

//# sourceMappingURL=index.mjs.map